export const supportedLocales = ['en', 'fr', 'ja', 'es'] as const;

export type SupportedLocale = (typeof supportedLocales)[number];
export type TranslationKey = keyof typeof en;

export const localeNames: Record<SupportedLocale, string> = {
  en: 'English',
  fr: 'Français',
  ja: '日本語',
  es: 'Español',
};

export const en = {
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.continue': 'Continue',
  'common.edit': 'Edit',
  'common.retry': 'Retry',
  'common.save': 'Save',
  'common.saved': 'Saved',
  'common.refresh': 'Refresh',
  'common.terms': 'Terms',
  'common.closeProfileSettings': 'Close profile settings',
  'common.openProfileSettings': 'Open profile settings',
  'common.editProfileAvatar': 'Edit profile avatar',
  'common.openMenu': 'Open menu',
  'common.closeMenu': 'Close menu',
  'common.navigation': 'Navigation',
  'tabs.home': 'Home',
  'tabs.market': 'Market',
  'tabs.portfolio': 'Sportfolio',
  'tabs.standings': 'Standings',
  'tabs.picks': 'Coach',
  'stack.createPool': 'Create Pool',
  'stack.signIn': 'Sign In',
  'stack.createAccount': 'Create Account',
  'poolWizard.title.create': 'Create Pool',
  'poolWizard.title.manage': 'Manage Pool',
  'poolWizard.tabs.details': 'Details',
  'poolWizard.tabs.members': 'Members',
  'poolWizard.fields.poolName': 'Pool Name',
  'poolWizard.fields.description': 'Description (optional)',
  'poolWizard.fields.valuePerPoint': 'Point Multiplier (optional)',
  'poolWizard.placeholders.poolName': "Martin's family pool 26",
  'poolWizard.placeholders.description': 'Short description',
  'poolWizard.placeholders.valuePerPoint': 'Value between 0.1 and 10',
  'poolWizard.placeholders.inviteEmails': 'Enter email addresses',
  'poolWizard.sections.poolSettings': 'Pool Settings',
  'poolWizard.sections.competition': 'Competition',
  'poolWizard.sections.selectCompetition': 'Select a competition',
  'poolWizard.sections.poolCode': 'Pool Code',
  'poolWizard.sections.invites': 'Invite by Email',
  'poolWizard.poolCode.hint': 'Share this code or link so others can join.',
  'poolWizard.approval.title': 'Require admin approval',
  'poolWizard.approval.description':
    'Turn this off to activate pending requests and let future pool-code joins enter immediately.',
  'poolWizard.approval.required': 'Required',
  'poolWizard.approval.notRequired': 'Not required',
  'poolWizard.actions.saveChanges': 'Save Changes',
  'poolWizard.actions.close': 'Close pool management',
  'poolWizard.actions.copy': 'Copy',
  'poolWizard.actions.copyLink': 'Copy Link',
  'poolWizard.actions.copied': 'Copied',
  'poolWizard.actions.shareLink': 'Share Link',
  'poolWizard.actions.sendInvites': 'Send Invites',
  'poolWizard.actions.accept': 'Accept',
  'poolWizard.actions.enable': 'Enable',
  'poolWizard.actions.createPool': 'Create Pool',
  'poolWizard.actions.acceptInvite': 'Accept Invite',
  'poolWizard.actions.rejectInvite': 'Reject Invite',
  'poolWizard.actions.removePlayer': 'Remove Player',
  'poolWizard.actions.resendInvite': 'Resend Invite',
  'poolWizard.actions.manageMember': 'Manage Member',
  'poolWizard.errors.loadMembers': 'Failed to load members.',
  'poolWizard.errors.nameTooShort': 'Pool name must be at least 3 characters.',
  'poolWizard.errors.nameTooLong': 'Pool name must be under 120 characters.',
  'poolWizard.errors.descriptionTooLong':
    'Description must be under 1024 characters.',
  'poolWizard.errors.selectCompetition': 'Please select a competition.',
  'poolWizard.errors.pointValue':
    'Point multiplier must be between 0.1 and 10 with at most 1 decimal digit.',
  'poolWizard.errors.updateFailed': 'Failed to update pool.',
  'poolWizard.errors.createFailed': 'Failed to create pool.',
  'poolWizard.errors.enterEmail': 'Enter at least one email.',
  'poolWizard.errors.inviteFailed': 'Failed to send invites.',
  'poolWizard.errors.resendInviteFailed': 'Failed to resend invite.',
  'poolWizard.errors.updateMember': 'Failed to update member.',
  'poolWizard.invites.sent': 'Invites sent.',
  'poolWizard.invites.resent': 'Invite resent.',
  'poolWizard.invites.or': 'OR',
  'poolWizard.invites.hint': 'Separated by commas or spaces.',
  'poolWizard.members.filter.all': 'All',
  'poolWizard.members.filter.pending': 'Pending',
  'poolWizard.members.filter.active': 'Active',
  'poolWizard.members.adminOnly': 'Only pool admins can manage members.',
  'poolWizard.members.section.pending': 'Pending Invites',
  'poolWizard.members.section.active': 'Active Players',
  'poolWizard.members.emptyAll': 'No members yet.',
  'poolWizard.members.emptyPending': 'No pending members.',
  'poolWizard.members.emptyActive': 'No active members.',
  'poolWizard.member.admin': 'Admin',
  'poolWizard.member.appleRelay.via': 'via Apple',
  'poolWizard.member.appleRelay.invited': 'Invited via Apple',
  'poolWizard.member.appleRelay.joined': 'Joined via Apple',
  'poolWizard.memberStatus.pending': 'Pending',
  'poolWizard.memberStatus.active': 'Active',
  'poolWizard.memberStatus.disabled': 'Disabled',
  'poolWizard.memberStatus.rejected': 'Rejected',
  'poolWizard.create.description':
    'Name your pool and pick a league season. Invite friends after.',
  'auth.redirectingToSignIn': 'Redirecting to sign in...',
  'auth.finishingSignIn': 'Finishing sign in...',
  'auth.tradingAccess': 'Trading Access',
  'auth.welcome': 'Welcome to Sport Exchange 100',
  'auth.appleSubtitle': 'One tap sign in with Apple',
  'auth.googleSubtitle': 'One tap sign in with Google',
  'auth.continueWithApple': 'Continue with Apple',
  'auth.continueWithGoogle': 'Continue with Google',
  'auth.signIn': 'Sign in',
  'auth.signingIn': 'Signing in...',
  'auth.signInInProgress': 'Sign-in is already in progress. Please wait.',
  'auth.appleIncomplete':
    'Apple sign in did not complete. Please check the device logs for the returned Clerk state.',
  'auth.providerNeedsVerification':
    '{provider} sign in requires additional verification.',
  'auth.providerFailed': 'Failed to sign in with {provider}',
  'auth.providerContextFailed': 'Unable to sign in from {context}.',
  'auth.errors.androidBrowserUnavailable':
    'Android sign in needs a browser app. Install or enable Chrome, Firefox, or another browser, then try again.',
  'auth.errors.invalidInviteCode':
    'Enter a valid pool code (4-20 chars, letters/numbers).',
  'auth.errors.invalidInviteCodeFormat':
    'Invite code format is invalid. Use 4-20 letters/numbers.',
  'guest.auth.getStarted': 'Get Started →',
  'guest.auth.alreadyHaveAccountSignIn': 'Already have an account? Sign in',
  'guest.auth.appStore': 'App Store',
  'guest.auth.playStore': 'Google Play',
  'guest.auth.playStoreComingSoon': 'Google Play Coming Soon',
  'guest.iosAppPrompt.title': 'Prefer the iOS app?',
  'guest.iosAppPrompt.body':
    'Install SportEx100 from the App Store for the smoothest invite flow.',
  'guest.iosAppPrompt.action': 'Open App Store',
  'guest.iosAppPrompt.dismiss': 'Continue on web',
  'guest.androidAppPrompt.title': 'Prefer the Android app?',
  'guest.androidAppPrompt.body':
    'Install SportEx100 from Google Play for the smoothest invite flow.',
  'guest.androidAppPrompt.action': 'Open Google Play',
  'guest.appPrompt.dismiss': 'Continue on web',
  'settings.preferences': 'Preferences',
  'settings.showTutorial.title': 'Show tutorial',
  'settings.showTutorial.subtitle': 'Learn the fundamentals',
  'settings.pulsePreview.title': 'Pulse preview',
  'settings.pulsePreview.subtitle': 'Show experimental Pool Pulse feed on Home',
  'settings.showTips.title': 'Reset tips',
  'settings.showTips.subtitle': 'Show onboarding tips again',
  'settings.showTips.action': 'Reset',
  'settings.showTips.done': 'Done',
  'settings.notifications.title': 'Notifications',
  'settings.notifications.subtitle': 'Trade fills & chat messages',
  'settings.support': 'Support',
  'settings.privacyPolicy': 'Privacy Policy',
  'settings.contactSupport': 'Contact support',
  'settings.account': 'Account',
  'settings.logOut': 'Log out',
  'settings.deleteAccount.title': 'Delete account',
  'settings.deleteAccount.subtitle':
    'Permanently removes your Sport Exchange 100 account',
  'settings.deleteAccount.promptTitle': 'Delete account?',
  'settings.deleteAccount.promptBody':
    'This removes your Sport Exchange 100 account and signs you out. Some anonymized trading records may be retained for pool accounting. This cannot be undone.',
  'settings.deleteAccount.finalTitle': 'Final confirmation',
  'settings.deleteAccount.finalBody':
    'Are you absolutely sure? This will remove your Sport Exchange 100 account and cannot be undone.',
  'settings.deleteAccount.deleteForever': 'Delete forever',
  'settings.language.title': 'Language',
  'settings.language.subtitle': 'Choose how SportEx100 displays text',
  'settings.language.current': 'Current: {language}',
  'settings.language.device': 'Device default',
  'settings.language.deviceDetail':
    'Uses your phone language when SportEx100 supports it.',
  'settings.language.englishDetail': 'Use English in SportEx100.',
  'settings.language.frenchDetail': 'Use French in SportEx100.',
  'settings.language.japaneseDetail': 'Market tab experiment in Japanese.',
  'settings.language.saved': 'Language updated',
  'settings.errors.openLink': 'Could not open the link. Please try again.',
  'settings.errors.openNotifications':
    'Could not open notification settings. Please try again.',
  'settings.errors.verifySession':
    'Could not verify your session. Please sign in again.',
  'settings.errors.deleteData':
    'Could not delete your SportEx100 data. Please try again.',
  'settings.errors.deleteAccount': 'Could not delete your account.',
  'settings.errors.tutorialPreference':
    'Could not update tutorial preference. Please try again.',
  'settings.errors.pulsePreview':
    'Could not update Pulse preview. Please try again.',
  'settings.errors.resetTips': 'Could not reset tips. Please try again.',
  'settings.errors.languagePreference':
    'Could not update language preference. Please try again.',
  'profile.cancelTraderEdit': 'Cancel trader code edit',
  'profile.editTraderCode': 'Edit trader code',
  'home.header.title': 'Home',
  'home.filters.current': 'Current',
  'home.filters.archived': 'Archived',
  'home.actions.doneReordering': 'Done Reordering',
  'home.actions.joinPool': 'Join Pool',
  'home.actions.createPool': 'Create Pool',
  'home.empty.filteredTitle': 'No pools match this filter',
  'home.empty.filteredBody': 'Try another status to see your pools.',
  'home.empty.noPoolsTitle': 'No pools yet',
  'home.empty.noPoolsBody':
    'Join an existing pool or create your own to get started.',
  'home.reorder.banner':
    'Reordering mode: use arrows on cards, then tap Done Reordering.',
  'home.pendingInvite.joined': 'Joined pool {code}.',
  'home.pendingInvite.couldNotJoin': 'Could not join pool {code}.',
  'home.welcome.context': 'New pool now open',
  'home.welcome.emphasis': 'Soccer World Tournament',
  'home.welcome.body':
    'Create a new pool for the 2026 Soccer World Tournament and invite your friends before kickoff.',
  'home.welcome.inviteBody':
    'Sign in to accept your pool invite and start tracking the tournament with your group.',
  'home.welcome.signInApple': 'Sign in with Apple',
  'home.welcome.signInGoogle': 'Sign in with Google',
  'home.welcome.createPool': 'Create New Pool',
  'home.welcome.acceptInvite': 'Accept Invite',
  'home.promo.baseball.context': 'Preseason trading opens now',
  'home.promo.baseball.title': 'October Baseball',
  'home.promo.baseball.body': '30 teams. One champion. Trade the full season.',
  'home.promo.baseball.join': 'Join',
  'home.promo.baseball.create': 'Create',
  'home.promo.baseball.minimize': 'Minimize baseball pool promo',
  'home.promo.baseball.expand': 'Expand baseball pool promo',
  'home.join.error.required': 'Please enter a pool code',
  'home.join.error.failed': 'Failed to join pool',
  'home.join.close': 'Close join pool',
  'home.join.title': 'Join the pool',
  'home.join.subtitle': 'Your spot is waiting.',
  'home.join.picksUnlock': 'Make picks right away',
  'home.join.accessPolicy': "Trading access follows the pool's approval setting",
  'home.join.immediately': 'immediately',
  'home.join.tradingUnlocks': 'Trade after admin approval',
  'home.join.afterApproval': 'after admin approval',
  'home.join.fieldLabel': 'Pool code',
  'home.join.placeholder': 'Enter code',
  'home.join.submit': 'Let me in →',
  'home.pool.status.preseason': 'Preseason',
  'home.pool.status.active': 'Active',
  'home.pool.status.completed': 'Completed',
  'home.pool.noOrders': 'No Orders',
  'home.pool.oneOrder': '1 Order',
  'home.pool.manyOrders': '{count} Orders',
  'home.pool.pendingApproval': 'Pending Admin Approval',
  'home.pool.stat.pnl': 'P&L',
  'home.pool.stat.ranking': 'Ranking',
  'home.pool.points': 'Pts',
  'home.pool.upside': 'Upside',
  'home.pool.pickValue': 'Your Pick Value',
  'home.pool.unitsAt': '{count} units at {marketRef}',
  'home.pool.completePicksToUnlock':
    'Complete picks to unlock trade suggestions.',
  'home.pool.awaitingApproval': 'Awaiting admin approval',
  'home.pool.leading': 'Leading',
  'home.pool.rising': 'Rising',
  'home.pool.stable': 'Stable',
  'home.pool.youToday': 'You ↑{delta} today',
  'home.pool.traderToday': '{traderCode} ↑{delta} today',
  'home.pool.leader.empty': 'Be the first to trade',
  'home.pool.leader.you': "You're leading the pool",
  'home.pool.leader.ahead': 'ahead',
  'home.feed.sectionTitle': 'Competition Pulse',
  'home.feed.pulseLabel': 'Pulse',
  'home.feed.pulseLabel.recap': 'Recap',
  'home.feed.pulseLabel.next': 'Next',
  'home.feed.loading': 'Loading...',
  'home.feed.error': 'Could not load updates right now.',
  'home.feed.retry': 'Retry',
  'home.feed.empty': 'Nothing to see here. Yet.',
  'home.feed.emptyMetaType': 'Insight',
  'home.feed.emptyMetaStatus': 'Waiting',
  'home.feed.swipeHint': 'Swipe for more',
  'home.feed.noDetail': 'No article detail available.',
  'home.feed.floor': 'Floor {value}',
  'home.feed.floorEmpty': 'Floor --',
  'home.feed.buyTeam': 'Buy {teamCode}',
  'home.feed.sellTeam': 'Sell {teamCode}',
  'home.feed.category.upcoming': 'Upcoming',
  'home.feed.category.recap': 'Recap',
  'home.feed.category.breaking': 'Breaking',
  'home.feed.category.injury': 'Injury',
  'home.feed.category.live': 'Live',
  'home.feed.category.result': 'Result',
  'home.feed.category.upset': 'Upset',
  'home.feed.category.eliminated': 'Eliminated',
  'home.feed.category.advancing': 'Advancing',
  'home.feed.category.momentum': 'Momentum',
  'home.feed.category.schedule': 'Schedule',
  'home.feed.category.champion': 'Champion',
  'home.feed.category.update': 'Update',
  'home.tutorial.modalTitle': 'Getting Started',
  'home.tutorial.cta.eyebrow': 'Welcome to Sport Exchange 100',
  'home.tutorial.cta.title': 'Trade teams. Outsmart your friends.',
  'home.tutorial.cta.subtitle':
    'Guess who wins. Trade smart. Climb the leaderboard. No real money — just bragging rights.',
  'home.tutorial.cta.start': 'Start the tutorial →',
  'home.tutorial.cta.startAccessibility': 'Start the tutorial',
  'home.tutorial.cta.duration': 'Takes 2 minutes',
  'home.tutorial.cta.hide': 'Hide',
  'home.tutorial.cta.hideAccessibility': 'Hide tutorial CTA',
  'home.tutorial.stepLabel': 'Step {step} of {total}',
  'home.tutorial.step1.heading': 'Teams earn points',
  'home.tutorial.step1.body':
    'Teams earn points based on how far they advance in the tournament. Use Picks to estimate how many points each team could finish with.',
  'home.tutorial.step2.heading': 'Teams settle between 0 and 100',
  'home.tutorial.step2.body':
    'Prices move as participants update what they think each team will settle for. At the end, every team settles at its final playoff point value. The champion always settles at 100.',
  'home.tutorial.step3.heading': 'Floors protect earned points',
  'home.tutorial.step3.body':
    'No team can trade below its floor value, which is the points it has already accumulated by winning games.',
  'home.tutorial.step4.heading': 'No fictional money to buy a team',
  'home.tutorial.step4.body':
    'Profit when a team trades or settles above your buy price.',
  'home.tutorial.step4.example':
    'Think a team can reach 30? Buy below 30. Start with 1 or 2 contracts.',
  'home.tutorial.step5.heading': 'Sell without owning',
  'home.tutorial.step5.body':
    'Profit when a team trades or settles below your sell price.',
  'home.tutorial.step5.example': 'Think it settles at 4? Sell above 4.',
  'home.tutorial.step5.note': 'Eliminated in 6 games · earns 4 points',
  'home.tutorial.step6.heading': 'Zero-sum pools',
  'home.tutorial.step6.body':
    'Every point won by one participant is lost by another. Trades only move points between people in the pool.',
  'home.tutorial.points.label': 'Pro Hockey · Best-of-7 (4 wins)',
  'home.tutorial.points.round': 'Round',
  'home.tutorial.points.gameWin': 'Game Win',
  'home.tutorial.points.roundWin': 'Rnd Win',
  'home.tutorial.points.maxPts': 'Max Pts',
  'home.tutorial.championLabel': 'Champion always settles at',
  'home.tutorial.chart.points': 'PTS',
  'home.tutorial.chart.playoffRound': 'PLAYOFF ROUND',
  'home.tutorial.chart.gamesPlayed': 'GAMES PLAYED',
  'home.tutorial.chart.boughtAt': 'BOUGHT AT {value}',
  'home.tutorial.chart.soldAt': 'SOLD AT {value}',
  'home.tutorial.chart.settlesAt': 'SETTLES AT {value}',
  'home.tutorial.chart.gain': 'GAIN',
  'home.tutorial.chart.loss': 'LOSS',
  'home.tutorial.previousAccessibility': 'Previous tutorial step',
  'home.tutorial.nextAccessibility': 'Next tutorial step',
  'home.tutorial.dismissAccessibility': 'Dismiss tutorial',
  'home.tutorial.gotIt': 'Got it',
  'market.header.title': 'Market',
  'market.noPool.message': 'Select a pool to view the market.',
  'market.pending.title': 'Approval Pending',
  'market.pending.message':
    "Set your picks now — trading unlocks once you're confirmed.",
  'market.pending.cta': 'Picks →',
  'market.invalidPool.title': 'Invalid Pool Configuration',
  'market.invalidPool.message':
    'The selected pool is missing season data. Please go back to Home and re-select the pool.',
  'market.filters.all': 'All',
  'market.filters.active': 'Active',
  'market.filters.eliminated': 'Eliminated',
  'market.nhlFilter.conference': 'Conference',
  'market.structureFilter.conference': 'Conference',
  'market.nhlFilter.division': 'Division',
  'market.structureFilter.division': 'Division',
  'market.nhlFilter.all': 'All',
  'market.structureFilter.all': 'All',
  'market.nhlFilter.east': 'East',
  'market.structureFilter.east': 'East',
  'market.nhlFilter.west': 'West',
  'market.structureFilter.west': 'West',
  'market.nhlFilter.emptyHint': 'Try another conference, division, status, or search.',
  'market.structureFilter.emptyHint': 'Try another conference, division, status, or search.',
  'market.sections.champion': 'Champion',
  'market.sections.activeTeams': 'Active Teams',
  'market.sections.eliminatedTeams': 'Eliminated Teams',
  'market.sections.group': 'Group {group}',
  'market.sections.otherTeams': 'Other Teams',
  'market.sections.conference': 'Conference · {conference}',
  'market.sections.division': 'Division · {division}',
  'market.sections.teamCount': '{visible}/{total} teams',
  'market.phase.preseason': 'Preseason',
  'market.phase.regularSeason': 'Regular Season',
  'market.phase.playoffCutover': 'Playoff Cutover',
  'market.phase.playoffs': 'Playoffs',
  'market.phase.completed': 'Completed',
  'market.record.wins': '{value} wins',
  'market.record.losses': '{value} losses',
  'market.record.overtimeLosses': '{value} overtime losses',
  'market.record.ties': '{value} ties',
  'market.tradingClosed.notQualified': 'This team did not qualify. Trading is closed.',
  'market.tradingClosed.unavailable': 'Trading is closed for this team.',
  'market.structure.americanLeague': 'American League',
  'market.structure.nationalLeague': 'National League',
  'market.structure.easternConference': 'Eastern Conference',
  'market.structure.westernConference': 'Western Conference',
  'market.structure.afc': 'American Football Conference',
  'market.structure.nfc': 'National Football Conference',
  'market.structure.americanLeagueEast': 'American League East',
  'market.structure.americanLeagueCentral': 'American League Central',
  'market.structure.americanLeagueWest': 'American League West',
  'market.structure.nationalLeagueEast': 'National League East',
  'market.structure.nationalLeagueCentral': 'National League Central',
  'market.structure.nationalLeagueWest': 'National League West',
  'market.structure.atlanticDivision': 'Atlantic',
  'market.structure.metropolitanDivision': 'Metropolitan',
  'market.structure.centralDivision': 'Central',
  'market.structure.pacificDivision': 'Pacific',
  'market.structure.afcEast': 'AFC East',
  'market.structure.afcNorth': 'AFC North',
  'market.structure.afcSouth': 'AFC South',
  'market.structure.afcWest': 'AFC West',
  'market.structure.nfcEast': 'NFC East',
  'market.structure.nfcNorth': 'NFC North',
  'market.structure.nfcSouth': 'NFC South',
  'market.structure.nfcWest': 'NFC West',
  'market.playoffPicture.divisionLeaders': 'Division Leaders',
  'market.playoffPicture.wildCard': 'Wild Card',
  'market.playoffPicture.outside': 'Outside Playoff Picture',
  'market.playoffPicture.cutLine': 'Wild Card cut line',
  'market.playoffPicture.cutLineAccessibilityLabel': 'Wild Card qualification cut line',
  'market.playoffPicture.unavailable': 'Playoff picture temporarily unavailable',
  'market.playoffPicture.updatedAt': 'Standings updated {timestamp}',
  'market.playoffPicture.delayed': 'Standings data may be delayed.',
  'market.playoffPicture.metric.pctAccessibilityLabel': 'Winning percentage',
  'market.playoffPicture.metric.ptsAccessibilityLabel': 'Standings points',
  'market.playoffPicture.metric.wcgbAccessibilityLabel': 'Wild Card games behind',
  'market.playoffPicture.metric.seedAccessibilityLabel': 'Playoff seed',
  'market.search.placeholder': 'Search teams',
  'market.search.clear': 'Clear team search',
  'market.quote.bid': 'Bid',
  'market.quote.ask': 'Ask',
  'market.participant.virtual': 'Virtual',
  'market.quote.bidSize': 'Bid Sz',
  'market.quote.askSize': 'Ask Sz',
  'market.action.buy': 'Buy',
  'market.action.sell': 'Sell',
  'market.floor.short': 'Floor',
  'market.last.short': 'Last',
  'market.orderBook.loading': 'Loading order book…',
  'market.orderBook.empty': 'No depth available',
  'market.orderBook.unavailable': 'Order book unavailable',
  'market.orderBook.expand': 'Expand order book',
  'market.orderBook.collapse': 'Collapse order book',
  'market.orderBook.notAuthenticated': 'Not authenticated',
  'market.bidAskTip.message': "Tap a team's Bid or Ask to pass an order.",
  'market.bidAskTip.action': 'Got it',
  'market.error.loadFailed':
    'Unable to load market. Check the API connection and try again.',
  'market.empty.noTeams': 'No teams available',
  'market.empty.worldCupHint': 'Try another status, group, or search term.',
  'market.empty.defaultHint': 'Market data will appear once the season starts.',
  'market.error.retry': 'Try again',
  'market.worldCup.searchPlaceholder': 'Search',
  'market.worldCup.clearSearch': 'Clear soccer team search',
  'market.worldCup.groups': 'Groups',
  'market.worldCup.groupAll': 'All',
  'market.worldCup.status.all': 'All',
  'market.worldCup.status.live': 'Live',
  'market.worldCup.status.out': 'Out',
  'market.worldCup.status.allAccessibility': 'Show all soccer teams',
  'market.worldCup.status.liveAccessibility': 'Show live soccer teams',
  'market.worldCup.status.outAccessibility': 'Show eliminated soccer teams',
  'market.guest.context': 'Every match moves the market.',
  'market.guest.emphasis': 'Buy low. Cheer loud.',
  'market.guest.body':
    'Watch the floor light up with bids and asks — then strike before your buddies move the price.',
  'market.guest.invite': 'Enter invite code',
  'guest.landing.sports.baseball': 'Baseball',
  'guest.landing.sports.hockey': 'Hockey',
  'guest.landing.sports.football': 'American Football',
  'guest.landing.background.label': 'Sport backgrounds',
  'guest.landing.sports.title': 'Now & next',
  'guest.landing.sports.now': 'NOW',
  'guest.landing.sports.playoffs': 'Playoffs',
  'guest.landing.sports.regularSeason': 'Regular season',
  'guest.landing.sports.playoffsStart': 'Playoffs start {date}',
  'guest.landing.sports.wildCardStart': 'Wild Card Weekend starts {date}',
  'guest.landing.sports.planned': 'Planned',
  'guest.landing.sports.schedule': 'See the league schedule for the next season.',
  'guest.landing.sports.helper': 'Start your pool now. Trade through the season and playoffs.',
  'guest.landing.sports.leagueLink': 'Visit the official {league} website (opens in a new tab)',
  'guest.landing.screenshots.market.imageAlt': 'SportEx100 mobile app screen: baseball team market',
  'guest.landing.screenshots.orderTicket.imageAlt': 'SportEx100 mobile app screen: baseball order ticket',
  'guest.landing.screenshots.sportfolio.imageAlt': 'SportEx100 mobile app screen: baseball portfolio',
  'guest.landing.screenshots.standings.imageAlt': 'SportEx100 mobile app screen: baseball pool standings',
  'guest.landing.screenshots.coach.imageAlt': 'SportEx100 mobile app screen: baseball Pool Coach',
  'guest.landing.web.hero.context': "A sports pool with a trading twist.",
  'guest.landing.web.hero.body': "Start a free sports pool with friends. Buy and sell teams with virtual points throughout the season, and compete for the top spot.",
  'guest.landing.web.market.body': "See what other players are willing to pay, then set your own price to buy or sell. Back the teams you believe in.",
  'guest.landing.web.picks.body': "Predict the playoff results and see what they would mean for each team’s value. Use your picks to guide your trades.",
  'guest.landing.web.social.emphasis': "Talk some trash.",
  'guest.landing.web.social.body': "Every pool has a live chat. Compare picks and talk trades with your friends before the next game.",
  'guest.landing.web.standings.body': "See who’s ahead and how your portfolio compares. Follow the race for first place as the season unfolds.",
  'guest.landing.web.track.emphasis': "See what you’re holding.",
  'guest.landing.web.track.body': "Keep your orders, trades, and team holdings in one place. Check your points and team values before your next move.",
  'guest.landing.web.footer.title': "Start your own pool. It’s free.",
  'guest.landing.web.footer.body': "Invite your friends and start trading. You can join even after the season has started.",
  'guest.landing.web.cta.joinWithCode': "JOIN WITH A CODE",
  'guest.landing.web.join.helper': "Your code will be saved while you sign in or create an account.",
  'guest.landing.web.footer.chip.noMoney': "Play with virtual points",
  'guest.landing.web.footer.chip.anytime': "Join even after the season starts",
  'guest.landing.hero.context': 'THE WHOLE WORLD’S IN PLAY',
  'guest.landing.hero.headline': 'Own the market',
  'guest.landing.hero.body':
    'Spin up your own free market, draft your crew, and out-trade every one of them. The standings don’t lie — winners rise, excuses sink.',
  'guest.landing.cta.createPool': 'START A POOL MARKET',
  'guest.landing.cta.joinWithCode': 'ENTER WITH CODE',
  'guest.landing.cta.hasAccount': 'Already have an account?',
  'guest.landing.cta.orDownload': 'Or download the app',
  'guest.landing.join.poolCodeLabel': 'Pool code',
  'guest.landing.join.poolCodePlaceholder': 'Enter code',
  'guest.landing.join.continue': 'Continue',
  'guest.landing.join.helper':
    'Sign in or create an account after entering your code. We will keep it ready.',
  'guest.landing.join.error.required': 'Enter a pool code to continue.',
  'guest.landing.nav.create': 'Create',
  'guest.landing.nav.market': 'Market',
  'guest.landing.nav.picks': 'Picks',
  'guest.landing.nav.social': 'Social',
  'guest.landing.social.context': 'THE CHAT IS THE FLOOR.',
  'guest.landing.social.emphasis': 'SportEx100 live chat.',
  'guest.landing.social.body':
    'Every pool gets a live chat — Call your shot, post your price, dare someone to take the other side. Every hot take is a trade waiting to happen.',
  'guest.landing.social.imageAlt': 'SportEx100 mobile app pool chat screen',
  'guest.landing.nav.standings': 'Standings',
  'guest.landing.standings.context': 'Every trade shakes up the race.',
  'guest.landing.standings.emphasis': 'Take the crown.',
  'guest.landing.standings.body':
    'Your rank moves with every match and every trade. No handouts, no mercy — climb over whoever’s in front and put the whole table on notice.',
  'guest.landing.standings.imageAlt':
    'SportEx100 mobile app standings leaderboard screen',
  'guest.landing.nav.track': 'Track',
  'guest.landing.contract.imageAlt':
    'SportEx100 mobile app team contract screen',
  'guest.landing.orderForm.imageAlt': 'SportEx100 mobile app order form screen',
  'guest.landing.market.imageAlt': 'SportEx100 mobile app soccer market screen',
  'guest.landing.picks.imageAlt': 'SportEx100 mobile app picks screen',
  'guest.landing.track.context': 'Every move. One screen.',
  'guest.landing.track.emphasis': 'Know your worth.',
  'guest.landing.track.body':
    'Orders, trades, points, team values — all in one place. See what you’re holding, what’s heating up, and where your next big play is hiding.',
  'guest.landing.track.imageAlt': 'SportEx100 mobile app portfolio screen',
  'guest.landing.footer.title': 'Start your market in 2 minutes. Free.',
  'guest.landing.footer.body':
    'Invite your friends now or once the tournament’s rolling. Jump in anytime, trade the swings, and prove you’re the sharpest one in the group.',
  'guest.landing.footer.kicker': 'Your tournament. Your rules. Your market.',
  'guest.landing.footer.chip.free': 'Free to play',
  'guest.landing.footer.chip.noMoney': 'No real money',
  'guest.landing.footer.chip.anytime': 'Join anytime — even mid-tournament',
  'order.title.place': 'Place Order',
  'order.title.update': 'Update Order',
  'order.form.side': 'Side',
  'order.form.qty': 'Qty',
  'order.form.price': 'Price',
  'order.action.buy': 'Buy',
  'order.action.sell': 'Sell',
  'order.preview.market': 'MKT',
  'order.preview.position': 'Position',
  'order.preview.myPickValue': 'My Pick Value: {value}',
  'order.preview.myPickValueLoading': 'My Pick Value: ...',
  'order.preview.setupSimulation': 'Set up theoretical team value in Coach.',
  'order.validation.pricePositive': 'Price must be positive',
  'order.validation.minPrice': 'Min price: {price}',
  'order.validation.maxPrice': 'Max price: {price}',
  'order.success.filled': 'Order filled! {fills} {tradeLabel} executed.',
  'order.success.tradeOne': 'trade',
  'order.success.tradeMany': 'trades',
  'order.success.updated': 'Order updated successfully!',
  'order.success.placed': 'Order placed successfully!',
  'order.error.title': 'Order Failed',
  'order.error.updateFailed': 'Failed to update order',
  'order.error.submitFailed': 'Failed to submit order',
  'order.action.cancel': 'Cancel',
  'order.action.submit': 'Submit Order',
  'order.action.submitting': 'Submitting',
  'order.action.update': 'Update Order',
  'order.action.updating': 'Updating',
  'order.alert.ok': 'OK',
  'picks.header.title': 'Pool Coach',
  'picks.noPool.message': 'Select a pool to open Coach.',
  'picks.hint.seriesMessage':
    "Tap a team row to pick the winner and series score. W-L is your predicted series record. PTS is the team's projected value.",
  'picks.hint.singleGameMessage':
    "Tap a team row to pick the winner. PTS is the team's projected value.",
  'picks.hint.action': 'Got it',
  'picks.actions.pointSystem': 'Point System',
  'picks.actions.reset': 'Reset',
  'picks.actions.tradeTeam': 'Trade {team}',
  'picks.guide.pointSystem': 'Point System',
  'picks.guide.bracketBuilder': 'Point Simulator',
  'picks.scenario.title': 'Scenario Builder',
  'picks.scenario.subtitle': 'Tap teams to predict',
  'picks.loading.bracket': 'Loading playoff bracket...',
  'picks.loading.saved': 'Loading saved predictions...',
  'picks.error.loadFailed':
    'Unable to load the bracket: {error}. Try refreshing the page.',
  'picks.empty.bracketPending': 'Bracket pending',
  'picks.empty.noBracket': 'No bracket data',
  'picks.empty.worldCupPending':
    'Soccer picks will unlock after the group-stage qualification script posts the Round of 32 matchups.',
  'picks.empty.noBracketBody':
    'The bracket will appear once the playoffs begin.',
  'picks.status.groupStage': 'GROUP STAGE',
  'picks.status.pickNow': 'PICK NOW',
  'picks.status.active': 'ACTIVE',
  'picks.status.complete': 'COMPLETE',
  'picks.status.pending': 'PENDING',
  'picks.column.series': 'W-L',
  'picks.column.points': 'PTS',
  'picks.pending.opponent': 'Pick previous round winner to set opponent.',
  'picks.pending.matchup': 'Pick winners from previous round.',
  'picks.pending.scoreHint': 'Tap winner again to adjust series score',
  'picks.trade.title': 'Trade {teamCode}',
  'picks.trade.value': 'Pick Value {value}',
  'picks.trade.cancel': 'Cancel',
  'picks.trade.buy': 'Buy at Pick Value',
  'picks.trade.sell': 'Sell at Pick Value',
  'picks.alert.pendingTitle': 'Read-only for pending members',
  'picks.alert.pendingMessage': 'Trading unlocks after admin approval.',
  'picks.alert.resetTitle': 'Reset Picks',
  'picks.alert.resetMessage':
    'Are you sure you want to reset all your picks for this bracket?',
  'picks.alert.resetConfirm': 'Reset',
  'picks.alert.lockedTitle': '{round} locked',
  'picks.alert.lockedMessage':
    'Almost there · Finish your {requiredRound} picks to unlock the next round.',
  'picks.alert.ok': 'OK',
  'picks.alert.previousRound': 'previous round',
  'picks.alert.groupLimitTitle': 'Only three can advance',
  'picks.alert.groupLimitMessage':
    'Pick the top two plus, at most, one third-place team from this group.',
  'picks.alert.qualifierLimitTitle': 'Qualifier limit',
  'picks.alert.qualifierLimitMessage':
    'Select {count} soccer qualifiers total.',
  'picks.alert.bestThirdLimitTitle': 'Best third limit',
  'picks.alert.bestThirdLimitMessage':
    'Select {count} best third-place qualifiers total.',
  'picks.worldCup.qualifierPicks': 'Qualifier Picks',
  'picks.worldCup.groupTitle': 'Group {group}',
  'picks.worldCup.seedGroupStage': 'Seed {seed} · Group stage',
  'picks.worldCup.topTwo': 'Top 2',
  'picks.worldCup.bestThird': 'Best 3rd',
  'picks.worldCup.groupRankFirst': 'Top 1',
  'picks.worldCup.groupRankSecond': 'Top 2',
  'picks.worldCup.groupRankThird': 'Best 3rd',
  'picks.worldCup.topTwoProgress': 'Top 2 {count}/{total}',
  'picks.worldCup.bestThirdProgress': 'Best 3rd {count}/{total}',
  'picks.worldCup.bestThirdOrder': 'Best 3rd order',
  'picks.worldCup.bestThirdMoveUp': 'Move {team} up',
  'picks.worldCup.bestThirdMoveDown': 'Move {team} down',
  'picks.round.groupStage': 'Group Stage',
  'picks.round.first': 'First Round',
  'picks.round.second': 'Second Round',
  'picks.round.roundOf16': 'Round of 16',
  'picks.round.quarterFinals': 'Quarter-finals',
  'picks.round.semiFinals': 'Semi-finals',
  'picks.round.final': 'Final',
  'picks.round.finals': 'Finals',
  'picks.round.round': 'Round {sequence}',
  'picks.round.lockedAccessibility': '{round} locked',
  'picks.competition.generic': 'Competition',
  'picks.competition.hockey': 'Pro Hockey',
  'picks.competition.football': 'Football',
  'picks.competition.soccer': 'World Soccer',
  'picks.series.singleGame': 'Single game',
  'picks.series.firstTo': 'First to {wins}',
  'picks.series.wins': '{format} ({wins} wins)',
  'picks.pointSystem.title': 'Point System',
  'picks.pointSystem.close': 'Close point system',
  'picks.pointSystem.gameWin': 'Game Win',
  'picks.pointSystem.roundWin': 'Round Win',
  'picks.pointSystem.maxRound': 'Max Round',
  'picks.pointSystem.champion': 'Champion always settles for',
  'picks.pointSystem.settlement': 'Settlement',
  'picks.pointSystem.settlementValue': 'Settlement value',
  'picks.pointSystem.winPoints': 'Win points',
  'picks.pointSystem.perGameWin': 'Per game win',
  'picks.pointSystem.teamPoints': 'Team Points',
  'picks.pointSystem.stage': 'Round',
  'picks.pointSystem.result': 'Result',
  'picks.pointSystem.earnAdds': 'EARN +',
  'picks.pointSystem.settlesAt': 'SETTLES AT',
  'picks.pointSystem.noValue': '—',
  'picks.pointSystem.value': 'Value',
  'picks.pointSystem.points': 'Points',
  'picks.pointSystem.qualification': 'Playoff qualification',
  'picks.pointSystem.summary.soccer': 'Settlement outcomes',
  'picks.pointSystem.summary.standard': 'Round scoring breakdown',
  'picks.pointSystem.soccer.winner': 'Champion',
  'picks.pointSystem.soccer.runnerUp': 'Runner-up',
  'picks.pointSystem.soccer.thirdPlace': '3rd place',
  'picks.pointSystem.soccer.worldCupChampion': 'Champion',
  'picks.pointSystem.soccer.perfectPath': 'Perfect tournament path',
  'picks.pointSystem.soccer.runningTotal': 'TOTAL {points}',
  'picks.pointSystem.winRound': 'WIN {round}',
  'picks.pointSystem.soccer.winRound': 'WIN {round}',
  'picks.pointSystem.soccer.exitValue': 'Settlement Value',
  'picks.pointSystem.soccer.exitValueBody':
    'A team settles at the points it has already earned.',
  'picks.pointSystem.soccer.eliminatedGroupStage': 'Eliminated in Group Stage',
  'picks.pointSystem.soccer.advanceKnockout': 'Advance to Knockout Stage',
  'picks.pointSystem.soccer.winRound32': 'Win in Round of 32',
  'picks.pointSystem.soccer.winRound16': 'Win in Round of 16',
  'picks.pointSystem.soccer.winQuarterFinal': 'Win in Quarter-finals',
  'picks.pointSystem.soccer.winSemiFinal': 'Win in Semi-finals',
  'picks.pointSystem.soccer.winThirdPlace': 'Win 3rd-place Match',
  'picks.pointSystem.soccer.eliminatedSemiFinal': 'Eliminated in Semi-finals',
  'picks.pointSystem.soccer.eliminatedQuarterFinal':
    'Eliminated in Quarter-finals',
  'picks.pointSystem.soccer.eliminatedRound16': 'Eliminated in Round of 16',
  'picks.pointSystem.soccer.eliminatedRound32': 'Eliminated in Round of 32',
  'picks.pointSystem.soccer.missedKnockout': "Didn't qualify",
  'picks.pointSystem.soccer.eliminatedHeader': 'ELIMINATED',
  'picks.pointSystem.imageAlt': 'Always 100',
  'picks.guest.context': 'Build your projection.',
  'picks.guest.emphasis': 'Trust your gut.',
  'picks.guest.body':
    'Predict the bracket, back your hunch, and pounce when the price is wrong. The bold get paid — the timid get fleeced.',
  'picks.guest.invite': 'Enter invite code',
  'standings.header.title': 'Standings',
  'standings.noPool.message': 'Select a pool to view standings.',
  'standings.freshness.updated': 'Updated {time}',
  'standings.podium.title': 'Top 3 Race',
  'standings.podium.subtitle': 'Hall of Fame',
  'standings.table.rank': 'Rank',
  'standings.table.trader': 'Trader',
  'standings.table.trades': 'Trades',
  'standings.table.pnl': 'P&L',
  'standings.virtual.title': 'Virtual Traders',
  'standings.virtual.subtitle': 'Automated liquidity · excluded from human rankings',
  'standings.virtual.active': 'Active',
  'standings.virtual.paused': 'Paused',
  'standings.empty.title': 'No standings yet',
  'standings.empty.body': 'Standings will appear once trading begins.',
  'standings.you': 'YOU',
  'standings.deletedTrader': 'DELETED',
  'standings.trade.one': 'trade',
  'standings.trade.many': 'trades',
  'standings.recentActivity.title': 'Recent Activity',
  'standings.recentActivity.loading': 'Loading activity...',
  'standings.recentActivity.error': 'Unable to load activity feed.',
  'standings.guest.context': 'Every trade reshapes the chase.',
  'standings.guest.emphasis': 'Hunt the top spot.',
  'standings.guest.body':
    'Your score and rank update after every match and every move. See who’s ahead, close the gap, and make the next trade put pressure on the table.',
  'standings.guest.invite': 'Enter invite code',
  'portfolio.guest.context': 'See how you are doing.',
  'portfolio.guest.emphasis': 'Track your score.',
  'portfolio.guest.body':
    'Your picks, moves, points, and progress all show up here.',
  'portfolio.guest.invite': 'Enter invite code',
  'portfolio.noPool.message': 'Join or create a pool to view your portfolio.',
  'portfolio.header.title': 'Sportfolio',
  'portfolio.filters.all': 'All',
  'portfolio.filters.positions': 'Positions',
  'portfolio.filters.orders': 'Orders',
  'portfolio.filters.history': 'History',
  'portfolio.filters.buy': 'Buy',
  'portfolio.filters.sell': 'Sell',
  'portfolio.filters.settle': 'Settle',
  'portfolio.filters.long': 'Long',
  'portfolio.filters.short': 'Short',
  'portfolio.sections.positions': 'Positions',
  'portfolio.sections.openOrders': 'Open Orders',
  'portfolio.sections.history': 'History',
  'portfolio.empty.positions': 'No positions',
  'portfolio.empty.openOrders': 'No open orders',
  'portfolio.empty.history': 'No trades yet',
  'portfolio.noPool': 'Select a pool to view your sportfolio.',
  'portfolio.summary.totalPnl': 'Total P&L',
  'portfolio.summary.rank': 'Rank',
  'portfolio.summary.settled': 'Settled',
  'portfolio.summary.trading': 'Trading',
  'portfolio.metric.qty': 'QTY',
  'portfolio.metric.entry': 'ENTRY',
  'portfolio.metric.mark': 'MARK',
  'portfolio.metric.pnl': 'P&L',
  'portfolio.metric.date': 'DATE',
  'portfolio.metric.price': 'PRICE',
  'portfolio.metric.floor': 'FLOOR',
  'portfolio.metric.total': 'TOTAL',
  'portfolio.metric.counterparty': 'COUNTERPARTY',
  'portfolio.position.flat': 'FLAT',
  'portfolio.position.short': 'SHORT',
  'portfolio.position.long': 'LONG',
  'portfolio.position.buyAvg': 'Buy avg',
  'portfolio.position.sellAvg': 'Sell avg',
  'portfolio.position.realized': 'Realized',
  'portfolio.position.unrealized': 'Unrealized',
  'portfolio.position.tradeLog': 'Trade log',
  'portfolio.settlementAudit.side': 'Side',
  'portfolio.settlementAudit.entry': 'Entry',
  'portfolio.settlementAudit.formula': 'Formula',
  'portfolio.settlementAudit.totalPnl': 'TOTAL P&L',
  'portfolio.settlementAudit.closedBefore': 'CLOSED EARLY',
  'portfolio.settlementAudit.settledAtFloor': 'FLOOR SETTLEMENT',
  'portfolio.settlementAudit.tradeLog': 'TRADE LOG',
  'portfolio.settlementAudit.sideBuy': 'BUY',
  'portfolio.settlementAudit.sideSell': 'SELL',
  'portfolio.settlementAudit.sideLong': 'LONG',
  'portfolio.settlementAudit.sideShort': 'SHORT',
  'portfolio.settlementAudit.floor': 'FLOOR',
  'portfolio.settlementAudit.basisMatched': 'matched',
  'portfolio.settlementAudit.basisAvg': 'avg basis',
  'portfolio.settlementAudit.netOpen': 'net open',
  'portfolio.settlementAudit.fillCountOne': '{count} fill',
  'portfolio.settlementAudit.fillCount': '{count} fills',
  'portfolio.settlementAudit.buyCountOne': '{count} BUY',
  'portfolio.settlementAudit.buyCount': '{count} BUYS',
  'portfolio.settlementAudit.sellCountOne': '{count} SELL',
  'portfolio.settlementAudit.sellCount': '{count} SELLS',
  'portfolio.trade.settle': 'SETTLE',
  'portfolio.trade.deletedTrader': 'DELETED',
  'portfolio.order.actionsAccessibility': '{team} order actions',
  'portfolio.order.promote': 'Promote',
  'portfolio.order.edit': 'Edit',
  'portfolio.order.cancel': 'Cancel',
  'portfolio.order.market': 'MKT',
  'portfolio.order.hint': 'Tap an order to promote, edit, or cancel.',
  'portfolio.order.hintAction': 'Got it',
  'portfolio.order.cancelTitle': 'Cancel Order',
  'portfolio.order.cancelPrompt': 'Are you sure you want to cancel this order?',
  'portfolio.order.cancelNo': 'No',
  'portfolio.order.cancelYes': 'Yes',
  'portfolio.order.cancelErrorTitle': 'Error',
  'portfolio.order.cancelErrorMessage': 'Failed to cancel order',
  'portfolio.order.poolNotReadyTitle': 'Pool not ready',
  'portfolio.order.poolNotReadyMessage':
    'Unable to load market data for this order yet.',
  'portfolio.order.teamNotFoundTitle': 'Team not found',
  'portfolio.order.teamNotFoundMessage':
    'Unable to load team data for this order.',
  'pulse.title': 'Pulse',
  'pulse.lane.pool': 'Pool',
  'pulse.lane.game': 'Game',
  'pulse.lane.you': 'You',
  'pulse.story.trade': 'Trade',
  'pulse.story.wall': 'Wall',
  'pulse.story.airball': 'Airball',
  'pulse.story.rankMove': 'Rank move',
  'pulse.story.crowdedSide': 'Crowded side',
  'pulse.story.contrarianPosition': 'Contrarian',
  'pulse.story.settlementSwing': 'Settlement',
  'pulse.story.gameResult': 'Result',
  'pulse.story.eliminationQualification': 'Bracket',
  'pulse.story.floorChange': 'Floor',
  'pulse.story.personalImpact': 'You',
  'pulse.impact.pnl': 'P&L',
  'pulse.impact.rank': 'Rank',
  'pulse.impact.exposure': 'Exposure',
  'pulse.impact.marketActivity': 'Market',
  'pulse.impact.settlement': 'Settlement',
  'pulse.impact.gameResult': 'Game',
  'pulse.impact.socialPrompt': 'Chat',
  'pulse.empty.title': 'Pool is quiet',
  'pulse.empty.body': 'No major pool moments in the current Pulse window.',
  'pulse.empty.action': 'Open Market',
  'pulse.action.share': 'Share',
  'pulse.action.open': 'Open',
  'pulse.action.viewAll': 'View all',
  'pulse.action.showLess': 'Show less',
  'pulse.time.justNow': 'JUST NOW',
  'pulse.time.minutesAgo': '{count}M AGO',
  'pulse.time.hoursAgo': '{count}H AGO',
  'pulse.time.daysAgo': '{count}D AGO',
  'pulse.time.lastWindow': 'LAST {duration}',
  'pulse.tradeBehavior.pill.stacking': 'STACKING',
  'pulse.tradeBehavior.pill.dumping': 'DUMPING',
  'pulse.tradeBehavior.pill.heatingUp': 'HEATING UP',
  'pulse.tradeBehavior.pill.whale': 'WHALE',
  'pulse.tradeBehavior.side.buy': 'BUY',
  'pulse.tradeBehavior.side.sell': 'SELL',
  'pulse.tradeBehavior.context.pattern':
    '{side} {quantity} · {fills} FILLS · AVG {avg}',
  'pulse.tradeBehavior.headline.stacking': '{trader} is stacking {team}',
  'pulse.tradeBehavior.headline.dumping': '{trader} is dumping {team}',
  'pulse.tradeBehavior.headline.whale': '{buyer} bought {team} from {seller}',
  'pulse.orderPressure.pill.wall': 'WALL',
  'pulse.orderPressure.pill.airball': 'AIRBALL',
  'pulse.orderPressure.side.buyHeadline': 'buy',
  'pulse.orderPressure.side.sellHeadline': 'sell',
  'pulse.orderPressure.headline.wall': '{side} wall on {team}',
  'pulse.orderPressure.headline.wallSell': '{side} wall on {team}',
  'pulse.orderPressure.headline.airball': '{side} airball on {team}',
  'pulse.orderPressure.context.full':
    '{side} {price} × {quantity} · VALUE {value} · MARK {mark}',
  'pulse.teamHeat.headline': '{team} is on fire.',
  'pulse.teamHeat.subline': '{traders} traders · {qty} qty moved · avg {avg}',
  'pulse.teamHeat.metric.traders': '{count} traders',
  'pulse.teamHeat.metric.qtyMoved': '{qty} qty moved',
  'pulse.teamHeat.metric.avg': 'avg {avg}',
  'pulse.position.asOf': 'AS OF {time}',
  'pulse.position.pill.crowded': 'CROWDED',
  'pulse.position.pill.contrarian': 'CONTRARIAN',
  'pulse.position.side.long': 'LONG',
  'pulse.position.side.short': 'SHORT',
  'pulse.position.intensity.allIn': 'ALL-IN',
  'pulse.position.intensity.lopsided': 'LOPSIDED',
  'pulse.position.intensity.heavily': 'HEAVILY',
  'pulse.position.intensity.leaning': 'LEANING',
  'pulse.position.context.value': 'VALUE {value}{qty}',
  'pulse.position.context.qtySuffix': ' · QTY {qty}',
  'pulse.position.context.liveQty': 'LIVE · QTY {qty}',
  'pulse.position.context.live': 'LIVE',
  'pulse.settlement.pill': 'SETTLE',
  'pulse.settlement.best': 'BEST',
  'pulse.settlement.worst': 'WORST',
  'pulse.settlement.vs': 'VS',
  'pulse.settlement.traders': '{count} TRADERS',
  'pulse.settlement.points': '{points} PTS',
  'pulse.fallback.headline': 'Pool moment',
  'pulse.fallback.impact': 'Tap to inspect the latest pool context.',
  'pulse.accessibility.story': '{lane}. {headline}. {impact}',
  'portfolio.history.loadMore': 'Load more ({remaining} remaining)',
} as const;

export const fr: Record<TranslationKey, string> = {
  'common.back': 'Retour',
  'common.cancel': 'Annuler',
  'common.continue': 'Continuer',
  'common.edit': 'Modifier',
  'common.retry': 'Réessayer',
  'common.save': 'Enregistrer',
  'common.saved': 'Enregistré',
  'common.refresh': 'Actualiser',
  'common.terms': 'Conditions',
  'common.closeProfileSettings': 'Fermer les réglages du profil',
  'common.openProfileSettings': 'Ouvrir les réglages du profil',
  'common.editProfileAvatar': 'Modifier l’avatar du profil',
  'common.openMenu': 'Ouvrir le menu',
  'common.closeMenu': 'Fermer le menu',
  'common.navigation': 'Navigation',
  'tabs.home': 'Accueil',
  'tabs.market': 'Marché',
  'tabs.portfolio': 'Sportfolio',
  'tabs.standings': 'Classement',
  'tabs.picks': 'Coach',
  'stack.createPool': 'Créer un groupe',
  'stack.signIn': 'Connexion',
  'stack.createAccount': 'Créer un compte',
  'poolWizard.title.create': 'Créer un groupe',
  'poolWizard.title.manage': 'Gérer le groupe',
  'poolWizard.tabs.details': 'Détails',
  'poolWizard.tabs.members': 'Membres',
  'poolWizard.fields.poolName': 'Nom du groupe',
  'poolWizard.fields.description': 'Description (facultative)',
  'poolWizard.fields.valuePerPoint': 'Multiplicateur de points (facultatif)',
  'poolWizard.placeholders.poolName': 'Pool familial Martin 26',
  'poolWizard.placeholders.description': 'Courte description',
  'poolWizard.placeholders.valuePerPoint': 'Valeur entre 0.1 et 10',
  'poolWizard.placeholders.inviteEmails': 'Saisissez les adresses courriel',
  'poolWizard.sections.poolSettings': 'Réglages du groupe',
  'poolWizard.sections.competition': 'Compétition',
  'poolWizard.sections.selectCompetition': 'Choisir une compétition',
  'poolWizard.sections.poolCode': 'Code du groupe',
  'poolWizard.sections.invites': 'Inviter par courriel',
  'poolWizard.poolCode.hint':
    'Partagez ce code ou ce lien pour permettre à d’autres personnes de rejoindre le groupe.',
  'poolWizard.approval.title': 'Exiger l’approbation d’un administrateur',
  'poolWizard.approval.description':
    'Désactivez ce réglage pour activer les demandes en attente et permettre aux futures personnes utilisant le code du groupe de rejoindre immédiatement.',
  'poolWizard.approval.required': 'Requise',
  'poolWizard.approval.notRequired': 'Non requise',
  'poolWizard.actions.saveChanges': 'Enregistrer',
  'poolWizard.actions.close': 'Fermer la gestion du groupe',
  'poolWizard.actions.copy': 'Copier',
  'poolWizard.actions.copyLink': 'Copier le lien',
  'poolWizard.actions.copied': 'Copié',
  'poolWizard.actions.shareLink': 'Partager le lien',
  'poolWizard.actions.sendInvites': 'Envoyer les invitations',
  'poolWizard.actions.accept': 'Accepter',
  'poolWizard.actions.enable': 'Réactiver',
  'poolWizard.actions.createPool': 'Créer le groupe',
  'poolWizard.actions.acceptInvite': 'Accepter l’invitation',
  'poolWizard.actions.rejectInvite': 'Refuser l’invitation',
  'poolWizard.actions.removePlayer': 'Retirer le joueur',
  'poolWizard.actions.resendInvite': 'Renvoyer l’invitation',
  'poolWizard.actions.manageMember': 'Gérer le membre',
  'poolWizard.errors.loadMembers': 'Impossible de charger les membres.',
  'poolWizard.errors.nameTooShort':
    'Le nom du groupe doit compter au moins 3 caractères.',
  'poolWizard.errors.nameTooLong':
    'Le nom du groupe doit faire moins de 120 caractères.',
  'poolWizard.errors.descriptionTooLong':
    'La description doit faire moins de 1024 caractères.',
  'poolWizard.errors.selectCompetition': 'Veuillez choisir une compétition.',
  'poolWizard.errors.pointValue':
    'Le multiplicateur de points doit être compris entre 0.1 et 10 avec au plus 1 décimale.',
  'poolWizard.errors.updateFailed': 'Impossible de mettre à jour le groupe.',
  'poolWizard.errors.createFailed': 'Impossible de créer le groupe.',
  'poolWizard.errors.enterEmail': 'Entrez au moins une adresse courriel.',
  'poolWizard.errors.inviteFailed': 'Impossible d’envoyer les invitations.',
  'poolWizard.errors.resendInviteFailed':
    'Impossible de renvoyer l’invitation.',
  'poolWizard.errors.updateMember': 'Impossible de mettre à jour le membre.',
  'poolWizard.invites.sent': 'Invitations envoyées.',
  'poolWizard.invites.resent': 'Invitation renvoyée.',
  'poolWizard.invites.or': 'OU',
  'poolWizard.invites.hint': 'Séparés par virgules ou espaces.',
  'poolWizard.members.filter.all': 'Tous',
  'poolWizard.members.filter.pending': 'En attente',
  'poolWizard.members.filter.active': 'Actifs',
  'poolWizard.members.adminOnly':
    'Seuls les admins du groupe peuvent gérer les membres.',
  'poolWizard.members.section.pending': 'Invitations en attente',
  'poolWizard.members.section.active': 'Joueurs actifs',
  'poolWizard.members.emptyAll': 'Aucun membre pour le moment.',
  'poolWizard.members.emptyPending': 'Aucun membre en attente.',
  'poolWizard.members.emptyActive': 'Aucun membre actif.',
  'poolWizard.member.admin': 'Admin',
  'poolWizard.member.appleRelay.via': 'via Apple',
  'poolWizard.member.appleRelay.invited': 'Invité via Apple',
  'poolWizard.member.appleRelay.joined': 'A rejoint via Apple',
  'poolWizard.memberStatus.pending': 'En attente',
  'poolWizard.memberStatus.active': 'Actif',
  'poolWizard.memberStatus.disabled': 'Désactivé',
  'poolWizard.memberStatus.rejected': 'Refusé',
  'poolWizard.create.description':
    'Nommez votre groupe et choisissez une saison de ligue. Invitez vos amis ensuite.',
  'auth.redirectingToSignIn': 'Redirection vers la connexion...',
  'auth.finishingSignIn': 'Connexion en cours...',
  'auth.tradingAccess': 'Accès au marché',
  'auth.welcome': 'Bienvenue sur Sport Exchange 100',
  'auth.appleSubtitle': 'Connexion Apple en un geste',
  'auth.googleSubtitle': 'Connexion Google en un geste',
  'auth.continueWithApple': 'Continuer avec Apple',
  'auth.continueWithGoogle': 'Continuer avec Google',
  'auth.signIn': 'Se connecter',
  'auth.signingIn': 'Connexion...',
  'auth.signInInProgress':
    'Une connexion est déjà en cours. Veuillez patienter.',
  'auth.appleIncomplete':
    'La connexion Apple n’a pas abouti. Consultez les journaux de l’appareil pour l’état Clerk retourné.',
  'auth.providerNeedsVerification':
    'La connexion avec {provider} nécessite une vérification supplémentaire.',
  'auth.providerFailed': 'Échec de la connexion avec {provider}',
  'auth.providerContextFailed': 'Impossible de se connecter depuis {context}.',
  'auth.errors.androidBrowserUnavailable':
    'La connexion Android nécessite une application de navigateur. Installez ou activez Chrome, Firefox ou un autre navigateur, puis réessayez.',
  'auth.errors.invalidInviteCode':
    'Entrez un code de groupe valide (4 à 20 lettres ou chiffres).',
  'auth.errors.invalidInviteCodeFormat':
    'Format de code d’invitation invalide. Utilisez 4 à 20 lettres ou chiffres.',
  'guest.auth.getStarted': 'Commencer →',
  'guest.auth.alreadyHaveAccountSignIn':
    'Vous avez déjà un compte ? Se connecter',
  'guest.auth.appStore': 'App Store',
  'guest.auth.playStore': 'Google Play',
  'guest.auth.playStoreComingSoon': 'Google Play bientôt disponible',
  'guest.iosAppPrompt.title': 'Vous préférez l’app iOS ?',
  'guest.iosAppPrompt.body':
    'Installez SportEx100 depuis l’App Store pour accepter les invitations plus facilement.',
  'guest.iosAppPrompt.action': 'Ouvrir l’App Store',
  'guest.iosAppPrompt.dismiss': 'Continuer sur le web',
  'guest.androidAppPrompt.title': 'Vous préférez l’app Android ?',
  'guest.androidAppPrompt.body':
    'Installez SportEx100 depuis Google Play pour accepter les invitations plus facilement.',
  'guest.androidAppPrompt.action': 'Ouvrir Google Play',
  'guest.appPrompt.dismiss': 'Continuer sur le web',
  'settings.preferences': 'Préférences',
  'settings.showTutorial.title': 'Afficher le tutoriel',
  'settings.showTutorial.subtitle': 'Apprendre les bases',
  'settings.pulsePreview.title': 'Aperçu Pulse',
  'settings.pulsePreview.subtitle':
    'Afficher le fil Pool Pulse expérimental sur l’accueil',
  'settings.showTips.title': 'Réinitialiser les conseils',
  'settings.showTips.subtitle': 'Afficher les conseils d’accueil à nouveau',
  'settings.showTips.action': 'Réinitialiser',
  'settings.showTips.done': 'Terminé',
  'settings.notifications.title': 'Notifications',
  'settings.notifications.subtitle':
    'Transactions exécutées et messages du clavardage',
  'settings.support': 'Assistance',
  'settings.privacyPolicy': 'Politique de confidentialité',
  'settings.contactSupport': 'Contacter l’assistance',
  'settings.account': 'Compte',
  'settings.logOut': 'Déconnexion',
  'settings.deleteAccount.title': 'Supprimer le compte',
  'settings.deleteAccount.subtitle':
    'Supprime définitivement votre compte Sport Exchange 100',
  'settings.deleteAccount.promptTitle': 'Supprimer le compte ?',
  'settings.deleteAccount.promptBody':
    'Cette action supprime votre compte Sport Exchange 100 et vous déconnecte. Certains registres de transactions anonymisés peuvent être conservés pour la comptabilité du groupe. Cette action est irréversible.',
  'settings.deleteAccount.finalTitle': 'Confirmation finale',
  'settings.deleteAccount.finalBody':
    'Êtes-vous absolument certain ? Votre compte Sport Exchange 100 sera supprimé définitivement.',
  'settings.deleteAccount.deleteForever': 'Supprimer définitivement',
  'settings.language.title': 'Langue',
  'settings.language.subtitle': 'Choisir la langue d’affichage de SportEx100',
  'settings.language.current': 'Actuel : {language}',
  'settings.language.device': 'Par défaut de l’appareil',
  'settings.language.deviceDetail':
    'Utilise la langue de votre téléphone lorsque SportEx100 la prend en charge.',
  'settings.language.englishDetail': 'Use English in SportEx100.',
  'settings.language.frenchDetail': 'Utiliser le français dans SportEx100.',
  'settings.language.japaneseDetail': 'Market tab experiment in Japanese.',
  'settings.language.saved': 'Langue mise à jour',
  'settings.errors.openLink':
    'Impossible d’ouvrir le lien. Veuillez réessayer.',
  'settings.errors.openNotifications':
    'Impossible d’ouvrir les réglages de notification. Veuillez réessayer.',
  'settings.errors.verifySession':
    'Impossible de vérifier votre session. Veuillez vous reconnecter.',
  'settings.errors.deleteData':
    'Impossible de supprimer vos données SportEx100. Veuillez réessayer.',
  'settings.errors.deleteAccount': 'Impossible de supprimer votre compte.',
  'settings.errors.tutorialPreference':
    'Impossible de mettre à jour la préférence du tutoriel. Veuillez réessayer.',
  'settings.errors.pulsePreview':
    'Impossible de mettre à jour l’aperçu Pulse. Veuillez réessayer.',
  'settings.errors.resetTips':
    'Impossible de réinitialiser les conseils. Veuillez réessayer.',
  'settings.errors.languagePreference':
    'Impossible de mettre à jour la préférence de langue. Veuillez réessayer.',
  'profile.cancelTraderEdit': 'Annuler la modification du code trader',
  'profile.editTraderCode': 'Modifier le code trader',
  'home.header.title': 'Accueil',
  'home.filters.current': 'Actifs',
  'home.filters.archived': 'Archives',
  'home.actions.doneReordering': 'Terminé',
  'home.actions.joinPool': 'Rejoindre',
  'home.actions.createPool': 'Créer',
  'home.empty.filteredTitle': 'Aucun groupe',
  'home.empty.filteredBody': 'Essayez un autre statut pour voir vos groupes.',
  'home.empty.noPoolsTitle': 'Aucun groupe',
  'home.empty.noPoolsBody':
    'Rejoignez un groupe ou créez le vôtre pour commencer.',
  'home.reorder.banner':
    'Mode réorganisation : utilisez les flèches, puis touchez Terminé.',
  'home.pendingInvite.joined': 'Groupe {code} rejoint.',
  'home.pendingInvite.couldNotJoin':
    'Impossible de rejoindre le groupe {code}.',
  'home.welcome.context': 'Nouveau groupe ouvert',
  'home.welcome.emphasis': 'Tournoi mondial de soccer',
  'home.welcome.body':
    'Créez un nouveau groupe pour le Tournoi mondial de soccer 2026 et invitez vos amis avant le coup d’envoi.',
  'home.welcome.inviteBody':
    'Connectez-vous pour accepter votre invitation et suivre le tournoi avec votre groupe.',
  'home.welcome.signInApple': 'Connexion Apple',
  'home.welcome.signInGoogle': 'Connexion Google',
  'home.welcome.createPool': 'Créer un groupe',
  'home.welcome.acceptInvite': 'Accepter l’invitation',
  'home.promo.baseball.context': 'Le marché de présaison est ouvert',
  'home.promo.baseball.title': 'Baseball d’octobre',
  'home.promo.baseball.body': '30 équipes. Un champion. Négociez toute la saison.',
  'home.promo.baseball.join': 'Rejoindre',
  'home.promo.baseball.create': 'Créer',
  'home.promo.baseball.minimize': 'Réduire la promo du groupe baseball',
  'home.promo.baseball.expand': 'Développer la promo du groupe baseball',
  'home.join.error.required': 'Entrez un code de groupe',
  'home.join.error.failed': 'Impossible de rejoindre le groupe',
  'home.join.close': 'Fermer la fenêtre rejoindre un groupe',
  'home.join.title': 'Rejoindre le groupe',
  'home.join.subtitle': 'Votre place vous attend.',
  'home.join.picksUnlock': 'Faites vos choix tout de suite',
  'home.join.accessPolicy':
    'L’accès aux échanges dépend du réglage d’approbation du groupe.',
  'home.join.immediately': 'immédiatement',
  'home.join.tradingUnlocks': 'Échangez après approbation admin',
  'home.join.afterApproval': 'après approbation',
  'home.join.fieldLabel': 'Code du groupe',
  'home.join.placeholder': 'Entrer le code',
  'home.join.submit': 'Entrer →',
  'home.pool.status.preseason': 'Pré-saison',
  'home.pool.status.active': 'Actif',
  'home.pool.status.completed': 'Terminé',
  'home.pool.noOrders': 'Aucun ordre',
  'home.pool.oneOrder': '1 ordre',
  'home.pool.manyOrders': '{count} ordres',
  'home.pool.pendingApproval': 'Approbation admin en attente',
  'home.pool.stat.pnl': 'P&L',
  'home.pool.stat.ranking': 'Rang',
  'home.pool.points': 'Pts',
  'home.pool.upside': 'Potentiel',
  'home.pool.pickValue': 'Votre valeur prévue',
  'home.pool.unitsAt': '{count} unités à {marketRef}',
  'home.pool.completePicksToUnlock':
    'Terminez vos choix pour débloquer des suggestions.',
  'home.pool.awaitingApproval': 'En attente d’approbation admin',
  'home.pool.leading': 'Mène',
  'home.pool.rising': 'Monte',
  'home.pool.stable': 'Stable',
  'home.pool.youToday': 'Vous ↑{delta} auj.',
  'home.pool.traderToday': '{traderCode} ↑{delta} auj.',
  'home.pool.leader.empty': 'Soyez le premier à transiger',
  'home.pool.leader.you': 'Vous menez le groupe',
  'home.pool.leader.ahead': 'devant',
  'home.feed.sectionTitle': 'Pouls de la compétition',
  'home.feed.pulseLabel': 'Pulse',
  'home.feed.pulseLabel.recap': 'Résumé',
  'home.feed.pulseLabel.next': 'Proch.',
  'home.feed.loading': 'Chargement...',
  'home.feed.error': 'Impossible de charger les nouvelles.',
  'home.feed.retry': 'Réessayer',
  'home.feed.empty': 'Aucune mise à jour',
  'home.feed.emptyMetaType': 'Info',
  'home.feed.emptyMetaStatus': 'En attente',
  'home.feed.swipeHint': 'Balayez pour plus',
  'home.feed.noDetail': 'Aucun détail disponible.',
  'home.feed.floor': 'Min {value}',
  'home.feed.floorEmpty': 'Min --',
  'home.feed.buyTeam': 'Achat {teamCode}',
  'home.feed.sellTeam': 'Vente {teamCode}',
  'home.feed.category.upcoming': 'À venir',
  'home.feed.category.recap': 'Résumé',
  'home.feed.category.breaking': 'Dernière',
  'home.feed.category.injury': 'Blessure',
  'home.feed.category.live': 'Direct',
  'home.feed.category.result': 'Résultat',
  'home.feed.category.upset': 'Surprise',
  'home.feed.category.eliminated': 'Éliminée',
  'home.feed.category.advancing': 'Avance',
  'home.feed.category.momentum': 'Élan',
  'home.feed.category.schedule': 'Horaire',
  'home.feed.category.champion': 'Champion',
  'home.feed.category.update': 'Mise à jour',
  'home.tutorial.modalTitle': 'Pour commencer',
  'home.tutorial.cta.eyebrow': 'Bienvenue sur Sport Exchange 100',
  'home.tutorial.cta.title': 'Transigez des équipes. Déjouez vos amis.',
  'home.tutorial.cta.subtitle':
    'Prédisez les gagnants. Transigez futé. Grimpez au classement. Pas d’argent réel — juste l’honneur.',
  'home.tutorial.cta.start': 'Démarrer le tuto →',
  'home.tutorial.cta.startAccessibility': 'Démarrer le tutoriel',
  'home.tutorial.cta.duration': '2 minutes',
  'home.tutorial.cta.hide': 'Masquer',
  'home.tutorial.cta.hideAccessibility': 'Masquer le tutoriel',
  'home.tutorial.stepLabel': 'Étape {step} sur {total}',
  'home.tutorial.step1.heading': 'Les équipes gagnent des points',
  'home.tutorial.step1.body':
    'Les équipes gagnent des points selon leur progression dans le tournoi. Utilisez les choix pour estimer leur total final.',
  'home.tutorial.step2.heading': 'Les équipes règlent entre 0 et 100',
  'home.tutorial.step2.body':
    'Les prix bougent selon les attentes des participants. À la fin, chaque équipe règle à sa valeur finale. Le champion règle toujours à 100.',
  'home.tutorial.step3.heading': 'Les minimums protègent les points',
  'home.tutorial.step3.body':
    'Aucune équipe ne peut transiger sous son minimum, soit les points déjà gagnés en remportant des matchs.',
  'home.tutorial.step4.heading': 'Pas d’argent fictif pour acheter',
  'home.tutorial.step4.body':
    'Vous profitez quand l’équipe transige ou règle plus haut que votre prix d’achat. Commencez petit : essayez de transiger seulement 1 ou 2 contrats pendant que vous vous familiarisez.',
  'home.tutorial.step4.example':
    'Vous pensez qu’une équipe atteindra 30 points ? Achetez-la sous 30.',
  'home.tutorial.step5.heading': 'Vendez sans posséder',
  'home.tutorial.step5.body':
    'L’inverse fonctionne aussi : vous profitez quand l’équipe transige ou règle plus bas que votre prix de vente.',
  'home.tutorial.step5.example':
    'Vous pensez qu’une équipe sera éliminée tôt et réglera à 4 points ? Vendez-la plus haut et profitez-en !',
  'home.tutorial.step5.note': 'Éliminée en 6 matchs · gagne 4 points',
  'home.tutorial.step6.heading': 'Les groupes sont à somme nulle',
  'home.tutorial.step6.body':
    'Chaque point gagné par un participant est perdu par un autre. Les transactions déplacent seulement les points entre joueurs.',
  'home.tutorial.points.label': 'Hockey SportEx100 · 4 de 7',
  'home.tutorial.points.round': 'Ronde',
  'home.tutorial.points.gameWin': 'Gain match',
  'home.tutorial.points.roundWin': 'Gain ronde',
  'home.tutorial.points.maxPts': 'Max pts',
  'home.tutorial.championLabel': 'Le champion règle toujours à',
  'home.tutorial.chart.points': 'PTS',
  'home.tutorial.chart.playoffRound': 'RONDE',
  'home.tutorial.chart.gamesPlayed': 'MATCHS',
  'home.tutorial.chart.boughtAt': 'ACHAT {value}',
  'home.tutorial.chart.soldAt': 'VENTE {value}',
  'home.tutorial.chart.settlesAt': 'RÈGLE {value}',
  'home.tutorial.chart.gain': 'GAIN',
  'home.tutorial.chart.loss': 'PERTE',
  'home.tutorial.previousAccessibility': 'Étape précédente du tutoriel',
  'home.tutorial.nextAccessibility': 'Étape suivante du tutoriel',
  'home.tutorial.dismissAccessibility': 'Fermer le tutoriel',
  'home.tutorial.gotIt': 'Compris',
  'market.header.title': 'Marché',
  'market.noPool.message': 'Sélectionnez un groupe pour consulter le marché.',
  'market.pending.title': 'Approbation en attente',
  'market.pending.message':
    'Faites vos choix maintenant — les transactions seront débloquées après votre confirmation.',
  'market.pending.cta': 'Choix →',
  'market.invalidPool.title': 'Configuration du groupe invalide',
  'market.invalidPool.message':
    'Le groupe sélectionné n’a pas de données de saison. Retournez à l’accueil et sélectionnez-le de nouveau.',
  'market.filters.all': 'Tous',
  'market.filters.active': 'Actives',
  'market.filters.eliminated': 'Éliminées',
  'market.nhlFilter.conference': 'Association',
  'market.structureFilter.conference': 'Association',
  'market.nhlFilter.division': 'Division',
  'market.structureFilter.division': 'Division',
  'market.nhlFilter.all': 'Toutes',
  'market.structureFilter.all': 'Toutes',
  'market.nhlFilter.east': 'Est',
  'market.structureFilter.east': 'Est',
  'market.nhlFilter.west': 'Ouest',
  'market.structureFilter.west': 'Ouest',
  'market.nhlFilter.emptyHint': 'Essayez une autre association, division, un autre statut ou une autre recherche.',
  'market.structureFilter.emptyHint': 'Essayez une autre association, division, un autre statut ou une autre recherche.',
  'market.sections.champion': 'Champion',
  'market.sections.activeTeams': 'Équipes actives',
  'market.sections.eliminatedTeams': 'Équipes éliminées',
  'market.sections.group': 'Groupe {group}',
  'market.sections.otherTeams': 'Autres équipes',
  'market.sections.conference': 'Conférence · {conference}',
  'market.sections.division': 'Division · {division}',
  'market.sections.teamCount': '{visible}/{total} équipes',
  'market.phase.preseason': 'Pré-saison',
  'market.phase.regularSeason': 'Saison régulière',
  'market.phase.playoffCutover': 'Passage aux séries',
  'market.phase.playoffs': 'Séries',
  'market.phase.completed': 'Terminée',
  'market.record.wins': '{value} victoires',
  'market.record.losses': '{value} défaites',
  'market.record.overtimeLosses': '{value} défaites en prolongation',
  'market.record.ties': '{value} matchs nuls',
  'market.tradingClosed.notQualified': 'Cette équipe ne s’est pas qualifiée. Les transactions sont fermées.',
  'market.tradingClosed.unavailable': 'Les transactions sont fermées pour cette équipe.',
  'market.structure.americanLeague': 'Ligue américaine',
  'market.structure.nationalLeague': 'Ligue nationale',
  'market.structure.easternConference': 'Association de l’Est',
  'market.structure.westernConference': 'Association de l’Ouest',
  'market.structure.afc': 'Association américaine',
  'market.structure.nfc': 'Association nationale',
  'market.structure.americanLeagueEast': 'Est de la Ligue américaine',
  'market.structure.americanLeagueCentral': 'Centrale de la Ligue américaine',
  'market.structure.americanLeagueWest': 'Ouest de la Ligue américaine',
  'market.structure.nationalLeagueEast': 'Est de la Ligue nationale',
  'market.structure.nationalLeagueCentral': 'Centrale de la Ligue nationale',
  'market.structure.nationalLeagueWest': 'Ouest de la Ligue nationale',
  'market.structure.atlanticDivision': 'Atlantique',
  'market.structure.metropolitanDivision': 'Métropolitaine',
  'market.structure.centralDivision': 'Centrale',
  'market.structure.pacificDivision': 'Pacifique',
  'market.structure.afcEast': 'Est de l’AFC',
  'market.structure.afcNorth': 'Nord de l’AFC',
  'market.structure.afcSouth': 'Sud de l’AFC',
  'market.structure.afcWest': 'Ouest de l’AFC',
  'market.structure.nfcEast': 'Est de la NFC',
  'market.structure.nfcNorth': 'Nord de la NFC',
  'market.structure.nfcSouth': 'Sud de la NFC',
  'market.structure.nfcWest': 'Ouest de la NFC',
  'market.playoffPicture.divisionLeaders': 'Meneurs de division',
  'market.playoffPicture.wildCard': 'Équipes repêchées',
  'market.playoffPicture.outside': 'Hors du portrait des séries',
  'market.playoffPicture.cutLine': 'Ligne de qualification des équipes repêchées',
  'market.playoffPicture.cutLineAccessibilityLabel':
    'Ligne de qualification des équipes repêchées',
  'market.playoffPicture.unavailable': 'Portrait des séries temporairement indisponible',
  'market.playoffPicture.updatedAt': 'Classement mis à jour {timestamp}',
  'market.playoffPicture.delayed': 'Les données du classement peuvent être retardées.',
  'market.playoffPicture.metric.pctAccessibilityLabel': 'Pourcentage de victoires',
  'market.playoffPicture.metric.ptsAccessibilityLabel': 'Points au classement',
  'market.playoffPicture.metric.wcgbAccessibilityLabel':
    'Matchs de retard pour une place de repêchage',
  'market.playoffPicture.metric.seedAccessibilityLabel': 'Tête de série',
  'market.search.placeholder': 'Rechercher des équipes',
  'market.search.clear': 'Effacer la recherche d’équipe',
  'market.quote.bid': 'Achat',
  'market.quote.ask': 'Vente',
  'market.participant.virtual': 'Virtuel',
  'market.quote.bidSize': 'Achat Qté',
  'market.quote.askSize': 'Vente Qté',
  'market.action.buy': 'Achat',
  'market.action.sell': 'Vente',
  'market.floor.short': 'Min',
  'market.last.short': 'Dern.',
  'market.orderBook.loading': 'Chargement du carnet…',
  'market.orderBook.empty': 'Aucune profondeur',
  'market.orderBook.unavailable': 'Carnet indisponible',
  'market.orderBook.expand': 'Développer le carnet',
  'market.orderBook.collapse': 'Réduire le carnet',
  'market.orderBook.notAuthenticated': 'Non connecté',
  'market.bidAskTip.message':
    'Touchez l’achat ou la vente de l’équipe voulue pour passer un ordre.',
  'market.bidAskTip.action': 'Compris',
  'market.error.loadFailed':
    'Impossible de charger le marché. Vérifiez la connexion à l’API et réessayez.',
  'market.empty.noTeams': 'Aucune équipe disponible',
  'market.empty.worldCupHint':
    'Essayez un autre statut, groupe ou terme de recherche.',
  'market.empty.defaultHint':
    'Les données du marché apparaîtront au début de la saison.',
  'market.error.retry': 'Réessayer',
  'market.worldCup.searchPlaceholder': 'Rechercher',
  'market.worldCup.clearSearch': 'Effacer la recherche d’équipe',
  'market.worldCup.groups': 'Groupes',
  'market.worldCup.groupAll': 'Tous',
  'market.worldCup.status.all': 'Tous',
  'market.worldCup.status.live': 'En jeu',
  'market.worldCup.status.out': 'Sorties',
  'market.worldCup.status.allAccessibility':
    'Afficher toutes les équipes de soccer',
  'market.worldCup.status.liveAccessibility':
    'Afficher les équipes de soccer encore en jeu',
  'market.worldCup.status.outAccessibility':
    'Afficher les équipes de soccer éliminées',
  'market.guest.context': 'Chaque match fait bouger le marché.',
  'market.guest.emphasis': 'Achetez bas. Criez fort.',
  'market.guest.body':
    'Regardez le parquet s’illuminer d’ordres d’achat et de vente — puis frappez avant que vos amis fassent bouger le prix.',
  'market.guest.invite': 'Entrer un code d’invitation',
  'guest.landing.sports.baseball': 'Baseball',
  'guest.landing.sports.hockey': 'Hockey',
  'guest.landing.sports.football': 'Football américain',
  'guest.landing.background.label': 'Arrière-plans sportifs',
  'guest.landing.sports.title': 'En cours et à venir',
  'guest.landing.sports.now': 'EN COURS',
  'guest.landing.sports.playoffs': 'Séries éliminatoires',
  'guest.landing.sports.regularSeason': 'Saison régulière',
  'guest.landing.sports.playoffsStart': 'Début des séries le {date}',
  'guest.landing.sports.wildCardStart': 'Tour des équipes repêchées dès le {date}',
  'guest.landing.sports.planned': 'Date prévue',
  'guest.landing.sports.schedule': 'Consultez le calendrier de la ligue pour la prochaine saison.',
  'guest.landing.sports.helper': 'Créez votre groupe dès maintenant. Échangez pendant la saison et les séries.',
  'guest.landing.sports.leagueLink': 'Consulter le site officiel de la {league} (nouvel onglet)',
  'guest.landing.screenshots.market.imageAlt': 'Écran de l’application SportEx100 : marché des équipes de baseball',
  'guest.landing.screenshots.orderTicket.imageAlt': 'Écran de l’application SportEx100 : formulaire d’ordre de baseball',
  'guest.landing.screenshots.sportfolio.imageAlt': 'Écran de l’application SportEx100 : portefeuille de baseball',
  'guest.landing.screenshots.standings.imageAlt': 'Écran de l’application SportEx100 : classement du groupe de baseball',
  'guest.landing.screenshots.coach.imageAlt': 'Écran de l’application SportEx100 : coach du groupe de baseball',
  'guest.landing.web.hero.context': 'LE MONDE ENTIER EST EN JEU',
  'guest.landing.web.hero.body': 'Lancez votre propre marché libre, recrutez votre clan et surpassez-les tous. Le classement ne ment pas — les gagnants montent, les excuses coulent.',
  'guest.landing.web.market.body': 'Regardez le parquet s’illuminer d’ordres d’achat et de vente — puis frappez avant que vos amis fassent bouger le prix.',
  'guest.landing.web.picks.body': 'Prévoyez le tableau, suivez votre intuition et bondissez quand le prix est faux. Les audacieux sont payés — les timides se font plumer.',
  'guest.landing.web.social.emphasis': 'Clavardage SportEx100 en direct.',
  'guest.landing.web.social.body': 'Chaque groupe a un clavardage en direct — annoncez votre coup, affichez votre prix, défiez quelqu’un de prendre l’autre côté. Chaque opinion brûlante peut devenir une transaction.',
  'guest.landing.web.standings.body': 'Votre rang bouge à chaque match et chaque transaction. Aucun cadeau, aucune pitié — passez par-dessus celui qui est devant et mettez tout le classement sous pression.',
  'guest.landing.web.track.emphasis': 'Connaissez votre valeur.',
  'guest.landing.web.track.body': 'Ordres, transactions, points, valeurs d’équipe — au même endroit. Voyez ce que vous détenez, ce qui s’emballe et où se cache votre prochain gros coup.',
  'guest.landing.web.footer.title': 'Lancez votre marché en 2 minutes. Gratuit.',
  'guest.landing.web.footer.body': 'Invitez vos amis maintenant ou une fois le tournoi lancé. Rejoignez en tout temps, échangez les mouvements et prouvez que vous êtes le plus fin du groupe.',
  'guest.landing.web.cta.joinWithCode': 'ENTRER AVEC UN CODE',
  'guest.landing.web.join.helper': 'Connectez-vous ou créez un compte après avoir entré votre code. Nous le garderons prêt.',
  'guest.landing.web.footer.chip.noMoney': 'Sans argent réel',
  'guest.landing.web.footer.chip.anytime': 'Rejoignez en tout temps — même en cours de tournoi',
  'guest.landing.hero.context': 'LE MONDE ENTIER EST EN JEU',
  'guest.landing.hero.headline': 'Dominez le marché',
  'guest.landing.hero.body':
    'Lancez votre propre marché libre, recrutez votre clan et surpassez-les tous. Le classement ne ment pas — les gagnants montent, les excuses coulent.',
  'guest.landing.cta.createPool': 'LANCER UN MARCHÉ DE GROUPE',
  'guest.landing.cta.joinWithCode': 'ENTRER AVEC UN CODE',
  'guest.landing.cta.hasAccount': 'Vous avez déjà un compte ?',
  'guest.landing.cta.orDownload': "Ou téléchargez l'application",
  'guest.landing.join.poolCodeLabel': 'Code du groupe',
  'guest.landing.join.poolCodePlaceholder': 'Entrer le code',
  'guest.landing.join.continue': 'Continuer',
  'guest.landing.join.helper':
    'Connectez-vous ou créez un compte après avoir entré votre code. Nous le garderons prêt.',
  'guest.landing.join.error.required':
    'Entrez un code de groupe pour continuer.',
  'guest.landing.nav.create': 'Créer',
  'guest.landing.nav.market': 'Marché',
  'guest.landing.nav.picks': 'Choix',
  'guest.landing.nav.social': 'Social',
  'guest.landing.social.context': 'LE CLAVARDAGE EST LE PARQUET.',
  'guest.landing.social.emphasis': 'Clavardage SportEx100 en direct.',
  'guest.landing.social.body':
    'Chaque groupe a un clavardage en direct — annoncez votre coup, affichez votre prix, défiez quelqu’un de prendre l’autre côté. Chaque opinion brûlante peut devenir une transaction.',
  'guest.landing.social.imageAlt':
    "Écran de clavardage de groupe de l'application mobile SportEx100",
  'guest.landing.nav.standings': 'Classement',
  'guest.landing.standings.context': 'Chaque transaction bouscule la course.',
  'guest.landing.standings.emphasis': 'Prenez la couronne.',
  'guest.landing.standings.body':
    'Votre rang bouge à chaque match et chaque transaction. Aucun cadeau, aucune pitié — passez par-dessus celui qui est devant et mettez tout le classement sous pression.',
  'guest.landing.standings.imageAlt':
    "Écran de classement de l'application mobile SportEx100",
  'guest.landing.nav.track': 'Suivre',
  'guest.landing.contract.imageAlt':
    'Ecran mobile SportEx100 d’un contrat d’équipe',
  'guest.landing.orderForm.imageAlt':
    'Ecran mobile SportEx100 du formulaire d’ordre',
  'guest.landing.market.imageAlt':
    'Ecran mobile SportEx100 du marché de soccer',
  'guest.landing.picks.imageAlt': 'Ecran mobile SportEx100 des choix',
  'guest.landing.track.context': 'Chaque mouvement. Un seul écran.',
  'guest.landing.track.emphasis': 'Connaissez votre valeur.',
  'guest.landing.track.body':
    'Ordres, transactions, points, valeurs d’équipe — au même endroit. Voyez ce que vous détenez, ce qui s’emballe et où se cache votre prochain gros coup.',
  'guest.landing.track.imageAlt':
    "Écran portfolio de l'application mobile SportEx100",
  'guest.landing.footer.title': 'Lancez votre marché en 2 minutes. Gratuit.',
  'guest.landing.footer.body':
    'Invitez vos amis maintenant ou une fois le tournoi lancé. Rejoignez en tout temps, échangez les mouvements et prouvez que vous êtes le plus fin du groupe.',
  'guest.landing.footer.kicker': 'Votre tournoi. Vos règles. Votre marché.',
  'guest.landing.footer.chip.free': 'Gratuit',
  'guest.landing.footer.chip.noMoney': 'Sans argent réel',
  'guest.landing.footer.chip.anytime':
    'Rejoignez en tout temps — même en cours de tournoi',
  'order.title.place': 'Placer un ordre',
  'order.title.update': 'Modifier l’ordre',
  'order.form.side': 'Côté',
  'order.form.qty': 'Qté',
  'order.form.price': 'Prix',
  'order.action.buy': 'Achat',
  'order.action.sell': 'Vente',
  'order.preview.market': 'MKT',
  'order.preview.position': 'Position',
  'order.preview.myPickValue': 'Ma valeur : {value}',
  'order.preview.myPickValueLoading': 'Ma valeur : ...',
  'order.preview.setupSimulation': 'Définissez la valeur théorique dans Choix.',
  'order.validation.pricePositive': 'Prix positif requis',
  'order.validation.minPrice': 'Prix min : {price}',
  'order.validation.maxPrice': 'Prix max : {price}',
  'order.success.filled': 'Ordre exécuté ! {fills} {tradeLabel}.',
  'order.success.tradeOne': 'transaction',
  'order.success.tradeMany': 'transactions',
  'order.success.updated': 'Ordre modifié !',
  'order.success.placed': 'Ordre placé !',
  'order.error.title': 'Échec de l’ordre',
  'order.error.updateFailed': 'Impossible de modifier l’ordre',
  'order.error.submitFailed': 'Impossible de soumettre l’ordre',
  'order.action.cancel': 'Annuler',
  'order.action.submit': 'Soumettre',
  'order.action.submitting': 'Envoi',
  'order.action.update': 'Modifier',
  'order.action.updating': 'Modification',
  'order.alert.ok': 'OK',
  'picks.header.title': 'Coach de pool',
  'picks.noPool.message': 'Sélectionnez un groupe pour ouvrir Coach.',
  'picks.hint.seriesMessage':
    'Touchez une équipe pour choisir le gagnant et le score de série. V-D est votre fiche de série prévue. PTS est la valeur projetée de l’équipe.',
  'picks.hint.singleGameMessage':
    'Touchez une équipe pour choisir le gagnant. PTS est la valeur projetée de l’équipe.',
  'picks.hint.action': 'Compris',
  'picks.actions.pointSystem': 'Points',
  'picks.actions.reset': 'Réinitialiser',
  'picks.actions.tradeTeam': 'Échanger {team}',
  'picks.guide.pointSystem': 'Système de points',
  'picks.guide.bracketBuilder': 'Simulateur de points',
  'picks.scenario.title': 'Générateur de scénario',
  'picks.scenario.subtitle': 'Touchez les équipes pour prédire',
  'picks.loading.bracket': 'Chargement du tableau...',
  'picks.loading.saved': 'Chargement des pronostics enregistrés...',
  'picks.error.loadFailed':
    'Impossible de charger le tableau : {error}. Essayez d’actualiser la page.',
  'picks.empty.bracketPending': 'Tableau en attente',
  'picks.empty.noBracket': 'Aucun tableau',
  'picks.empty.worldCupPending':
    'Les choix de soccer seront débloqués quand les qualifications de groupe publieront les matchs des 32es.',
  'picks.empty.noBracketBody': 'Le tableau apparaîtra au début des séries.',
  'picks.status.groupStage': 'GROUPES',
  'picks.status.pickNow': 'CHOISIR',
  'picks.status.active': 'ACTIF',
  'picks.status.complete': 'TERMINÉ',
  'picks.status.pending': 'À VENIR',
  'picks.column.series': 'V-D',
  'picks.column.points': 'PTS',
  'picks.pending.opponent':
    'Choisissez le gagnant précédent pour fixer l’adversaire.',
  'picks.pending.matchup': 'Choisissez les gagnants de la ronde précédente.',
  'picks.pending.scoreHint': 'Touchez encore le gagnant pour ajuster le score',
  'picks.trade.title': 'Transiger {teamCode}',
  'picks.trade.value': 'Valeur choix {value}',
  'picks.trade.cancel': 'Annuler',
  'picks.trade.buy': 'Acheter à la valeur',
  'picks.trade.sell': 'Vendre à la valeur',
  'picks.alert.pendingTitle': 'Lecture seule pour membres en attente',
  'picks.alert.pendingMessage':
    'Le marché se débloque après approbation admin.',
  'picks.alert.resetTitle': 'Réinitialiser les choix',
  'picks.alert.resetMessage':
    'Voulez-vous vraiment réinitialiser tous vos choix pour ce tableau ?',
  'picks.alert.resetConfirm': 'Réinitialiser',
  'picks.alert.lockedTitle': '{round} verrouillée',
  'picks.alert.lockedMessage':
    'Presque · Terminez vos choix de {requiredRound} pour débloquer la prochaine ronde.',
  'picks.alert.ok': 'OK',
  'picks.alert.previousRound': 'la ronde précédente',
  'picks.alert.groupLimitTitle': 'Seulement trois peuvent avancer',
  'picks.alert.groupLimitMessage':
    'Choisissez les deux premiers et, au maximum, une équipe classée 3e dans ce groupe.',
  'picks.alert.qualifierLimitTitle': 'Limite de qualifiés',
  'picks.alert.qualifierLimitMessage':
    'Choisissez {count} qualifiés de soccer au total.',
  'picks.alert.bestThirdLimitTitle': 'Limite meilleurs 3es',
  'picks.alert.bestThirdLimitMessage':
    'Choisissez {count} meilleurs troisièmes au total.',
  'picks.worldCup.qualifierPicks': 'Choix qualifiés',
  'picks.worldCup.groupTitle': 'Groupe {group}',
  'picks.worldCup.seedGroupStage': 'Tête {seed} · Groupes',
  'picks.worldCup.topTwo': 'Top 2',
  'picks.worldCup.bestThird': 'Meilleur 3e',
  'picks.worldCup.groupRankFirst': 'Top 1',
  'picks.worldCup.groupRankSecond': 'Top 2',
  'picks.worldCup.groupRankThird': 'Meilleur 3e',
  'picks.worldCup.topTwoProgress': 'Top 2 {count}/{total}',
  'picks.worldCup.bestThirdProgress': 'Meil. 3e {count}/{total}',
  'picks.worldCup.bestThirdOrder': 'Ordre des meilleurs 3es',
  'picks.worldCup.bestThirdMoveUp': 'Monter {team}',
  'picks.worldCup.bestThirdMoveDown': 'Descendre {team}',
  'picks.round.groupStage': 'Phase de groupes',
  'picks.round.first': 'Première ronde',
  'picks.round.second': 'Deuxième ronde',
  'picks.round.roundOf16': 'Huitièmes de finale',
  'picks.round.quarterFinals': 'Quarts de finale',
  'picks.round.semiFinals': 'Demi-finales',
  'picks.round.final': 'Finale',
  'picks.round.finals': 'Finale',
  'picks.round.round': 'Ronde {sequence}',
  'picks.round.lockedAccessibility': '{round} verrouillée',
  'picks.competition.generic': 'Compétition',
  'picks.competition.hockey': 'Hockey SportEx100',
  'picks.competition.football': 'Football',
  'picks.competition.soccer': 'Soccer SportEx100',
  'picks.series.singleGame': 'Match unique',
  'picks.series.firstTo': 'Premier à {wins}',
  'picks.series.wins': '{format} ({wins} gains)',
  'picks.pointSystem.title': 'Système de points',
  'picks.pointSystem.close': 'Fermer le système de points',
  'picks.pointSystem.gameWin': 'Gain match',
  'picks.pointSystem.roundWin': 'Gain ronde',
  'picks.pointSystem.maxRound': 'Max ronde',
  'picks.pointSystem.champion': 'Le champion se règle toujours à',
  'picks.pointSystem.settlement': 'Règlement',
  'picks.pointSystem.settlementValue': 'Valeur de règlement',
  'picks.pointSystem.winPoints': 'Points par gain',
  'picks.pointSystem.perGameWin': 'Par gain de match',
  'picks.pointSystem.teamPoints': 'Points d’équipe',
  'picks.pointSystem.stage': 'Ronde',
  'picks.pointSystem.result': 'Résultat',
  'picks.pointSystem.earnAdds': 'Gagne +',
  'picks.pointSystem.settlesAt': 'Règle à',
  'picks.pointSystem.noValue': '—',
  'picks.pointSystem.value': 'Valeur',
  'picks.pointSystem.points': 'Points',
  'picks.pointSystem.qualification': 'Qualification aux séries',
  'picks.pointSystem.summary.soccer': 'Résultats de règlement',
  'picks.pointSystem.summary.standard': 'Détail du barème par ronde',
  'picks.pointSystem.soccer.winner': 'Champion',
  'picks.pointSystem.soccer.runnerUp': 'Finaliste',
  'picks.pointSystem.soccer.thirdPlace': '3e place',
  'picks.pointSystem.soccer.worldCupChampion': 'Champion',
  'picks.pointSystem.soccer.perfectPath': 'Parcours parfait',
  'picks.pointSystem.soccer.runningTotal': 'TOTAL {points}',
  'picks.pointSystem.winRound': 'GAGNER {round}',
  'picks.pointSystem.soccer.winRound': 'GAGNER {round}',
  'picks.pointSystem.soccer.exitValue': 'Valeur à la sortie',
  'picks.pointSystem.soccer.exitValueBody':
    'Une équipe se règle aux points qu’elle a déjà accumulés.',
  'picks.pointSystem.soccer.eliminatedGroupStage':
    'Éliminé en phase de groupes',
  'picks.pointSystem.soccer.advanceKnockout': 'Avance en élimination directe',
  'picks.pointSystem.soccer.winRound32': 'Gain en ronde des 32',
  'picks.pointSystem.soccer.winRound16': 'Gain en huitième de finale',
  'picks.pointSystem.soccer.winQuarterFinal': 'Gain en quart de finale',
  'picks.pointSystem.soccer.winSemiFinal': 'Gain en demi-finale',
  'picks.pointSystem.soccer.winThirdPlace': 'Gain du match pour la 3e place',
  'picks.pointSystem.soccer.eliminatedSemiFinal': 'Éliminé en demi-finale',
  'picks.pointSystem.soccer.eliminatedQuarterFinal':
    'Éliminé en quart de finale',
  'picks.pointSystem.soccer.eliminatedRound16': 'Éliminé en huitième de finale',
  'picks.pointSystem.soccer.eliminatedRound32': 'Éliminé en ronde des 32',
  'picks.pointSystem.soccer.missedKnockout': 'Non qualifié',
  'picks.pointSystem.soccer.eliminatedHeader': 'ÉLIMINÉ',
  'picks.pointSystem.imageAlt': 'Toujours 100',
  'picks.guest.context': 'Bâtissez votre projection.',
  'picks.guest.emphasis': 'Fiez-vous à votre instinct.',
  'picks.guest.body':
    'Prévoyez le tableau, suivez votre intuition et bondissez quand le prix est faux. Les audacieux sont payés — les timides se font plumer.',
  'picks.guest.invite': 'Entrer un code d’invitation',
  'standings.header.title': 'Classement',
  'standings.noPool.message': 'Sélectionnez un groupe pour voir le classement.',
  'standings.freshness.updated': 'Mis à jour {time}',
  'standings.podium.title': 'Top 3',
  'standings.podium.subtitle': 'Tableau d’honneur',
  'standings.table.rank': 'Rang',
  'standings.table.trader': 'Trader',
  'standings.table.trades': 'Tx',
  'standings.table.pnl': 'P&L',
  'standings.virtual.title': 'Traders virtuels',
  'standings.virtual.subtitle': 'Liquidité automatisée · exclue du classement humain',
  'standings.virtual.active': 'Actif',
  'standings.virtual.paused': 'En pause',
  'standings.empty.title': 'Aucun classement',
  'standings.empty.body':
    'Le classement apparaîtra dès les premières transactions.',
  'standings.you': 'VOUS',
  'standings.deletedTrader': 'SUPPR.',
  'standings.trade.one': 'tx',
  'standings.trade.many': 'tx',
  'standings.recentActivity.title': 'Activité récente',
  'standings.recentActivity.loading': 'Chargement de l’activité...',
  'standings.recentActivity.error': 'Impossible de charger le fil d’activité.',
  'standings.guest.context': 'Chaque transaction relance la chasse.',
  'standings.guest.emphasis': 'Visez le sommet.',
  'standings.guest.body':
    'Votre score et votre rang se mettent à jour après chaque match et chaque mouvement. Voyez qui est devant, réduisez l’écart et faites pression avec votre prochain échange.',
  'standings.guest.invite': 'Entrer un code d’invitation',
  'portfolio.guest.context': 'Voyez où vous en êtes.',
  'portfolio.guest.emphasis': 'Suivez votre score.',
  'portfolio.guest.body':
    'Vos choix, vos coups, vos points et votre progression sont ici.',
  'portfolio.guest.invite': 'Entrer un code d’invitation',
  'portfolio.noPool.message':
    'Rejoignez ou créez un groupe pour voir votre portfolio.',
  'portfolio.header.title': 'Sportfolio',
  'portfolio.filters.all': 'Tous',
  'portfolio.filters.positions': 'Positions',
  'portfolio.filters.orders': 'Ordres',
  'portfolio.filters.history': 'Historique',
  'portfolio.filters.buy': 'Achat',
  'portfolio.filters.sell': 'Vente',
  'portfolio.filters.settle': 'Règlement',
  'portfolio.filters.long': 'Long',
  'portfolio.filters.short': 'Court',
  'portfolio.sections.positions': 'Positions',
  'portfolio.sections.openOrders': 'Ordres ouverts',
  'portfolio.sections.history': 'Historique',
  'portfolio.empty.positions': 'Aucune position',
  'portfolio.empty.openOrders': 'Aucun ordre ouvert',
  'portfolio.empty.history': 'Aucune transaction',
  'portfolio.noPool': 'Sélectionnez un groupe pour voir votre sportfolio.',
  'portfolio.summary.totalPnl': 'P&L total',
  'portfolio.summary.rank': 'Rang',
  'portfolio.summary.settled': 'Réglé',
  'portfolio.summary.trading': 'Marché',
  'portfolio.metric.qty': 'QTÉ',
  'portfolio.metric.entry': 'ENTRÉE',
  'portfolio.metric.mark': 'MARK',
  'portfolio.metric.pnl': 'P&L',
  'portfolio.metric.date': 'DATE',
  'portfolio.metric.price': 'PRIX',
  'portfolio.metric.floor': 'MIN',
  'portfolio.metric.total': 'TOTAL',
  'portfolio.metric.counterparty': 'CONTREPARTIE',
  'portfolio.position.flat': 'FERMÉ',
  'portfolio.position.short': 'COURT',
  'portfolio.position.long': 'LONG',
  'portfolio.position.buyAvg': 'Achat moy.',
  'portfolio.position.sellAvg': 'Vente moy.',
  'portfolio.position.realized': 'Réalisé',
  'portfolio.position.unrealized': 'Non réal.',
  'portfolio.position.tradeLog': 'Transactions',
  'portfolio.settlementAudit.side': 'Côté',
  'portfolio.settlementAudit.entry': 'Entrée',
  'portfolio.settlementAudit.formula': 'Formule',
  'portfolio.settlementAudit.totalPnl': 'P&L TOTAL',
  'portfolio.settlementAudit.closedBefore': 'FERMÉ TÔT',
  'portfolio.settlementAudit.settledAtFloor': 'RÈGLEMENT AU MIN.',
  'portfolio.settlementAudit.tradeLog': 'TRANSACTIONS',
  'portfolio.settlementAudit.sideBuy': 'ACHAT',
  'portfolio.settlementAudit.sideSell': 'VENTE',
  'portfolio.settlementAudit.sideLong': 'LONG',
  'portfolio.settlementAudit.sideShort': 'COURT',
  'portfolio.settlementAudit.floor': 'MIN.',
  'portfolio.settlementAudit.basisMatched': 'apparié',
  'portfolio.settlementAudit.basisAvg': 'base moy.',
  'portfolio.settlementAudit.netOpen': 'net ouvert',
  'portfolio.settlementAudit.fillCountOne': '{count} exécution',
  'portfolio.settlementAudit.fillCount': '{count} exécutions',
  'portfolio.settlementAudit.buyCountOne': '{count} ACHAT',
  'portfolio.settlementAudit.buyCount': '{count} ACHATS',
  'portfolio.settlementAudit.sellCountOne': '{count} VENTE',
  'portfolio.settlementAudit.sellCount': '{count} VENTES',
  'portfolio.trade.settle': 'RÈGLE',
  'portfolio.trade.deletedTrader': 'SUPPR.',
  'portfolio.order.actionsAccessibility': 'Actions ordre {team}',
  'portfolio.order.promote': 'Partager',
  'portfolio.order.edit': 'Modifier',
  'portfolio.order.cancel': 'Annuler',
  'portfolio.order.market': 'MKT',
  'portfolio.order.hint':
    'Touchez un ordre pour partager, modifier ou annuler.',
  'portfolio.order.hintAction': 'Compris',
  'portfolio.order.cancelTitle': 'Annuler l’ordre',
  'portfolio.order.cancelPrompt': 'Voulez-vous vraiment annuler cet ordre ?',
  'portfolio.order.cancelNo': 'Non',
  'portfolio.order.cancelYes': 'Oui',
  'portfolio.order.cancelErrorTitle': 'Erreur',
  'portfolio.order.cancelErrorMessage': 'Impossible d’annuler l’ordre',
  'portfolio.order.poolNotReadyTitle': 'Groupe non prêt',
  'portfolio.order.poolNotReadyMessage':
    'Impossible de charger les données de marché pour cet ordre.',
  'portfolio.order.teamNotFoundTitle': 'Équipe introuvable',
  'portfolio.order.teamNotFoundMessage':
    'Impossible de charger les données de l’équipe.',
  'pulse.title': 'Pulse',
  'pulse.lane.pool': 'Groupe',
  'pulse.lane.game': 'Match',
  'pulse.lane.you': 'Vous',
  'pulse.story.trade': 'Transaction',
  'pulse.story.wall': 'Mur',
  'pulse.story.airball': 'Airball',
  'pulse.story.rankMove': 'Classement',
  'pulse.story.crowdedSide': 'Côté chargé',
  'pulse.story.contrarianPosition': 'Contrarien',
  'pulse.story.settlementSwing': 'Règlement',
  'pulse.story.gameResult': 'Résultat',
  'pulse.story.eliminationQualification': 'Tableau',
  'pulse.story.floorChange': 'Plancher',
  'pulse.story.personalImpact': 'Vous',
  'pulse.impact.pnl': 'P&L',
  'pulse.impact.rank': 'Rang',
  'pulse.impact.exposure': 'Exposition',
  'pulse.impact.marketActivity': 'Marché',
  'pulse.impact.settlement': 'Règlement',
  'pulse.impact.gameResult': 'Match',
  'pulse.impact.socialPrompt': 'Chat',
  'pulse.empty.title': 'Groupe calme',
  'pulse.empty.body': 'Aucun moment important dans la fenêtre Pulse actuelle.',
  'pulse.empty.action': 'Ouvrir le marché',
  'pulse.action.share': 'Partager',
  'pulse.action.open': 'Ouvrir',
  'pulse.action.viewAll': 'Voir tout',
  'pulse.action.showLess': 'Réduire',
  'pulse.time.justNow': "À L'INSTANT",
  'pulse.time.minutesAgo': 'IL Y A {count}M',
  'pulse.time.hoursAgo': 'IL Y A {count}H',
  'pulse.time.daysAgo': 'IL Y A {count}J',
  'pulse.time.lastWindow': 'DERNIÈRES {duration}',
  'pulse.tradeBehavior.pill.stacking': 'STACKING',
  'pulse.tradeBehavior.pill.dumping': 'DUMPING',
  'pulse.tradeBehavior.pill.heatingUp': 'ÇA CHAUFFE',
  'pulse.tradeBehavior.pill.whale': 'BALEINE',
  'pulse.tradeBehavior.side.buy': 'ACHAT',
  'pulse.tradeBehavior.side.sell': 'VENTE',
  'pulse.tradeBehavior.context.pattern':
    '{side} {quantity} · {fills} EXÉC. · MOY {avg}',
  'pulse.tradeBehavior.headline.stacking': '{trader} accumule {team}',
  'pulse.tradeBehavior.headline.dumping': '{trader} liquide {team}',
  'pulse.tradeBehavior.headline.whale': '{buyer} achète {team} de {seller}',
  'pulse.orderPressure.pill.wall': 'MUR',
  'pulse.orderPressure.pill.airball': 'AIRBALL',
  'pulse.orderPressure.side.buyHeadline': 'achat',
  'pulse.orderPressure.side.sellHeadline': 'vente',
  'pulse.orderPressure.headline.wall': 'Mur d’achat {team}',
  'pulse.orderPressure.headline.wallSell': 'Mur de vente {team}',
  'pulse.orderPressure.headline.airball': 'Airball {side} {team}',
  'pulse.orderPressure.context.full':
    '{side} {price} × {quantity} · VALEUR {value} · MARK {mark}',
  'pulse.teamHeat.headline': '{team} chauffe.',
  'pulse.teamHeat.subline': '{traders} traders · {qty} contrats · moy {avg}',
  'pulse.teamHeat.metric.traders': '{count} traders',
  'pulse.teamHeat.metric.qtyMoved': '{qty} contrats',
  'pulse.teamHeat.metric.avg': 'moy {avg}',
  'pulse.position.asOf': 'MAJ {time}',
  'pulse.position.pill.crowded': 'CHARGÉ',
  'pulse.position.pill.contrarian': 'CONTRARIEN',
  'pulse.position.side.long': 'LONG',
  'pulse.position.side.short': 'SHORT',
  'pulse.position.intensity.allIn': 'ALL-IN',
  'pulse.position.intensity.lopsided': 'DÉSÉQUILIBRÉ',
  'pulse.position.intensity.heavily': 'FORTEMENT',
  'pulse.position.intensity.leaning': 'PENCHE',
  'pulse.position.context.value': 'VALEUR {value}{qty}',
  'pulse.position.context.qtySuffix': ' · QTÉ {qty}',
  'pulse.position.context.liveQty': 'ACTIF · QTÉ {qty}',
  'pulse.position.context.live': 'ACTIF',
  'pulse.settlement.pill': 'RÈGLE',
  'pulse.settlement.best': 'MEILLEUR',
  'pulse.settlement.worst': 'PIRE',
  'pulse.settlement.vs': 'VS',
  'pulse.settlement.traders': '{count} TRADERS',
  'pulse.settlement.points': '{points} PTS',
  'pulse.fallback.headline': 'Moment du groupe',
  'pulse.fallback.impact': 'Touchez pour voir le contexte du groupe.',
  'pulse.accessibility.story': '{lane}. {headline}. {impact}',
  'portfolio.history.loadMore': 'Charger plus ({remaining} restants)',
};

export const ja: Partial<Record<TranslationKey, string>> = {
  'common.back': '戻る',
  'common.cancel': 'キャンセル',
  'common.continue': '続ける',
  'common.edit': '編集',
  'common.retry': '再試行',
  'common.save': '保存',
  'common.saved': '保存済み',
  'common.refresh': '更新',
  'common.terms': '利用規約',
  'common.closeProfileSettings': 'プロフィール設定を閉じる',
  'common.openProfileSettings': 'プロフィール設定を開く',
  'common.editProfileAvatar': 'プロフィール画像を編集',
  'common.openMenu': 'メニューを開く',
  'common.closeMenu': 'メニューを閉じる',
  'common.navigation': 'ナビゲーション',
  'tabs.home': 'ホーム',
  'tabs.market': '市場',
  'tabs.portfolio': 'マイフォリオ',
  'tabs.standings': '順位',
  'tabs.picks': 'コーチ',
  'stack.createPool': 'プール作成',
  'stack.signIn': 'サインイン',
  'stack.createAccount': 'アカウント作成',
  'poolWizard.title.create': 'プール作成',
  'poolWizard.title.manage': 'プール管理',
  'poolWizard.tabs.details': '詳細',
  'poolWizard.tabs.members': 'メンバー',
  'poolWizard.fields.poolName': 'プール名',
  'poolWizard.fields.description': '説明（任意）',
  'poolWizard.fields.valuePerPoint': 'ポイント倍率（任意）',
  'poolWizard.placeholders.poolName': '2026年鈴木ファミリーワールドサッカー',
  'poolWizard.placeholders.description': '短い説明',
  'poolWizard.placeholders.valuePerPoint': '0.1〜10の値',
  'poolWizard.placeholders.inviteEmails': 'メールアドレスを入力',
  'poolWizard.sections.poolSettings': 'プール設定',
  'poolWizard.sections.competition': '大会',
  'poolWizard.sections.selectCompetition': '大会を選択',
  'poolWizard.sections.poolCode': 'プールコード',
  'poolWizard.sections.invites': 'メールで招待',
  'poolWizard.poolCode.hint':
    'このコードまたはリンクを共有して、他のメンバーを招待できます。',
  'poolWizard.approval.title': '管理者の承認を必須にする',
  'poolWizard.approval.description':
    'オフにすると、保留中のリクエストが有効になり、今後プールコードで参加するメンバーはすぐに参加できます。',
  'poolWizard.approval.required': '必須',
  'poolWizard.approval.notRequired': '不要',
  'poolWizard.actions.saveChanges': '変更を保存',
  'poolWizard.actions.close': 'プール管理を閉じる',
  'poolWizard.actions.copy': 'コピー',
  'poolWizard.actions.copyLink': 'リンクをコピー',
  'poolWizard.actions.copied': 'コピー済み',
  'poolWizard.actions.shareLink': 'リンクを共有',
  'poolWizard.actions.sendInvites': '招待状を送信',
  'poolWizard.actions.accept': '承認',
  'poolWizard.actions.enable': '有効化',
  'poolWizard.actions.createPool': 'プール作成',
  'poolWizard.actions.acceptInvite': '招待を承認',
  'poolWizard.actions.rejectInvite': '招待を拒否',
  'poolWizard.actions.removePlayer': 'プレイヤー削除',
  'poolWizard.actions.resendInvite': '招待を再送信',
  'poolWizard.actions.manageMember': 'メンバー管理',
  'poolWizard.errors.loadMembers': 'メンバーを読み込めませんでした。',
  'poolWizard.errors.nameTooShort': 'プール名は3文字以上にしてください。',
  'poolWizard.errors.nameTooLong': 'プール名は120文字未満にしてください。',
  'poolWizard.errors.descriptionTooLong': '説明は1024文字未満にしてください。',
  'poolWizard.errors.selectCompetition': '大会を選択してください。',
  'poolWizard.errors.pointValue':
    'ポイント倍率は0.1〜10で、小数は1桁までにしてください。',
  'poolWizard.errors.updateFailed': 'プールを更新できませんでした。',
  'poolWizard.errors.createFailed': 'プールを作成できませんでした。',
  'poolWizard.errors.enterEmail': 'メールアドレスを1件以上入力してください。',
  'poolWizard.errors.inviteFailed': '招待状を送信できませんでした。',
  'poolWizard.errors.resendInviteFailed': '招待を再送信できませんでした。',
  'poolWizard.errors.updateMember': 'メンバーを更新できませんでした。',
  'poolWizard.invites.sent': '招待状を送信しました。',
  'poolWizard.invites.resent': '招待を再送信しました。',
  'poolWizard.invites.or': 'または',
  'poolWizard.invites.hint': 'カンマまたはスペース区切り。',
  'poolWizard.members.filter.all': 'すべて',
  'poolWizard.members.filter.pending': '承認待ち',
  'poolWizard.members.filter.active': 'アクティブ',
  'poolWizard.members.adminOnly': 'メンバー管理はプール管理者のみ可能です。',
  'poolWizard.members.section.pending': '承認待ち',
  'poolWizard.members.section.active': 'アクティブプレーヤー',
  'poolWizard.members.emptyAll': 'メンバーはまだいません。',
  'poolWizard.members.emptyPending': '承認待ちメンバーはいません。',
  'poolWizard.members.emptyActive': 'アクティブプレーヤーはいません。',
  'poolWizard.member.admin': '管理者',
  'poolWizard.member.appleRelay.via': 'Apple経由',
  'poolWizard.member.appleRelay.invited': 'Apple経由で招待',
  'poolWizard.member.appleRelay.joined': 'Apple経由で参加',
  'poolWizard.memberStatus.pending': '承認待ち',
  'poolWizard.memberStatus.active': '有効',
  'poolWizard.memberStatus.disabled': '無効',
  'poolWizard.memberStatus.rejected': '拒否済み',
  'poolWizard.create.description':
    'プール名を決めてリーグシーズンを選択。友だちの招待は後でできます。',
  'auth.redirectingToSignIn': 'サインインへ移動中...',
  'auth.finishingSignIn': 'サインインを完了中...',
  'auth.tradingAccess': '取引へアクセス',
  'auth.welcome': 'Sport Exchange 100 へようこそ',
  'auth.appleSubtitle': 'Appleでサインイン',
  'auth.googleSubtitle': 'Googleでサインイン',
  'auth.continueWithApple': 'Appleで続ける',
  'auth.continueWithGoogle': 'Googleで続ける',
  'auth.signIn': 'サインイン',
  'auth.signingIn': 'サインイン中...',
  'auth.signInInProgress': 'サインイン処理中です。しばらくお待ちください。',
  'auth.appleIncomplete':
    'Appleサインインが完了しませんでした。返されたClerk状態をデバイスログで確認してください。',
  'auth.providerNeedsVerification':
    '{provider}サインインには追加の確認が必要です。',
  'auth.providerFailed': '{provider}でサインインできませんでした',
  'auth.providerContextFailed': '{context}からサインインできませんでした。',
  'auth.errors.androidBrowserUnavailable':
    'Androidでのサインインにはブラウザーアプリが必要です。Chrome、Firefox、または別のブラウザーをインストールまたは有効化してから、もう一度お試しください。',
  'auth.errors.invalidInviteCode':
    '有効なプールコードを入力してください（4〜20文字の英数字）。',
  'auth.errors.invalidInviteCodeFormat':
    '招待コードの形式が正しくありません。4〜20文字の英数字を使用してください。',
  'guest.auth.getStarted': 'プレイ →',
  'guest.auth.alreadyHaveAccountSignIn':
    'すでにアカウントがありますか？ サインイン',
  'guest.auth.appStore': 'App Store',
  'guest.auth.playStore': 'Google Play',
  'guest.auth.playStoreComingSoon': 'Google Play 近日公開',
  'guest.iosAppPrompt.title': 'iOSアプリを使いますか？',
  'guest.iosAppPrompt.body':
    '招待状をスムーズに受け取るには、App StoreからSportEx100をインストールしてください。',
  'guest.iosAppPrompt.action': 'App Storeを開く',
  'guest.iosAppPrompt.dismiss': 'Webで続ける',
  'guest.androidAppPrompt.title': 'Androidアプリを使いますか？',
  'guest.androidAppPrompt.body':
    '招待状をスムーズに受け取るには、Google PlayからSportEx100をインストールしてください。',
  'guest.androidAppPrompt.action': 'Google Playを開く',
  'guest.appPrompt.dismiss': 'Webで続ける',
  'settings.preferences': '設定',
  'settings.showTutorial.title': '解説を表示',
  'settings.showTutorial.subtitle': '基本を学ぶ',
  'settings.pulsePreview.title': 'Pulseプレビュー',
  'settings.pulsePreview.subtitle': 'ホームに実験版Pool Pulseフィードを表示',
  'settings.showTips.title': '解説をもう一度見る',
  'settings.showTips.action': '見る',
  'settings.showTips.done': '完了',
  'settings.notifications.title': '通知',
  'settings.notifications.subtitle': '取引成立とチャット',
  'settings.support': 'サポート',
  'settings.privacyPolicy': 'プライバシーポリシー',
  'settings.contactSupport': 'お問い合せ',
  'settings.account': 'アカウント',
  'settings.logOut': 'ログアウト',
  'settings.deleteAccount.title': 'アカウントを削除',
  'settings.deleteAccount.subtitle':
    'Sport Exchange 100 アカウントを削除します',
  'settings.deleteAccount.promptTitle': 'アカウントを削除しますか？',
  'settings.deleteAccount.promptBody':
    'Sport Exchange 100 アカウントを削除し、サインアウトします。プールの会計処理のため、匿名化された取引記録が残る場合があります。この操作は取り消せません。',
  'settings.deleteAccount.finalTitle': '最終確認',
  'settings.deleteAccount.finalBody':
    '本当に削除しますか？Sport Exchange 100 アカウントは完全に削除され、元に戻せません。',
  'settings.deleteAccount.deleteForever': '完全に削除する',
  'settings.language.title': '言語',
  'settings.language.subtitle': 'SportEx100 の表示言語を選択',
  'settings.language.current': '現在: {language}',
  'settings.language.device': 'デバイス設定',
  'settings.language.deviceDetail':
    'SportEx100 が対応している場合、端末の言語を使います。',
  'settings.language.englishDetail': 'SportEx100 を英語で表示。',
  'settings.language.frenchDetail': 'SportEx100 をフランス語で表示。',
  'settings.language.japaneseDetail': 'マーケットタブの日本語実験版。',
  'settings.language.saved': '言語を更新しました',
  'settings.errors.openLink':
    'リンクを開けませんでした。もう一度お試しください。',
  'settings.errors.openNotifications':
    '通知設定を開けませんでした。もう一度お試しください。',
  'settings.errors.verifySession':
    'セッションを確認できませんでした。もう一度サインインしてください。',
  'settings.errors.deleteData':
    'SportEx100 のデータを削除できませんでした。もう一度お試しください。',
  'settings.errors.deleteAccount': 'アカウントを削除できませんでした。',
  'settings.errors.tutorialPreference':
    'ヒント設定を更新できませんでした。もう一度お試しください。',
  'settings.errors.pulsePreview':
    'Pulseプレビューを更新できませんでした。もう一度お試しください。',
  'settings.errors.languagePreference':
    '言語設定を更新できませんでした。もう一度お試しください。',
  'profile.cancelTraderEdit': 'トレーダーコード編集をキャンセル',
  'profile.editTraderCode': 'トレーダーコードを編集',
  'home.header.title': 'ホーム',
  'home.filters.current': '現在',
  'home.filters.archived': 'アーカイブ',
  'home.actions.doneReordering': '並び替え完了',
  'home.actions.joinPool': 'プールに参加',
  'home.actions.createPool': 'プール作成',
  'home.empty.filteredTitle': '該当プールなし',
  'home.empty.filteredBody': '別のステータスを試してください。',
  'home.empty.noPoolsTitle': 'プールなし',
  'home.empty.noPoolsBody':
    '既存のプールに参加するか、新しく作成してください。',
  'home.reorder.banner': '並べ替え中: 矢印を使い、完了をタップします。',
  'home.pendingInvite.joined': 'プール {code} に参加しました。',
  'home.pendingInvite.couldNotJoin': 'プール {code} に参加できませんでした。',
  'home.welcome.context': '新しいプールがオープン',
  'home.welcome.emphasis': 'サッカー世界トーナメント',
  'home.welcome.body':
    '2026サッカー世界トーナメントの新しいプールを作成し、キックオフ前に友だちを招待しよう。',
  'home.welcome.inviteBody':
    'サインインしてプール招待状を受け取り、仲間と大会をフォローしよう。',
  'home.welcome.signInApple': 'Appleでサインイン',
  'home.welcome.signInGoogle': 'Googleでサインイン',
  'home.welcome.createPool': '新しいプールを作成',
  'home.welcome.acceptInvite': '招待状を受ける',
  'home.promo.baseball.context': 'プレシーズン取引スタート',
  'home.promo.baseball.title': '10月のベースボール',
  'home.promo.baseball.body': '30チーム。王者は1つ。シーズンを通して取引しよう。',
  'home.promo.baseball.join': '参加',
  'home.promo.baseball.create': '作成',
  'home.promo.baseball.minimize': '野球プールの案内を縮小',
  'home.promo.baseball.expand': '野球プールの案内を展開',
  'home.join.error.required': 'プールコードを入力してください',
  'home.join.error.failed': 'プールに参加できませんでした',
  'home.join.close': 'プール参加画面を閉じる',
  'home.join.title': 'プールに参加',
  'home.join.subtitle': 'ポジションを取ろう。',
  'home.join.picksUnlock': '予想を立てよう。',
  'home.join.accessPolicy': '取引へのアクセスはプールの承認設定に従います。',
  'home.join.immediately': '今すぐ利用可',
  'home.join.tradingUnlocks': '取引開始',
  'home.join.afterApproval': '管理者承認後',
  'home.join.fieldLabel': 'プールコード',
  'home.join.placeholder': 'コード入力',
  'home.join.submit': 'プレイ →',
  'home.pool.status.preseason': '開幕前',
  'home.pool.status.active': '進行中',
  'home.pool.status.completed': '完了',
  'home.pool.noOrders': '注文なし',
  'home.pool.oneOrder': '1件の注文',
  'home.pool.manyOrders': '{count}件の注文',
  'home.pool.pendingApproval': '管理者承認待ち',
  'home.pool.stat.pnl': '損益計算（P&L）',
  'home.pool.stat.ranking': '順位',
  'home.pool.points': 'ポイント',
  'home.pool.upside': '上昇余地',
  'home.pool.pickValue': '予想値',
  'home.pool.unitsAt': '{marketRef}で{count}単位',
  'home.pool.completePicksToUnlock': '予想を完了すると取引候補が表示されます。',
  'home.pool.awaitingApproval': '管理者承認待ち',
  'home.pool.leading': '首位',
  'home.pool.rising': '上昇',
  'home.pool.stable': '変動なし',
  'home.pool.youToday': 'あなた ↑{delta} 今日',
  'home.pool.traderToday': '{traderCode} ↑{delta} 今日',
  'home.pool.leader.empty': '取引に一番乗り',
  'home.pool.leader.you': 'トップだ。おめでとう。',
  'home.pool.leader.ahead': '差',
  'home.feed.sectionTitle': '大会パルス',
  'home.feed.pulseLabel': 'パルス',
  'home.feed.pulseLabel.recap': 'ハイライト',
  'home.feed.pulseLabel.next': '次戦',
  'home.feed.loading': '読み込み中...',
  'home.feed.error': '最新情報を読み込めません。',
  'home.feed.retry': '再試行',
  'home.feed.empty': '更新なし',
  'home.feed.emptyMetaType': 'お得な情報',
  'home.feed.emptyMetaStatus': '待機中',
  'home.feed.swipeHint': 'スワイプ',
  'home.feed.noDetail': '記事の詳細はありません。',
  'home.feed.floor': '下限 {value}',
  'home.feed.floorEmpty': '下限 --',
  'home.feed.buyTeam': '{teamCode} 買い',
  'home.feed.sellTeam': '{teamCode} 売り',
  'home.feed.category.upcoming': '予定',
  'home.feed.category.recap': '回顧',
  'home.feed.category.breaking': '速報',
  'home.feed.category.injury': '負傷',
  'home.feed.category.live': '実況',
  'home.feed.category.result': '結果',
  'home.feed.category.upset': '番狂わせ',
  'home.feed.category.eliminated': '敗退',
  'home.feed.category.advancing': '進出',
  'home.feed.category.momentum': '勢い',
  'home.feed.category.schedule': '日程',
  'home.feed.category.champion': '優勝',
  'home.feed.category.update': '更新',
  'home.tutorial.modalTitle': 'はじめに',
  'home.tutorial.cta.eyebrow': 'Sport Exchange 100へようこそ',
  'home.tutorial.cta.title': 'チームを取引。ランキングを勝ち登ろう。',
  'home.tutorial.cta.subtitle':
    '勝者を予想し、賢く取引し、順位を上げる。現金なし、名誉だけ。',
  'home.tutorial.cta.start': 'ヒント→',
  'home.tutorial.cta.startAccessibility': 'ヒントを見る',
  'home.tutorial.cta.duration': '所要時間２分',
  'home.tutorial.cta.hide': '見ない',
  'home.tutorial.cta.hideAccessibility': 'ヒントを見ない',
  'home.tutorial.stepLabel': 'ステップ {step}/{total}',
  'home.tutorial.step1.heading':
    '例：ホッケー（NHL)。チームはプレイオフ点を獲得。',
  'home.tutorial.step1.body':
    '勝ち進むほど点数は増える。予想で最終点を見積もろう。',
  'home.tutorial.step2.heading': '決済は0から100',
  'home.tutorial.step2.body':
    '参加者の見方で価格が動く。参加者の数や点数に応じて配当ポイントは自然に調整。優勝チームの得点は常に100。',
  'home.tutorial.step3.heading': '下限が獲得点を守る。',
  'home.tutorial.step3.body':
    'チームは、累計ポイント点を下回って取引できません。',
  'home.tutorial.step4.heading': '架空資金で買う仕組みではありません。',
  'home.tutorial.step4.body':
    '買った価格より高く取引または決済されると利益になります。少数から始めましょう。慣れるまでは1、2枚から。',
  'home.tutorial.step4.example': '30点まで行くと思ったら、30未満で買います。',
  'home.tutorial.step5.heading': '持っていなくても売れます。',
  'home.tutorial.step5.body':
    '逆も同じです。売った価格より低く取引または決済されると利益です。',
  'home.tutorial.step5.example':
    '早期敗退して4点で決済されると思うチームなら、もっと高く売って利益を狙おう！',
  'home.tutorial.step5.note': '6試合で敗退 · 4点獲得',
  'home.tutorial.step6.heading': 'プールはゼロサム',
  'home.tutorial.step6.body':
    '君の1点の利益は、誰かの1点の損失です。取引はポイントを参加者間で動かす。',
  'home.tutorial.points.label': 'SportEx100 ホッケー · 7戦4勝',
  'home.tutorial.points.round': 'ラウンド',
  'home.tutorial.points.gameWin': '試合勝',
  'home.tutorial.points.roundWin': '突破',
  'home.tutorial.points.maxPts': '最大点',
  'home.tutorial.championLabel': '優勝チームは必ず',
  'home.tutorial.chart.points': '点',
  'home.tutorial.chart.playoffRound': 'ラウンド',
  'home.tutorial.chart.gamesPlayed': '試合数',
  'home.tutorial.chart.boughtAt': '{value}で買い',
  'home.tutorial.chart.soldAt': '{value}で売り',
  'home.tutorial.chart.settlesAt': '{value}で決済',
  'home.tutorial.chart.gain': '利益',
  'home.tutorial.chart.loss': '損失',
  'home.tutorial.previousAccessibility': '前のチュートリアル',
  'home.tutorial.nextAccessibility': '次のチュートリアル',
  'home.tutorial.dismissAccessibility': 'チュートリアルを閉じる',
  'home.tutorial.gotIt': '了解',
  'market.header.title': 'マーケット',
  'market.noPool.message': 'マーケットを見るにはプールを選択してください。',
  'market.pending.title': '承認待ち',
  'market.pending.message': '今すぐ予想を設定できます。承認後に取引できます。',
  'market.pending.cta': '予想 →',
  'market.invalidPool.title': 'プール設定が無効です',
  'market.invalidPool.message':
    '選択したプールにシーズン情報がありません。ホームに戻ってプールを選び直してください。',
  'market.filters.all': 'すべて',
  'market.filters.active': '勝ち残り',
  'market.filters.eliminated': '敗退',
  'market.nhlFilter.conference': 'カンファレンス',
  'market.structureFilter.conference': 'カンファレンス',
  'market.nhlFilter.division': '地区',
  'market.structureFilter.division': '地区',
  'market.nhlFilter.all': 'すべて',
  'market.structureFilter.all': 'すべて',
  'market.nhlFilter.east': '東',
  'market.structureFilter.east': '東',
  'market.nhlFilter.west': '西',
  'market.structureFilter.west': '西',
  'market.nhlFilter.emptyHint': 'カンファレンス、地区、状態、または検索条件を変更してください。',
  'market.structureFilter.emptyHint': 'カンファレンス、地区、状態、または検索条件を変更してください。',
  'market.sections.champion': '優勝',
  'market.sections.activeTeams': '勝ち残りチーム',
  'market.sections.eliminatedTeams': '敗退チーム',
  'market.sections.group': 'グループ {group}',
  'market.sections.otherTeams': 'その他',
  'market.sections.conference': 'カンファレンス・{conference}',
  'market.sections.division': '地区・{division}',
  'market.sections.teamCount': '{visible}/{total} チーム',
  'market.phase.preseason': 'プレシーズン',
  'market.phase.regularSeason': 'レギュラーシーズン',
  'market.phase.playoffCutover': 'プレーオフ移行',
  'market.phase.playoffs': 'プレーオフ',
  'market.phase.completed': '終了',
  'market.record.wins': '{value} 勝',
  'market.record.losses': '{value} 敗',
  'market.record.overtimeLosses': '延長戦 {value} 敗',
  'market.record.ties': '{value} 分',
  'market.tradingClosed.notQualified': 'このチームはプレーオフに進出しませんでした。取引は終了しています。',
  'market.tradingClosed.unavailable': 'このチームの取引は終了しています。',
  'market.structure.americanLeague': 'アメリカン・リーグ',
  'market.structure.nationalLeague': 'ナショナル・リーグ',
  'market.structure.easternConference': 'イースタン・カンファレンス',
  'market.structure.westernConference': 'ウェスタン・カンファレンス',
  'market.structure.afc': 'アメリカン・フットボール・カンファレンス',
  'market.structure.nfc': 'ナショナル・フットボール・カンファレンス',
  'market.structure.americanLeagueEast': 'アメリカン・リーグ東地区',
  'market.structure.americanLeagueCentral': 'アメリカン・リーグ中地区',
  'market.structure.americanLeagueWest': 'アメリカン・リーグ西地区',
  'market.structure.nationalLeagueEast': 'ナショナル・リーグ東地区',
  'market.structure.nationalLeagueCentral': 'ナショナル・リーグ中地区',
  'market.structure.nationalLeagueWest': 'ナショナル・リーグ西地区',
  'market.structure.atlanticDivision': 'アトランティック',
  'market.structure.metropolitanDivision': 'メトロポリタン',
  'market.structure.centralDivision': 'セントラル',
  'market.structure.pacificDivision': 'パシフィック',
  'market.structure.afcEast': 'AFC東地区',
  'market.structure.afcNorth': 'AFC北地区',
  'market.structure.afcSouth': 'AFC南地区',
  'market.structure.afcWest': 'AFC西地区',
  'market.structure.nfcEast': 'NFC東地区',
  'market.structure.nfcNorth': 'NFC北地区',
  'market.structure.nfcSouth': 'NFC南地区',
  'market.structure.nfcWest': 'NFC西地区',
  'market.playoffPicture.divisionLeaders': '地区首位',
  'market.playoffPicture.wildCard': 'ワイルドカード',
  'market.playoffPicture.outside': 'プレーオフ圏外',
  'market.playoffPicture.cutLine': 'ワイルドカード進出ライン',
  'market.playoffPicture.cutLineAccessibilityLabel': 'ワイルドカード進出の境界線',
  'market.playoffPicture.unavailable': 'プレーオフ争いの情報は一時的に利用できません',
  'market.playoffPicture.updatedAt': '順位表の更新：{timestamp}',
  'market.playoffPicture.delayed': '順位表データが遅れている可能性があります。',
  'market.playoffPicture.metric.pctAccessibilityLabel': '勝率',
  'market.playoffPicture.metric.ptsAccessibilityLabel': '順位ポイント',
  'market.playoffPicture.metric.wcgbAccessibilityLabel': 'ワイルドカードまでのゲーム差',
  'market.playoffPicture.metric.seedAccessibilityLabel': 'プレーオフシード',
  'market.search.placeholder': 'チームを検索',
  'market.search.clear': 'チーム検索をクリア',
  'market.quote.bid': '買い',
  'market.quote.ask': '売り',
  'market.participant.virtual': 'バーチャル',
  'market.quote.bidSize': '買 数',
  'market.quote.askSize': '売 数',
  'market.action.buy': '買い',
  'market.action.sell': '売り',
  'market.floor.short': '下限',
  'market.last.short': '直近',
  'market.orderBook.loading': '板を読み込み中…',
  'market.orderBook.empty': '板なし',
  'market.orderBook.unavailable': '板を表示できません',
  'market.orderBook.expand': '板を展開',
  'market.orderBook.collapse': '板を折りたたむ',
  'market.orderBook.notAuthenticated': '未ログイン',
  'market.bidAskTip.message':
    '注文するには、対象チームの買いまたは売りをタップします。',
  'market.bidAskTip.action': '了解',
  'market.error.loadFailed':
    'マーケットを読み込めません。API接続を確認してもう一度お試しください。',
  'market.empty.noTeams': 'チームがありません',
  'market.empty.worldCupHint':
    '別のステータス、グループ、検索語を試してください。',
  'market.empty.defaultHint':
    'シーズン開始後にマーケットデータが表示されます。',
  'market.error.retry': '再試行',
  'market.worldCup.searchPlaceholder': '検索',
  'market.worldCup.clearSearch': 'チーム検索をクリア',
  'market.worldCup.groups': 'グループ',
  'market.worldCup.groupAll': 'すべて',
  'market.worldCup.status.all': 'すべて',
  'market.worldCup.status.live': '勝ち残り',
  'market.worldCup.status.out': '敗退',
  'market.worldCup.status.allAccessibility': '全サッカーチームを表示',
  'market.worldCup.status.liveAccessibility': '勝ち残りチームを表示',
  'market.worldCup.status.outAccessibility': '敗退チームを表示',
  'market.guest.context': 'すべての試合がマーケットを動かします。',
  'market.guest.emphasis': '安く買って、熱く応援。',
  'market.guest.body':
    '買い気配と売り気配で取引フロアが灯る — いち早く動こう。',
  'market.guest.invite': '招待コードを入力',
  'guest.landing.sports.baseball': '野球',
  'guest.landing.sports.hockey': 'アイスホッケー',
  'guest.landing.sports.football': 'アメリカンフットボール',
  'guest.landing.background.label': 'スポーツの背景',
  'guest.landing.sports.title': '開催中・今後の予定',
  'guest.landing.sports.now': '開催中',
  'guest.landing.sports.playoffs': 'プレーオフ',
  'guest.landing.sports.regularSeason': 'レギュラーシーズン',
  'guest.landing.sports.playoffsStart': 'プレーオフ開幕：{date}',
  'guest.landing.sports.wildCardStart': 'ワイルドカード・ウィークエンド開幕：{date}',
  'guest.landing.sports.planned': '予定',
  'guest.landing.sports.schedule': '次のシーズンはリーグ公式日程をご確認ください。',
  'guest.landing.sports.helper': '今すぐプールを作成。シーズンからプレーオフまでトレードを楽しもう。',
  'guest.landing.sports.leagueLink': '{league}の公式サイトを見る（新しいタブで開きます）',
  'guest.landing.screenshots.market.imageAlt': 'SportEx100モバイルアプリ画面：野球チームのマーケット',
  'guest.landing.screenshots.orderTicket.imageAlt': 'SportEx100モバイルアプリ画面：野球の注文画面',
  'guest.landing.screenshots.sportfolio.imageAlt': 'SportEx100モバイルアプリ画面：野球のポートフォリオ',
  'guest.landing.screenshots.standings.imageAlt': 'SportEx100モバイルアプリ画面：野球プールの順位表',
  'guest.landing.screenshots.coach.imageAlt': 'SportEx100モバイルアプリ画面：野球のプールコーチ',
  'guest.landing.web.hero.context': '世界中がプレー中。',
  'guest.landing.web.hero.body': '自分だけの自由市場を立ち上げ、仲間を集め、頂点へ。順位表は嘘をつかない — 勝者は昇り、言い訳は落ちる。',
  'guest.landing.web.market.body': '買い気配と売り気配で取引フロアが灯る — いち早く動こう。',
  'guest.landing.web.picks.body': 'トーナメント表を予測し、直感に賭け、価格が間違っていれば柔軟に。何よりも大胆にプレー。',
  'guest.landing.web.social.emphasis': 'SportEx100ライブチャット。',
  'guest.landing.web.social.body': '各プールにライブチャット完備 — 勝負を宣言し、価格を出し、反対側を取れるものなら取ってみろと挑もう。熱い予想はすべて、取引の一歩手前。',
  'guest.landing.web.standings.body': '順位は試合や取引のたびに動く。情けも容赦もなし — 参加者全員にプレッシャーをかけろ。',
  'guest.landing.web.track.emphasis': '自分のポジションを知れ。',
  'guest.landing.web.track.body': '注文、取引、ポイント、チーム価値をひとつの場所で。何を所持し、何が熱く、次の大勝負がどこに潜むかが見える。',
  'guest.landing.web.footer.title': '2分でマーケットを開設。無料。',
  'guest.landing.web.footer.body': '友だち招待は大会開始前、後いつでもOK。チームを取引し、グループで一番の切れ者だと証明しよう。',
  'guest.landing.web.cta.joinWithCode': 'コードで入る',
  'guest.landing.web.join.helper': 'コードを入力したあと、サインインまたはアカウント作成をしてください。コードは保持されます。',
  'guest.landing.web.footer.chip.noMoney': '現金不要',
  'guest.landing.web.footer.chip.anytime': 'いつでも参加 — 大会の途中でも',
  'guest.landing.hero.context': '世界中がプレー中。',
  'guest.landing.hero.headline': 'マーケットを支配！',
  'guest.landing.hero.body':
    '自分だけの自由市場を立ち上げ、仲間を集め、頂点へ。順位表は嘘をつかない — 勝者は昇り、言い訳は落ちる。',
  'guest.landing.cta.createPool': 'プールマーケットを開始',
  'guest.landing.cta.joinWithCode': 'コードで入る',
  'guest.landing.cta.hasAccount': 'すでにアカウントをお持ちですか？',
  'guest.landing.cta.orDownload': 'またはアプリをダウンロード',
  'guest.landing.join.poolCodeLabel': 'プールコード',
  'guest.landing.join.poolCodePlaceholder': 'コードを入力',
  'guest.landing.join.continue': '続行',
  'guest.landing.join.helper':
    'コードを入力したあと、サインインまたはアカウント作成をしてください。コードは保持されます。',
  'guest.landing.join.error.required':
    '続行するにはプールコードを入力してください。',
  'guest.landing.nav.create': '作成',
  'guest.landing.nav.market': 'マーケット',
  'guest.landing.nav.picks': 'マイ予想',
  'guest.landing.nav.social': 'ソーシャル',
  'guest.landing.social.context': 'チャットがマーケットセンターだ。',
  'guest.landing.social.emphasis': 'SportEx100ライブチャット。',
  'guest.landing.social.body':
    '各プールにライブチャット完備 — 勝負を宣言し、価格を出し、反対側を取れるものなら取ってみろと挑もう。熱い予想はすべて、取引の一歩手前。',
  'guest.landing.social.imageAlt':
    'SportEx100モバイルアプリのプールチャット画面',
  'guest.landing.nav.standings': '順位表',
  'guest.landing.standings.context': '君のトレードが流れを変える。',
  'guest.landing.standings.emphasis': '王座を奪え。',
  'guest.landing.standings.body':
    '順位は試合や取引のたびに動く。情けも容赦もなし — 参加者全員にプレッシャーをかけろ。',
  'guest.landing.standings.imageAlt': 'SportEx100モバイルアプリの順位表画面',
  'guest.landing.nav.track': 'フォロー',
  'guest.landing.contract.imageAlt': 'SportEx100モバイルアプリのチーム契約画面',
  'guest.landing.orderForm.imageAlt':
    'SportEx100モバイルアプリの注文フォーム画面',
  'guest.landing.market.imageAlt':
    'SportEx100モバイルアプリのサッカーマーケット画面',
  'guest.landing.picks.imageAlt': 'SportEx100モバイルアプリの予想画面',
  'guest.landing.track.context': 'すべての動きを、ひとつの画面で。',
  'guest.landing.track.emphasis': '自分のポジションを知れ。',
  'guest.landing.track.body':
    '注文、取引、ポイント、チーム価値をひとつの場所で。何を所持し、何が熱く、次の大勝負がどこに潜むかが見える。',
  'guest.landing.track.imageAlt': 'SportEx100モバイルアプリのマイフォリオ画面',
  'guest.landing.footer.title': '2分でマーケットを開設。無料。',
  'guest.landing.footer.body':
    '友だち招待は大会開始前、後いつでもOK。チームを取引し、グループで一番の切れ者だと証明しよう。',
  'guest.landing.footer.kicker':
    'あなたの大会。あなたのルール。あなたのマーケット。',
  'guest.landing.footer.chip.free': '無料でプレイ',
  'guest.landing.footer.chip.noMoney': '現金不要',
  'guest.landing.footer.chip.anytime': 'いつでも参加 — 大会の途中でも',
  'order.title.place': '注文',
  'order.title.update': '注文変更',
  'order.form.side': '売買',
  'order.form.qty': '数量',
  'order.form.price': '価格',
  'order.action.buy': '買い',
  'order.action.sell': '売り',
  'order.preview.market': '市場',
  'order.preview.position': 'ポジション',
  'order.preview.myPickValue': '予想値: {value}',
  'order.preview.myPickValueLoading': '予想値: ...',
  'order.preview.setupSimulation': '予想でチームの理論値を設定してください。',
  'order.validation.pricePositive': '価格は０以上。',
  'order.validation.minPrice': '最小価格: {price}',
  'order.validation.maxPrice': '最大価格: {price}',
  'order.success.filled': '注文成立！{fills}{tradeLabel}確定。',
  'order.success.tradeOne': '件',
  'order.success.tradeMany': '件',
  'order.success.updated': '注文を変更！',
  'order.success.placed': '注文しました！',
  'order.error.title': '注文失敗',
  'order.error.updateFailed': '注文を変更できませんでした',
  'order.error.submitFailed': '注文を送信できませんでした',
  'order.action.cancel': 'キャンセル',
  'order.action.submit': '注文する',
  'order.action.submitting': '送信中',
  'order.action.update': '変更する',
  'order.action.updating': '変更中',
  'order.alert.ok': 'OK',
  'picks.header.title': 'プールコーチ',
  'picks.noPool.message': 'Coachを開くにはプールを選択してください。',
  'picks.hint.seriesMessage':
    'チームをタップして勝者とシリーズスコアを予想します。勝敗は予想シリーズ成績、点はチームの予想価値です。',
  'picks.hint.singleGameMessage':
    'チームをタップして勝者を予想。点はチームの予想価値です。',
  'picks.hint.action': '了解',
  'picks.actions.pointSystem': '点数表',
  'picks.actions.reset': 'リセット',
  'picks.actions.tradeTeam': '{team}を取引',
  'picks.guide.pointSystem': '点数表',
  'picks.guide.bracketBuilder': 'ポイントシミュレーター',
  'picks.scenario.title': 'シナリオビルダー',
  'picks.scenario.subtitle': 'チームをタップして予想',
  'picks.loading.bracket': 'トーナメント表を読み込み中...',
  'picks.loading.saved': '保存済みの予想を読み込み中...',
  'picks.error.loadFailed':
    'トーナメント表を読み込めません: {error}。ページを再読み込みしてください。',
  'picks.empty.bracketPending': '組み合わせ待ち',
  'picks.empty.noBracket': '組み合わせなし',
  'picks.empty.worldCupPending':
    'グループステージ突破確定後、ラウンド32の組み合わせが出てサッカー予想が開きます。',
  'picks.empty.noBracketBody': 'プレーオフ開始後に組み合わせが表示されます。',
  'picks.status.groupStage': 'グループ',
  'picks.status.pickNow': '予想',
  'picks.status.active': '進行中',
  'picks.status.complete': '完了',
  'picks.status.pending': '予定',
  'picks.column.series': '勝敗',
  'picks.column.points': 'ポイント',
  'picks.pending.opponent': '前ラウンドの勝者を選ぶと相手が決まります。',
  'picks.pending.matchup': '前ラウンドの勝者を選んでください。',
  'picks.pending.scoreHint': '勝者をもう一度タップしてスコア調整',
  'picks.trade.title': '{teamCode} を取引',
  'picks.trade.value': '予想値 {value}',
  'picks.trade.cancel': 'キャンセル',
  'picks.trade.buy': '予想値で買い',
  'picks.trade.sell': '予想値で売り',
  'picks.alert.pendingTitle': '承認待ちは閲覧のみ',
  'picks.alert.pendingMessage': '管理者承認後に取引できます。',
  'picks.alert.resetTitle': '予想をリセット',
  'picks.alert.resetMessage': 'この組み合わせの予想をすべてリセットしますか？',
  'picks.alert.resetConfirm': 'リセット',
  'picks.alert.lockedTitle': '{round} はロック中',
  'picks.alert.lockedMessage':
    'あと少し · {requiredRound} の予想を終えると次のラウンドが開きます。',
  'picks.alert.ok': 'OK',
  'picks.alert.previousRound': '前ラウンド',
  'picks.alert.groupLimitTitle': '進出は最大3チーム',
  'picks.alert.groupLimitMessage':
    '各グループでは上位2チームに加えて、3位チームを最大1チーム選べます。',
  'picks.alert.qualifierLimitTitle': '進出枠上限',
  'picks.alert.qualifierLimitMessage':
    'サッカー進出チームは合計{count}チームです。',
  'picks.alert.bestThirdLimitTitle': '3位通過上限',
  'picks.alert.bestThirdLimitMessage': '3位通過は合計{count}チームまでです。',
  'picks.worldCup.qualifierPicks': '進出予想',
  'picks.worldCup.groupTitle': 'グループ{group}',
  'picks.worldCup.seedGroupStage': 'シード{seed} · グループ',
  'picks.worldCup.topTwo': '上位2',
  'picks.worldCup.bestThird': '3位通過',
  'picks.worldCup.groupRankFirst': '上位1',
  'picks.worldCup.groupRankSecond': '上位2',
  'picks.worldCup.groupRankThird': '3位通過',
  'picks.worldCup.topTwoProgress': '上位2 {count}/{total}',
  'picks.worldCup.bestThirdProgress': '3位通過 {count}/{total}',
  'picks.worldCup.bestThirdOrder': '3位通過順位',
  'picks.worldCup.bestThirdMoveUp': '{team}を上へ',
  'picks.worldCup.bestThirdMoveDown': '{team}を下へ',
  'picks.round.groupStage': 'グループステージ',
  'picks.round.first': '1回戦',
  'picks.round.second': '2回戦',
  'picks.round.roundOf16': 'ベスト16',
  'picks.round.quarterFinals': '準々決勝',
  'picks.round.semiFinals': '準決勝',
  'picks.round.final': '決勝',
  'picks.round.finals': '決勝',
  'picks.round.round': 'ラウンド{sequence}',
  'picks.round.lockedAccessibility': '{round} はロック中',
  'picks.competition.generic': '大会',
  'picks.competition.hockey': 'SportEx100 ホッケー',
  'picks.competition.football': 'フットボール',
  'picks.competition.soccer': 'SportEx100 サッカー',
  'picks.series.singleGame': '1試合',
  'picks.series.firstTo': '{wins}勝先取',
  'picks.series.wins': '{format}（{wins}勝）',
  'picks.pointSystem.title': '点数表',
  'picks.pointSystem.close': '点数表を閉じる',
  'picks.pointSystem.gameWin': '試合勝',
  'picks.pointSystem.roundWin': '突破',
  'picks.pointSystem.maxRound': '最大点',
  'picks.pointSystem.champion': '優勝チームは必ず',
  'picks.pointSystem.settlement': '精算',
  'picks.pointSystem.settlementValue': '精算値',
  'picks.pointSystem.winPoints': '勝利点',
  'picks.pointSystem.perGameWin': '1勝ごと',
  'picks.pointSystem.teamPoints': 'チーム点',
  'picks.pointSystem.stage': 'ラウンド',
  'picks.pointSystem.result': '結果',
  'picks.pointSystem.earnAdds': '獲得 +',
  'picks.pointSystem.settlesAt': '精算値',
  'picks.pointSystem.noValue': '—',
  'picks.pointSystem.value': '値',
  'picks.pointSystem.points': 'ポイント',
  'picks.pointSystem.qualification': 'プレーオフ進出',
  'picks.pointSystem.summary.soccer': '精算結果',
  'picks.pointSystem.summary.standard': 'ラウンド別の配点',
  'picks.pointSystem.soccer.winner': '優勝',
  'picks.pointSystem.soccer.runnerUp': '準優勝',
  'picks.pointSystem.soccer.thirdPlace': '3位',
  'picks.pointSystem.soccer.worldCupChampion': '優勝',
  'picks.pointSystem.soccer.perfectPath': '優勝までの道',
  'picks.pointSystem.soccer.runningTotal': '合計 {points}',
  'picks.pointSystem.winRound': '勝利 {round}',
  'picks.pointSystem.soccer.winRound': '勝利 {round}',
  'picks.pointSystem.soccer.exitValue': '敗退時の精算値',
  'picks.pointSystem.soccer.exitValueBody':
    'チームはすでに獲得したポイントで精算されます。',
  'picks.pointSystem.soccer.eliminatedGroupStage': 'グループステージ敗退',
  'picks.pointSystem.soccer.advanceKnockout': '決勝トーナメント進出',
  'picks.pointSystem.soccer.winRound32': 'ラウンド32勝利',
  'picks.pointSystem.soccer.winRound16': 'ラウンド16勝利',
  'picks.pointSystem.soccer.winQuarterFinal': '準々決勝勝利',
  'picks.pointSystem.soccer.winSemiFinal': '準決勝勝利',
  'picks.pointSystem.soccer.winThirdPlace': '3位決定戦勝利',
  'picks.pointSystem.soccer.eliminatedSemiFinal': '準決勝敗退',
  'picks.pointSystem.soccer.eliminatedQuarterFinal': '準々決勝敗退',
  'picks.pointSystem.soccer.eliminatedRound16': 'ラウンド16敗退',
  'picks.pointSystem.soccer.eliminatedRound32': 'ラウンド32敗退',
  'picks.pointSystem.soccer.missedKnockout': '未進出',
  'picks.pointSystem.soccer.eliminatedHeader': '敗退',
  'picks.pointSystem.imageAlt': '常に100',
  'picks.guest.context': '自分の予測を組み立てよう。',
  'picks.guest.emphasis': '直感を信じて。',
  'picks.guest.body':
    'トーナメント表を予測し、直感に賭け、価格が間違っていれば柔軟に。何よりも大胆にプレー。',
  'picks.guest.invite': '招待コードを入力',
  'standings.header.title': '順位',
  'standings.noPool.message': '順位を見るにはプールを選択してください。',
  'standings.freshness.updated': '{time} 更新',
  'standings.podium.title': 'トップ3',
  'standings.podium.subtitle': '殿堂',
  'standings.table.rank': '順位',
  'standings.table.trader': 'トレーダー',
  'standings.table.trades': '取引',
  'standings.table.pnl': 'P&L',
  'standings.virtual.title': 'バーチャルトレーダー',
  'standings.virtual.subtitle': '自動流動性 · 人間の順位から除外',
  'standings.virtual.active': '稼働中',
  'standings.virtual.paused': '一時停止',
  'standings.empty.title': '順位なし',
  'standings.empty.body': '取引が始まると順位が表示されます。',
  'standings.you': 'あなた',
  'standings.deletedTrader': '削除済',
  'standings.trade.one': '取引',
  'standings.trade.many': '取引',
  'standings.recentActivity.title': '最近の動き',
  'standings.recentActivity.loading': '動きを読み込み中...',
  'standings.recentActivity.error': '動きのフィードを読み込めません。',
  'standings.guest.context': '取引がどう展開するかは君次第。',
  'standings.guest.emphasis': '首位を狙おう。',
  'standings.guest.body':
    'スコアと順位は、試合や動きのたびに更新されます。ロングとショートを上手く組み合わせポイントを増やし上位を目指そう。',
  'standings.guest.invite': '招待コードを入力',
  'portfolio.guest.context': 'ステータス',
  'portfolio.guest.emphasis': 'スコアをチェック。',
  'portfolio.guest.body': '予想、動き、点数、進み具合確認はここで。',
  'portfolio.guest.invite': '招待コードを入力',
  'portfolio.noPool.message':
    'マイフォリオを見るにはプールを選択してください。',
  'portfolio.header.title': 'マイフォリオ',
  'portfolio.filters.all': 'すべて',
  'portfolio.filters.positions': 'ポジション',
  'portfolio.filters.orders': '注文',
  'portfolio.filters.history': '履歴',
  'portfolio.filters.buy': '買い',
  'portfolio.filters.sell': '売り',
  'portfolio.filters.settle': '決済',
  'portfolio.filters.long': 'ロング',
  'portfolio.filters.short': 'ショート',
  'portfolio.sections.positions': 'ポジション',
  'portfolio.sections.openOrders': '未約定注文',
  'portfolio.sections.history': '履歴',
  'portfolio.empty.positions': 'ポジションなし',
  'portfolio.empty.openOrders': '未約定注文なし',
  'portfolio.empty.history': '取引はまだありません',
  'portfolio.noPool': 'マイフォリオを見るにはプールを選択してください。',
  'portfolio.summary.totalPnl': '損益合計',
  'portfolio.summary.rank': '順位',
  'portfolio.summary.settled': '決済済',
  'portfolio.summary.trading': '取引',
  'portfolio.metric.qty': '数量',
  'portfolio.metric.entry': 'エントリー',
  'portfolio.metric.mark': '評価',
  'portfolio.metric.pnl': 'P&L',
  'portfolio.metric.date': '日付',
  'portfolio.metric.price': '価格',
  'portfolio.metric.floor': '下限',
  'portfolio.metric.total': '合計',
  'portfolio.metric.counterparty': '相手',
  'portfolio.position.flat': 'フラット',
  'portfolio.position.short': 'ショート',
  'portfolio.position.long': 'ロング',
  'portfolio.position.buyAvg': '買平均',
  'portfolio.position.sellAvg': '売平均',
  'portfolio.position.realized': '実現',
  'portfolio.position.unrealized': '未実現',
  'portfolio.position.tradeLog': '取引記録',
  'portfolio.settlementAudit.side': 'サイド',
  'portfolio.settlementAudit.entry': 'エントリー',
  'portfolio.settlementAudit.formula': '計算式',
  'portfolio.settlementAudit.totalPnl': '合計P&L',
  'portfolio.settlementAudit.closedBefore': '早期クローズ',
  'portfolio.settlementAudit.settledAtFloor': '下限決済',
  'portfolio.settlementAudit.tradeLog': '取引記録',
  'portfolio.settlementAudit.sideBuy': '買い',
  'portfolio.settlementAudit.sideSell': '売り',
  'portfolio.settlementAudit.sideLong': 'ロング',
  'portfolio.settlementAudit.sideShort': 'ショート',
  'portfolio.settlementAudit.floor': '下限',
  'portfolio.settlementAudit.basisMatched': 'マッチ済',
  'portfolio.settlementAudit.basisAvg': '平均基準',
  'portfolio.settlementAudit.netOpen': '純建玉',
  'portfolio.settlementAudit.fillCountOne': '{count} 約定',
  'portfolio.settlementAudit.fillCount': '{count} 約定',
  'portfolio.settlementAudit.buyCountOne': '{count} 買い',
  'portfolio.settlementAudit.buyCount': '{count} 買い',
  'portfolio.settlementAudit.sellCountOne': '{count} 売り',
  'portfolio.settlementAudit.sellCount': '{count} 売り',
  'portfolio.trade.settle': '決済',
  'portfolio.trade.deletedTrader': '削除済',
  'portfolio.order.actionsAccessibility': '{team} の注文操作',
  'portfolio.order.promote': '共有',
  'portfolio.order.edit': '編集',
  'portfolio.order.cancel': '取消',
  'portfolio.order.market': '市場',
  'portfolio.order.hint': '注文をタップして共有、編集、取消。',
  'portfolio.order.hintAction': '了解',
  'portfolio.order.cancelTitle': '注文を取消',
  'portfolio.order.cancelPrompt': 'この注文を取消しますか？',
  'portfolio.order.cancelNo': 'いいえ',
  'portfolio.order.cancelYes': 'はい',
  'portfolio.order.cancelErrorTitle': 'エラー',
  'portfolio.order.cancelErrorMessage': '注文を取消できませんでした',
  'portfolio.order.poolNotReadyTitle': 'プール準備中',
  'portfolio.order.poolNotReadyMessage':
    'この注文の市場データをまだ読み込めません。',
  'portfolio.order.teamNotFoundTitle': 'チームが見つかりません。',
  'portfolio.order.teamNotFoundMessage': 'チームデータを読み込めません。',
  'pulse.title': 'Pulse',
  'pulse.lane.pool': 'プール',
  'pulse.lane.game': '試合',
  'pulse.lane.you': 'あなた',
  'pulse.story.trade': '取引',
  'pulse.story.wall': '壁',
  'pulse.story.airball': '空振り',
  'pulse.story.rankMove': '順位変動',
  'pulse.story.crowdedSide': '集中',
  'pulse.story.contrarianPosition': '逆張り',
  'pulse.story.settlementSwing': '決済',
  'pulse.story.gameResult': '結果',
  'pulse.story.eliminationQualification': '進出',
  'pulse.story.floorChange': '下限',
  'pulse.story.personalImpact': 'あなた',
  'pulse.impact.pnl': '損益',
  'pulse.impact.rank': '順位',
  'pulse.impact.exposure': '建玉',
  'pulse.impact.marketActivity': '市場',
  'pulse.impact.settlement': '決済',
  'pulse.impact.gameResult': '試合',
  'pulse.impact.socialPrompt': 'チャット',
  'pulse.empty.title': 'プールは静かです',
  'pulse.empty.body': '現在のPulse期間に大きな動きはありません。',
  'pulse.empty.action': '市場を開く',
  'pulse.action.share': '共有',
  'pulse.action.open': '開く',
  'pulse.action.viewAll': 'すべて表示',
  'pulse.action.showLess': '閉じる',
  'pulse.time.justNow': 'たった今',
  'pulse.time.minutesAgo': '{count}分前',
  'pulse.time.hoursAgo': '{count}時間前',
  'pulse.time.daysAgo': '{count}日前',
  'pulse.time.lastWindow': '直近 {duration}',
  'pulse.tradeBehavior.pill.stacking': '買い集め',
  'pulse.tradeBehavior.pill.dumping': '売り抜け',
  'pulse.tradeBehavior.pill.heatingUp': '加熱中',
  'pulse.tradeBehavior.pill.whale': '大口',
  'pulse.tradeBehavior.side.buy': '買い',
  'pulse.tradeBehavior.side.sell': '売り',
  'pulse.tradeBehavior.context.pattern':
    '{side} {quantity} · 約定 {fills} · 平均 {avg}',
  'pulse.tradeBehavior.headline.stacking': '{trader} が {team} を買い集め',
  'pulse.tradeBehavior.headline.dumping': '{trader} が {team} を売り抜け',
  'pulse.tradeBehavior.headline.whale':
    '{buyer} が {seller} から {team} を購入',
  'pulse.orderPressure.pill.wall': '壁',
  'pulse.orderPressure.pill.airball': '空振り',
  'pulse.orderPressure.side.buyHeadline': '買い',
  'pulse.orderPressure.side.sellHeadline': '売り',
  'pulse.orderPressure.headline.wall': '{team} に {side} の壁',
  'pulse.orderPressure.headline.wallSell': '{team} に {side} の壁',
  'pulse.orderPressure.headline.airball': '{team} に {side} の空振り',
  'pulse.orderPressure.context.full':
    '{side} {price} × {quantity} · 価値 {value} · マーク {mark}',
  'pulse.teamHeat.headline': '{team} が加熱中。',
  'pulse.teamHeat.subline': '{traders}人 · {qty}枚動く · 平均 {avg}',
  'pulse.teamHeat.metric.traders': '{count}人',
  'pulse.teamHeat.metric.qtyMoved': '{qty}枚動く',
  'pulse.teamHeat.metric.avg': '平均 {avg}',
  'pulse.position.asOf': '{time} 時点',
  'pulse.position.pill.crowded': '集中',
  'pulse.position.pill.contrarian': '逆張り',
  'pulse.position.side.long': 'ロング',
  'pulse.position.side.short': 'ショート',
  'pulse.position.intensity.allIn': '全振り',
  'pulse.position.intensity.lopsided': '偏り大',
  'pulse.position.intensity.heavily': '強め',
  'pulse.position.intensity.leaning': '傾き',
  'pulse.position.context.value': '価値 {value}{qty}',
  'pulse.position.context.qtySuffix': ' · 数量 {qty}',
  'pulse.position.context.liveQty': '保有中 · 数量 {qty}',
  'pulse.position.context.live': '保有中',
  'pulse.settlement.pill': '決済',
  'pulse.settlement.best': '最高',
  'pulse.settlement.worst': '最低',
  'pulse.settlement.vs': '対',
  'pulse.settlement.traders': '{count}人',
  'pulse.settlement.points': '{points}点',
  'pulse.fallback.headline': 'プールの動き',
  'pulse.fallback.impact': 'タップしてプール状況を見る。',
  'pulse.accessibility.story': '{lane}. {headline}. {impact}',
  'portfolio.history.loadMore': 'さらに表示（残り{remaining}）',
};

export const es: Partial<Record<TranslationKey, string>> = {
  'common.back': 'Atrás',
  'common.cancel': 'Cancelar',
  'common.continue': 'Continuar',
  'common.edit': 'Editar',
  'common.retry': 'Reintentar',
  'common.save': 'Guardar',
  'common.saved': 'Guardado',
  'common.refresh': 'Actualizar',
  'common.terms': 'Términos',
  'common.closeProfileSettings': 'Cerrar configuración del perfil',
  'common.openProfileSettings': 'Abrir configuración del perfil',
  'common.editProfileAvatar': 'Editar avatar del perfil',
  'common.openMenu': 'Abrir menú',
  'common.closeMenu': 'Cerrar menú',
  'common.navigation': 'Navegación',
  'tabs.home': 'Inicio',
  'tabs.market': 'Mercado',
  'tabs.portfolio': 'Sportfolio',
  'tabs.standings': 'Clasificación',
  'tabs.picks': 'Coach',
  'auth.redirectingToSignIn': 'Redirigiendo al inicio de sesión...',
  'auth.finishingSignIn': 'Finalizando inicio de sesión...',
  'auth.tradingAccess': 'Acceso al mercado',
  'auth.welcome': 'Bienvenido a Sport Exchange 100',
  'auth.appleSubtitle': 'Inicia sesión con Apple en un toque',
  'auth.googleSubtitle': 'Inicia sesión con Google en un toque',
  'auth.continueWithApple': 'Continuar con Apple',
  'auth.continueWithGoogle': 'Continuar con Google',
  'auth.signIn': 'Iniciar sesión',
  'auth.signingIn': 'Iniciando sesión...',
  'auth.signInInProgress':
    'Ya hay un inicio de sesión en curso. Espera un momento.',
  'auth.providerNeedsVerification':
    'El inicio de sesión con {provider} requiere verificación adicional.',
  'auth.providerFailed': 'No se pudo iniciar sesión con {provider}',
  'auth.providerContextFailed': 'No se pudo iniciar sesión desde {context}.',
  'auth.errors.invalidInviteCode':
    'Ingresa un código de pool válido (4-20 caracteres, letras/números).',
  'auth.errors.invalidInviteCodeFormat':
    'El formato del código de invitación no es válido. Usa 4-20 letras o números.',
  'guest.auth.alreadyHaveAccountSignIn': '¿Ya tienes una cuenta? Inicia sesión',
  'guest.auth.appStore': 'App Store',
  'guest.auth.playStore': 'Google Play',
  'guest.appPrompt.dismiss': 'Continuar en la web',
  'settings.preferences': 'Preferencias',
  'settings.showTutorial.title': 'Mostrar tutorial',
  'settings.showTutorial.subtitle': 'Aprende los fundamentos',
  'settings.pulsePreview.title': 'Vista previa de Pulse',
  'settings.pulsePreview.subtitle':
    'Mostrar el feed experimental Pool Pulse en Inicio',
  'settings.showTips.title': 'Restablecer consejos',
  'settings.showTips.subtitle': 'Mostrar consejos de bienvenida otra vez',
  'settings.showTips.action': 'Restablecer',
  'settings.showTips.done': 'Listo',
  'settings.support': 'Soporte',
  'settings.privacyPolicy': 'Política de privacidad',
  'settings.contactSupport': 'Contactar soporte',
  'settings.account': 'Cuenta',
  'settings.logOut': 'Cerrar sesión',
  'settings.deleteAccount.title': 'Eliminar cuenta',
  'settings.deleteAccount.subtitle':
    'Elimina permanentemente tu cuenta de Sport Exchange 100',
  'settings.deleteAccount.promptTitle': '¿Eliminar cuenta?',
  'settings.deleteAccount.promptBody':
    'Esto elimina tu cuenta de Sport Exchange 100 y cierra tu sesión. Algunos registros de trading anonimizados pueden conservarse para la contabilidad del pool. Esta acción no se puede deshacer.',
  'settings.deleteAccount.finalTitle': 'Confirmación final',
  'settings.deleteAccount.finalBody':
    '¿Estás totalmente seguro? Esto eliminará tu cuenta de Sport Exchange 100 y no se puede deshacer.',
  'settings.deleteAccount.deleteForever': 'Eliminar para siempre',
  'settings.language.title': 'Idioma',
  'settings.language.subtitle': 'Elige cómo SportEx100 muestra el texto',
  'settings.language.current': 'Actual: {language}',
  'settings.language.device': 'Predeterminado del dispositivo',
  'settings.language.deviceDetail':
    'Usa el idioma de tu dispositivo cuando SportEx100 lo admite.',
  'settings.language.englishDetail': 'Usar inglés en SportEx100.',
  'settings.language.frenchDetail': 'Usar francés en SportEx100.',
  'settings.language.japaneseDetail':
    'Experimento de la pestaña Mercado en japonés.',
  'settings.language.saved': 'Idioma actualizado',
  'settings.errors.deleteAccount': 'No se pudo eliminar tu cuenta.',
  'settings.errors.tutorialPreference':
    'No se pudo actualizar la preferencia del tutorial. Inténtalo de nuevo.',
  'settings.errors.pulsePreview':
    'No se pudo actualizar la vista previa de Pulse. Inténtalo de nuevo.',
  'settings.errors.resetTips':
    'No se pudieron restablecer los consejos. Inténtalo de nuevo.',
  'settings.errors.languagePreference':
    'No se pudo actualizar la preferencia de idioma. Inténtalo de nuevo.',
  'profile.cancelTraderEdit': 'Cancelar edición del código de trader',
  'profile.editTraderCode': 'Editar código de trader',
  'home.header.title': 'Inicio',
  'home.filters.current': 'Actuales',
  'home.filters.archived': 'Archivados',
  'home.actions.createPool': 'Crear pool',
  'home.empty.filteredTitle': 'Ningún pool coincide con este filtro',
  'home.empty.noPoolsTitle': 'Aún no hay pools',
  'home.empty.noPoolsBody':
    'Únete a un pool existente o crea el tuyo para empezar.',
  'home.welcome.context': 'Nuevo pool abierto',
  'home.welcome.emphasis': 'Torneo Mundial de Fútbol',
  'home.welcome.body':
    'Crea un pool nuevo para el Torneo Mundial de Fútbol 2026 e invita a tus amigos antes del inicio.',
  'home.welcome.createPool': 'Crear pool nuevo',
  'home.promo.baseball.context': 'El mercado de pretemporada ya está abierto',
  'home.promo.baseball.title': 'Béisbol de octubre',
  'home.promo.baseball.body':
    '30 equipos. Un campeón. Opera durante toda la temporada.',
  'home.promo.baseball.join': 'Unirse',
  'home.promo.baseball.create': 'Crear',
  'home.promo.baseball.minimize': 'Minimizar promoción del pool de béisbol',
  'home.promo.baseball.expand': 'Expandir promoción del pool de béisbol',
  'home.join.title': 'Únete al pool',
  'home.join.subtitle': 'Tu lugar te espera.',
  'home.join.picksUnlock': 'Haz pronósticos de inmediato',
  'home.join.accessPolicy':
    'El acceso al mercado depende de la configuración de aprobación del pool.',
  'home.join.immediately': 'de inmediato',
  'home.join.tradingUnlocks': 'Opera tras la aprobación del administrador',
  'home.join.afterApproval': 'tras la aprobación',
  'home.join.fieldLabel': 'Código del pool',
  'home.join.placeholder': 'Ingresa el código',
  'home.join.submit': 'Déjame entrar →',
  'home.pool.status.preseason': 'Pretemporada',
  'home.pool.status.active': 'Activo',
  'home.pool.status.completed': 'Completado',
  'home.pool.noOrders': 'Sin órdenes',
  'home.pool.oneOrder': '1 orden',
  'home.pool.manyOrders': '{count} órdenes',
  'home.pool.pendingApproval': 'Aprobación del administrador pendiente',
  'home.pool.stat.pnl': 'P&L',
  'home.pool.stat.ranking': 'Ranking',
  'home.pool.awaitingApproval': 'Esperando aprobación del administrador',
  'home.pool.leading': 'Liderando',
  'home.pool.rising': 'Subiendo',
  'home.pool.stable': 'Estable',
  'home.pool.youToday': 'Tú ↑{delta} hoy',
  'home.pool.traderToday': '{traderCode} ↑{delta} hoy',
  'home.pool.leader.empty': 'Sé el primero en operar',
  'home.feed.loading': 'Cargando...',
  'home.feed.error': 'No se pudieron cargar las actualizaciones ahora.',
  'home.tutorial.modalTitle': 'Cómo funciona SportEx100',
  'home.tutorial.stepLabel': 'Paso {step} de {total}',
  'home.tutorial.previousAccessibility': 'Panel anterior del tutorial',
  'home.tutorial.nextAccessibility': 'Siguiente panel del tutorial',
  'home.tutorial.step1.heading': 'Haz tus pronósticos',
  'home.tutorial.step1.body':
    'Los equipos ganan puntos según qué tan lejos avanzan en el torneo. Usa Pronósticos para estimar con cuántos puntos podría terminar cada equipo.',
  'home.tutorial.step2.heading': 'Los equipos liquidan entre 0 y 100',
  'home.tutorial.step2.body':
    'Los precios se mueven cuando los participantes actualizan cuánto creen que liquidará cada equipo. Al final, cada equipo liquida a su valor final de puntos de playoff. El campeón siempre liquida a 100.',
  'home.tutorial.step3.heading': 'Opera con el pool',
  'home.tutorial.step3.body':
    'Compra equipos que crees que están baratos y vende equipos que crees que están caros.',
  'home.tutorial.step4.heading': 'Comprar es apostar al alza',
  'home.tutorial.step4.body':
    'Ganas cuando un equipo opera o liquida por encima de tu precio de compra.',
  'home.tutorial.step4.example': 'Compras a 42 y liquida a 70: ganas 28.',
  'home.tutorial.step5.heading': 'Vender es apostar a la baja',
  'home.tutorial.step5.body':
    'Ganas cuando un equipo opera o liquida por debajo de tu precio de venta.',
  'home.tutorial.step5.example': 'Vendes a 62 y liquida a 35: ganas 27.',
  'home.tutorial.step6.heading': 'Todo queda dentro del pool',
  'home.tutorial.step6.body':
    'Cada punto que gana un participante lo pierde otro. Las operaciones solo mueven puntos entre personas del pool.',
  'market.noPool.message': 'Selecciona un pool para ver el mercado.',
  'market.pending.title': 'Aprobación pendiente',
  'market.pending.message':
    'Define tus pronósticos ahora; el trading se desbloquea cuando te confirmen.',
  'market.error.loadFailed':
    'No se pudo cargar el mercado. Revisa la conexión con la API e inténtalo de nuevo.',
  'market.empty.noTeams': 'No hay equipos disponibles.',
  'market.empty.defaultHint': 'Vuelve cuando el mercado abra.',
  'market.empty.worldCupHint':
    'Los equipos aparecerán cuando se cargue la fase de grupos.',
  'market.sections.champion': 'Campeón',
  'market.sections.activeTeams': 'Equipos activos',
  'market.sections.eliminatedTeams': 'Equipos eliminados',
  'market.sections.otherTeams': 'Otros equipos',
  'market.sections.conference': 'Conferencia · {conference}',
  'market.sections.division': 'División · {division}',
  'market.sections.teamCount': '{visible}/{total} equipos',
  'market.phase.preseason': 'Pretemporada',
  'market.phase.regularSeason': 'Temporada regular',
  'market.phase.playoffCutover': 'Transición a playoffs',
  'market.phase.playoffs': 'Playoffs',
  'market.phase.completed': 'Completada',
  'market.record.wins': '{value} victorias',
  'market.record.losses': '{value} derrotas',
  'market.record.overtimeLosses': '{value} derrotas en prórroga',
  'market.record.ties': '{value} empates',
  'market.tradingClosed.notQualified': 'Este equipo no se clasificó. El mercado está cerrado.',
  'market.tradingClosed.unavailable': 'El mercado de este equipo está cerrado.',
  'market.structure.americanLeague': 'Liga Americana',
  'market.structure.nationalLeague': 'Liga Nacional',
  'market.structure.easternConference': 'Conferencia Este',
  'market.structure.westernConference': 'Conferencia Oeste',
  'market.structure.afc': 'Conferencia Americana',
  'market.structure.nfc': 'Conferencia Nacional',
  'market.structure.americanLeagueEast': 'Este de la Liga Americana',
  'market.structure.americanLeagueCentral': 'Central de la Liga Americana',
  'market.structure.americanLeagueWest': 'Oeste de la Liga Americana',
  'market.structure.nationalLeagueEast': 'Este de la Liga Nacional',
  'market.structure.nationalLeagueCentral': 'Central de la Liga Nacional',
  'market.structure.nationalLeagueWest': 'Oeste de la Liga Nacional',
  'market.structure.atlanticDivision': 'Atlántica',
  'market.structure.metropolitanDivision': 'Metropolitana',
  'market.structure.centralDivision': 'Central',
  'market.structure.pacificDivision': 'Pacífica',
  'market.structure.afcEast': 'Este de la AFC',
  'market.structure.afcNorth': 'Norte de la AFC',
  'market.structure.afcSouth': 'Sur de la AFC',
  'market.structure.afcWest': 'Oeste de la AFC',
  'market.structure.nfcEast': 'Este de la NFC',
  'market.structure.nfcNorth': 'Norte de la NFC',
  'market.structure.nfcSouth': 'Sur de la NFC',
  'market.structure.nfcWest': 'Oeste de la NFC',
  'market.playoffPicture.divisionLeaders': 'Líderes de división',
  'market.playoffPicture.wildCard': 'Comodín',
  'market.playoffPicture.outside': 'Fuera de los puestos de playoffs',
  'market.playoffPicture.cutLine': 'Línea de corte del comodín',
  'market.playoffPicture.cutLineAccessibilityLabel': 'Línea de clasificación del comodín',
  'market.playoffPicture.unavailable': 'La clasificación para playoffs no está disponible temporalmente',
  'market.playoffPicture.updatedAt': 'Clasificación actualizada {timestamp}',
  'market.playoffPicture.delayed': 'Los datos de clasificación pueden estar retrasados.',
  'market.playoffPicture.metric.pctAccessibilityLabel': 'Porcentaje de victorias',
  'market.playoffPicture.metric.ptsAccessibilityLabel': 'Puntos en la clasificación',
  'market.playoffPicture.metric.wcgbAccessibilityLabel':
    'Juegos de diferencia para el comodín',
  'market.playoffPicture.metric.seedAccessibilityLabel': 'Cabeza de serie de playoffs',
  'market.search.placeholder': 'Buscar equipos',
  'market.search.clear': 'Borrar búsqueda de equipos',
  'market.sections.group': 'Grupo {group}',
  'market.filters.all': 'Todos',
  'market.filters.active': 'Activos',
  'market.filters.eliminated': 'Eliminados',
  'market.nhlFilter.conference': 'Conferencia',
  'market.structureFilter.conference': 'Conferencia',
  'market.nhlFilter.division': 'División',
  'market.structureFilter.division': 'División',
  'market.nhlFilter.all': 'Todas',
  'market.structureFilter.all': 'Todas',
  'market.nhlFilter.east': 'Este',
  'market.structureFilter.east': 'Este',
  'market.nhlFilter.west': 'Oeste',
  'market.structureFilter.west': 'Oeste',
  'market.nhlFilter.emptyHint': 'Prueba otra conferencia, división, estado o búsqueda.',
  'market.structureFilter.emptyHint': 'Prueba otra conferencia, división, estado o búsqueda.',
  'market.quote.bid': 'Compra',
  'market.quote.ask': 'Venta',
  'market.participant.virtual': 'Virtual',
  'market.floor.short': 'Piso',
  'market.last.short': 'Últ.',
  'market.orderBook.expand': 'Expandir libro de órdenes',
  'market.orderBook.collapse': 'Contraer libro de órdenes',
  'market.worldCup.searchPlaceholder': 'Buscar equipos',
  'market.worldCup.clearSearch': 'Borrar búsqueda',
  'market.worldCup.groups': 'Grupos',
  'market.worldCup.groupAll': 'Todos los grupos',
  'guest.landing.sports.baseball': 'Béisbol',
  'guest.landing.sports.hockey': 'Hockey sobre hielo',
  'guest.landing.sports.football': 'Fútbol americano',
  'guest.landing.background.label': 'Fondos deportivos',
  'guest.landing.sports.title': 'Ahora y próximamente',
  'guest.landing.sports.now': 'EN CURSO',
  'guest.landing.sports.playoffs': 'Playoffs',
  'guest.landing.sports.regularSeason': 'Temporada regular',
  'guest.landing.sports.playoffsStart': 'Los playoffs comienzan el {date}',
  'guest.landing.sports.wildCardStart': 'La ronda de comodines comienza el {date}',
  'guest.landing.sports.planned': 'Fecha prevista',
  'guest.landing.sports.schedule': 'Consulta el calendario de la liga para la próxima temporada.',
  'guest.landing.sports.helper': 'Crea tu pool ahora. Opera durante la temporada y los playoffs.',
  'guest.landing.sports.leagueLink': 'Visitar el sitio oficial de la {league} (se abre en otra pestaña)',
  'guest.landing.screenshots.market.imageAlt': 'Pantalla de la aplicación móvil SportEx100: mercado de equipos de béisbol',
  'guest.landing.screenshots.orderTicket.imageAlt': 'Pantalla de la aplicación móvil SportEx100: formulario de orden de béisbol',
  'guest.landing.screenshots.sportfolio.imageAlt': 'Pantalla de la aplicación móvil SportEx100: cartera de béisbol',
  'guest.landing.screenshots.standings.imageAlt': 'Pantalla de la aplicación móvil SportEx100: clasificación del pool de béisbol',
  'guest.landing.screenshots.coach.imageAlt': 'Pantalla de la aplicación móvil SportEx100: entrenador del pool de béisbol',
  'guest.landing.web.hero.context': 'TODO EL MUNDO ESTÁ EN JUEGO',
  'guest.landing.web.hero.body': 'Crea tu propio mercado gratuito, arma tu grupo y supera a todos con tus operaciones. La tabla no miente: los ganadores suben y las excusas se hunden.',
  'guest.landing.web.market.body': 'Mira cómo el piso se ilumina con compras y ventas, y actúa antes de que tus amigos muevan el precio.',
  'guest.landing.web.picks.body': 'Predice el bracket, respalda tu corazonada y ataca cuando el precio esté mal. Los audaces cobran; los tímidos se quedan atrás.',
  'guest.landing.web.social.emphasis': 'Chat en vivo de SportEx100.',
  'guest.landing.web.social.body': 'Cada pool tiene chat en vivo: lanza tu predicción, publica tu precio y reta a alguien a tomar el otro lado. Cada opinión fuerte puede convertirse en una operación.',
  'guest.landing.web.standings.body': 'Tu ranking se mueve con cada partido y cada operación. Sin regalos ni piedad: supera a quien tengas delante y avisa a toda la tabla.',
  'guest.landing.web.track.emphasis': 'Conoce tu valor.',
  'guest.landing.web.track.body': 'Órdenes, trades, puntos y valores de equipo, todo en un lugar. Mira qué tienes, qué se calienta y dónde se esconde tu próxima gran jugada.',
  'guest.landing.web.footer.title': 'Crea tu mercado en 2 minutos. Gratis.',
  'guest.landing.web.footer.body': 'Invita a tus amigos ahora o cuando el torneo ya esté en marcha. Entra cuando quieras, opera los cambios y demuestra quién es el más preciso del grupo.',
  'guest.landing.web.cta.joinWithCode': 'ENTRAR CON CÓDIGO',
  'guest.landing.web.join.helper': 'Inicia sesión o crea una cuenta después de ingresar tu código. Lo dejaremos listo.',
  'guest.landing.web.footer.chip.noMoney': 'Sin dinero real',
  'guest.landing.web.footer.chip.anytime': 'Únete cuando quieras, incluso a mitad del torneo',
  'guest.landing.hero.context': 'TODO EL MUNDO ESTÁ EN JUEGO',
  'guest.landing.hero.headline': 'Domina el mercado',
  'guest.landing.hero.body':
    'Crea tu propio mercado gratuito, arma tu grupo y supera a todos con tus operaciones. La tabla no miente: los ganadores suben y las excusas se hunden.',
  'guest.landing.cta.createPool': 'CREAR UN MERCADO DE POOL',
  'guest.landing.cta.joinWithCode': 'ENTRAR CON CÓDIGO',
  'guest.landing.cta.hasAccount': 'Ya tengo una cuenta',
  'guest.landing.cta.orDownload': 'O descarga la app',
  'guest.landing.join.poolCodeLabel': 'Código del pool',
  'guest.landing.join.poolCodePlaceholder': 'Ingresa el código',
  'guest.landing.join.continue': 'Continuar',
  'guest.landing.join.helper':
    'Inicia sesión o crea una cuenta después de ingresar tu código. Lo dejaremos listo.',
  'guest.landing.join.error.required':
    'Ingresa un código de pool para continuar.',
  'guest.landing.footer.title': 'Crea tu mercado en 2 minutos. Gratis.',
  'guest.landing.footer.body':
    'Invita a tus amigos ahora o cuando el torneo ya esté en marcha. Entra cuando quieras, opera los cambios y demuestra quién es el más preciso del grupo.',
  'guest.landing.footer.kicker': 'Tu torneo. Tus reglas. Tu mercado.',
  'guest.landing.footer.chip.free': 'Gratis para jugar',
  'guest.landing.footer.chip.noMoney': 'Sin dinero real',
  'guest.landing.footer.chip.anytime':
    'Únete cuando quieras, incluso a mitad del torneo',
  'guest.landing.orderForm.imageAlt':
    'Formulario de orden de SportEx100 en la app móvil',
  'picks.scenario.title': 'Constructor de escenarios',
  'picks.loading.bracket': 'Cargando bracket...',
  'picks.loading.saved': 'Guardado',
  'picks.error.loadFailed':
    'No se pudo cargar el bracket: {error}. Intenta actualizar la página.',
  'picks.actions.reset': 'Restablecer',
  'picks.actions.pointSystem': 'Sistema de puntos',
  'picks.actions.tradeTeam': 'Operar {team}',
  'picks.guide.pointSystem': 'Sistema de puntos',
  'picks.guide.bracketBuilder': 'Simulador de puntos',
  'picks.alert.lockedTitle': '{round} bloqueada',
  'picks.alert.lockedMessage':
    'Casi listo · Completa tus pronósticos de {requiredRound} para desbloquear la siguiente ronda.',
  'picks.alert.previousRound': 'Elige el ganador de la ronda anterior primero.',
  'picks.series.singleGame': 'Partido único',
  'picks.series.firstTo': 'Primero a {wins}',
  'picks.series.wins': '{format}: primero a {wins}',
  'picks.round.groupStage': 'Fase de grupos',
  'picks.worldCup.groupTitle': 'Grupo {group}',
  'picks.worldCup.topTwo': 'Dos primeros',
  'picks.worldCup.bestThird': 'Mejores terceros',
  'picks.worldCup.bestThirdOrder': 'Orden de mejores terceros',
  'picks.worldCup.bestThirdMoveUp': 'Subir {team}',
  'picks.worldCup.bestThirdMoveDown': 'Bajar {team}',
  'picks.worldCup.qualifierPicks': 'Pronósticos de clasificados',
  'picks.pointSystem.title': 'Sistema de puntos',
  'picks.pointSystem.imageAlt': 'Tabla del sistema de puntos',
  'picks.pointSystem.gameWin': 'Victoria',
  'picks.pointSystem.roundWin': 'Ronda ganada',
  'picks.pointSystem.maxRound': 'Ronda máxima',
  'picks.pointSystem.summary.standard': 'Resumen del sistema de puntos',
  'picks.pointSystem.summary.soccer': 'Resultados de liquidación',
  'standings.noPool.message': 'Selecciona un pool para ver la clasificación.',
  'standings.empty.title': 'Aún no hay clasificación',
  'standings.podium.title': 'Podio',
  'standings.podium.subtitle': 'Los tres primeros del pool',
  'standings.table.rank': 'Rango',
  'standings.table.trader': 'Trader',
  'standings.table.pnl': 'P&L',
  'standings.table.trades': 'Operaciones',
  'standings.virtual.title': 'Traders virtuales',
  'standings.virtual.subtitle': 'Liquidez automatizada · fuera de la clasificación humana',
  'standings.virtual.active': 'Activo',
  'standings.virtual.paused': 'En pausa',
  'standings.you': 'Tú',
  'standings.deletedTrader': 'Eliminado',
  'standings.trade.one': 'operación',
  'standings.trade.many': 'operaciones',
  'portfolio.noPool.message': 'Selecciona un pool para ver tu Sportfolio.',
  'portfolio.empty.history': 'Aún no hay operaciones',
  'portfolio.summary.trading': 'Trading',
  'portfolio.metric.qty': 'Cant.',
  'portfolio.metric.entry': 'Entrada',
  'portfolio.metric.mark': 'Marca',
  'portfolio.metric.pnl': 'P&L',
  'portfolio.metric.date': 'Fecha',
  'portfolio.metric.price': 'Precio',
  'portfolio.metric.floor': 'Piso',
  'portfolio.metric.total': 'Total',
  'portfolio.metric.counterparty': 'Contraparte',
  'portfolio.position.flat': 'Plano',
  'portfolio.position.short': 'Corto',
  'portfolio.position.long': 'Largo',
  'portfolio.position.buyAvg': 'Compra prom.',
  'portfolio.position.sellAvg': 'Venta prom.',
  'portfolio.position.realized': 'Realizado',
  'portfolio.position.unrealized': 'No realizado',
  'portfolio.position.tradeLog': 'Registro de operaciones',
  'portfolio.settlementAudit.side': 'Lado',
  'portfolio.settlementAudit.entry': 'Entrada',
  'portfolio.settlementAudit.formula': 'Fórmula',
  'portfolio.settlementAudit.totalPnl': 'P&L TOTAL',
  'portfolio.settlementAudit.closedBefore': 'CIERRE ANTICIPADO',
  'portfolio.settlementAudit.settledAtFloor': 'LIQUIDACIÓN AL PISO',
  'portfolio.settlementAudit.tradeLog': 'REGISTRO',
  'portfolio.settlementAudit.sideBuy': 'COMPRA',
  'portfolio.settlementAudit.sideSell': 'VENTA',
  'portfolio.settlementAudit.sideLong': 'LARGO',
  'portfolio.settlementAudit.sideShort': 'CORTO',
  'portfolio.settlementAudit.floor': 'PISO',
  'portfolio.settlementAudit.basisMatched': 'emparejado',
  'portfolio.settlementAudit.basisAvg': 'base prom.',
  'portfolio.settlementAudit.netOpen': 'neto abierto',
  'portfolio.settlementAudit.fillCountOne': '{count} ejecución',
  'portfolio.settlementAudit.fillCount': '{count} ejecuciones',
  'portfolio.settlementAudit.buyCountOne': '{count} COMPRA',
  'portfolio.settlementAudit.buyCount': '{count} COMPRAS',
  'portfolio.settlementAudit.sellCountOne': '{count} VENTA',
  'portfolio.settlementAudit.sellCount': '{count} VENTAS',
  'portfolio.trade.settle': 'Liquidación',
  'portfolio.order.promote': 'Compartir',
  'portfolio.order.edit': 'Editar',
  'portfolio.order.cancel': 'Cancelar',
  'stack.createPool': 'Crear pool',
  'stack.signIn': 'Iniciar sesión',
  'stack.createAccount': 'Crear cuenta',
  'poolWizard.title.create': 'Crear pool',
  'poolWizard.title.manage': 'Gestionar pool',
  'poolWizard.tabs.details': 'Detalles',
  'poolWizard.tabs.members': 'Miembros',
  'poolWizard.fields.poolName': 'Nombre del pool',
  'poolWizard.fields.description': 'Descripción (opcional)',
  'poolWizard.fields.valuePerPoint': 'Multiplicador de puntos (opcional)',
  'poolWizard.placeholders.poolName': 'Pool familiar de Martin 26',
  'poolWizard.placeholders.description': 'Descripción breve',
  'poolWizard.placeholders.valuePerPoint': 'Valor entre 0.1 y 10',
  'poolWizard.placeholders.inviteEmails': 'Ingresa direcciones de email',
  'poolWizard.sections.poolSettings': 'Configuración del pool',
  'poolWizard.sections.competition': 'Competencia',
  'poolWizard.sections.selectCompetition': 'Selecciona una competencia',
  'poolWizard.sections.poolCode': 'Código del pool',
  'poolWizard.sections.invites': 'Invitar por email',
  'poolWizard.poolCode.hint':
    'Comparte este código o enlace para que otras personas puedan unirse.',
  'poolWizard.approval.title': 'Requerir aprobación del administrador',
  'poolWizard.approval.description':
    'Desactiva esta opción para activar las solicitudes pendientes y permitir que quienes usen el código del pool en el futuro entren de inmediato.',
  'poolWizard.approval.required': 'Requerida',
  'poolWizard.approval.notRequired': 'No requerida',
  'poolWizard.actions.saveChanges': 'Guardar cambios',
  'poolWizard.actions.close': 'Cerrar gestión del pool',
  'poolWizard.actions.copy': 'Copiar',
  'poolWizard.actions.copyLink': 'Copiar enlace',
  'poolWizard.actions.copied': 'Copiado',
  'poolWizard.actions.shareLink': 'Compartir enlace',
  'poolWizard.actions.sendInvites': 'Enviar invitaciones',
  'poolWizard.actions.accept': 'Aceptar',
  'poolWizard.actions.enable': 'Activar',
  'poolWizard.actions.createPool': 'Crear pool',
  'poolWizard.actions.acceptInvite': 'Aceptar invitación',
  'poolWizard.actions.rejectInvite': 'Rechazar invitación',
  'poolWizard.actions.removePlayer': 'Eliminar jugador',
  'poolWizard.actions.resendInvite': 'Reenviar invitación',
  'poolWizard.actions.manageMember': 'Gestionar miembro',
  'poolWizard.errors.loadMembers': 'No se pudieron cargar los miembros.',
  'poolWizard.errors.nameTooShort':
    'El nombre del pool debe tener al menos 3 caracteres.',
  'poolWizard.errors.nameTooLong':
    'El nombre del pool debe tener menos de 120 caracteres.',
  'poolWizard.errors.descriptionTooLong':
    'La descripción debe tener menos de 1024 caracteres.',
  'poolWizard.errors.selectCompetition': 'Selecciona una competencia.',
  'poolWizard.errors.pointValue':
    'El multiplicador de puntos debe estar entre 0.1 y 10 con como máximo 1 decimal.',
  'poolWizard.errors.updateFailed': 'No se pudo actualizar el pool.',
  'poolWizard.errors.createFailed': 'No se pudo crear el pool.',
  'poolWizard.errors.enterEmail': 'Ingresa al menos un email.',
  'poolWizard.errors.inviteFailed': 'No se pudieron enviar las invitaciones.',
  'poolWizard.errors.resendInviteFailed': 'No se pudo reenviar la invitación.',
  'poolWizard.errors.updateMember': 'No se pudo actualizar el miembro.',
  'poolWizard.invites.sent': 'Invitaciones enviadas.',
  'poolWizard.invites.resent': 'Invitación reenviada.',
  'poolWizard.invites.or': 'O',
  'poolWizard.invites.hint': 'Separados por comas o espacios.',
  'poolWizard.members.filter.all': 'Todos',
  'poolWizard.members.filter.pending': 'Pendientes',
  'poolWizard.members.filter.active': 'Activos',
  'poolWizard.members.adminOnly':
    'Solo los administradores del pool pueden gestionar miembros.',
  'poolWizard.members.section.pending': 'Invitaciones pendientes',
  'poolWizard.members.section.active': 'Jugadores activos',
  'poolWizard.members.emptyAll': 'Aún no hay miembros.',
  'poolWizard.members.emptyPending': 'No hay miembros pendientes.',
  'poolWizard.members.emptyActive': 'No hay miembros activos.',
  'poolWizard.member.admin': 'Admin',
  'poolWizard.member.appleRelay.via': 'vía Apple',
  'poolWizard.member.appleRelay.invited': 'Invitado vía Apple',
  'poolWizard.member.appleRelay.joined': 'Se unió vía Apple',
  'poolWizard.memberStatus.pending': 'Pendiente',
  'poolWizard.memberStatus.active': 'Activo',
  'poolWizard.memberStatus.disabled': 'Desactivado',
  'poolWizard.memberStatus.rejected': 'Rechazado',
  'poolWizard.create.description':
    'Nombra tu pool y elige una temporada de liga. Invita amigos después.',
  'auth.appleIncomplete':
    'El inicio de sesión con Apple no se completó. Revisa los registros del dispositivo para ver el estado devuelto por Clerk.',
  'auth.errors.androidBrowserUnavailable':
    'El inicio de sesión en Android necesita una app de navegador. Instala o activa Chrome, Firefox u otro navegador y vuelve a intentarlo.',
  'guest.auth.getStarted': 'Comenzar →',
  'guest.auth.playStoreComingSoon': 'Google Play próximamente',
  'guest.iosAppPrompt.title': '¿Prefieres la app de iOS?',
  'guest.iosAppPrompt.body':
    'Instala SportEx100 desde App Store para aceptar invitaciones con mayor facilidad.',
  'guest.iosAppPrompt.action': 'Abrir App Store',
  'guest.iosAppPrompt.dismiss': 'Continuar en la web',
  'guest.androidAppPrompt.title': '¿Prefieres la app de Android?',
  'guest.androidAppPrompt.body':
    'Instala SportEx100 desde Google Play para aceptar invitaciones con mayor facilidad.',
  'guest.androidAppPrompt.action': 'Abrir Google Play',
  'settings.notifications.title': 'Notificaciones',
  'settings.notifications.subtitle': 'Ejecuciones de trades y mensajes de chat',
  'settings.errors.openLink': 'No se pudo abrir el enlace. Inténtalo de nuevo.',
  'settings.errors.openNotifications':
    'No se pudo abrir la configuración de notificaciones. Inténtalo de nuevo.',
  'settings.errors.verifySession':
    'No se pudo verificar tu sesión. Inicia sesión de nuevo.',
  'settings.errors.deleteData':
    'No se pudieron eliminar tus datos de SportEx100. Inténtalo de nuevo.',
  'home.actions.doneReordering': 'Terminar reordenamiento',
  'home.actions.joinPool': 'Unirse a pool',
  'home.empty.filteredBody': 'Prueba otro estado para ver tus pools.',
  'home.reorder.banner':
    'Modo de reordenamiento: usa las flechas en las tarjetas y luego toca Terminar reordenamiento.',
  'home.pendingInvite.joined': 'Te uniste al pool {code}.',
  'home.pendingInvite.couldNotJoin': 'No se pudo unir al pool {code}.',
  'home.welcome.inviteBody':
    'Inicia sesión para aceptar tu invitación al pool y empezar a seguir el torneo con tu grupo.',
  'home.welcome.signInApple': 'Iniciar sesión con Apple',
  'home.welcome.signInGoogle': 'Iniciar sesión con Google',
  'home.welcome.acceptInvite': 'Aceptar invitación',
  'home.join.error.required': 'Ingresa un código de pool',
  'home.join.error.failed': 'No se pudo unir al pool',
  'home.join.close': 'Cerrar unirse a pool',
  'home.pool.points': 'Pts',
  'home.pool.upside': 'Potencial',
  'home.pool.pickValue': 'Valor de tu pronóstico',
  'home.pool.unitsAt': '{count} unidades a {marketRef}',
  'home.pool.completePicksToUnlock':
    'Completa tus pronósticos para desbloquear sugerencias de trade.',
  'home.pool.leader.you': 'Lideras el pool',
  'home.pool.leader.ahead': 'de ventaja',
  'home.feed.sectionTitle': 'Pulso de la competencia',
  'home.feed.pulseLabel': 'Pulso',
  'home.feed.pulseLabel.recap': 'Resumen',
  'home.feed.pulseLabel.next': 'Próximo',
  'home.feed.retry': 'Reintentar',
  'home.feed.empty': 'Nada que ver aquí. Todavía.',
  'home.feed.emptyMetaType': 'Insight',
  'home.feed.emptyMetaStatus': 'Esperando',
  'home.feed.swipeHint': 'Desliza para ver más',
  'home.feed.noDetail': 'No hay detalle del artículo disponible.',
  'home.feed.floor': 'Piso {value}',
  'home.feed.floorEmpty': 'Piso --',
  'home.feed.buyTeam': 'Comprar {teamCode}',
  'home.feed.sellTeam': 'Vender {teamCode}',
  'home.feed.category.upcoming': 'Próximo',
  'home.feed.category.recap': 'Resumen',
  'home.feed.category.breaking': 'Última hora',
  'home.feed.category.injury': 'Lesión',
  'home.feed.category.live': 'En vivo',
  'home.feed.category.result': 'Resultado',
  'home.feed.category.upset': 'Sorpresa',
  'home.feed.category.eliminated': 'Eliminado',
  'home.feed.category.advancing': 'Avanza',
  'home.feed.category.momentum': 'Impulso',
  'home.feed.category.schedule': 'Calendario',
  'home.feed.category.champion': 'Campeón',
  'home.feed.category.update': 'Actualización',
  'home.tutorial.cta.eyebrow': 'Bienvenido a Sport Exchange 100',
  'home.tutorial.cta.title': 'Opera equipos. Supera a tus amigos.',
  'home.tutorial.cta.subtitle':
    'Adivina quién gana. Opera con inteligencia. Sube en la clasificación. Sin dinero real, solo derecho a presumir.',
  'home.tutorial.cta.start': 'Iniciar tutorial →',
  'home.tutorial.cta.startAccessibility': 'Iniciar tutorial',
  'home.tutorial.cta.duration': 'Toma 2 minutos',
  'home.tutorial.cta.hide': 'Ocultar',
  'home.tutorial.cta.hideAccessibility': 'Ocultar CTA del tutorial',
  'home.tutorial.step5.note': 'Eliminado en 6 partidos · gana 4 puntos',
  'home.tutorial.points.label': 'Hockey pro · Mejor de 7 (4 victorias)',
  'home.tutorial.points.round': 'Ronda',
  'home.tutorial.points.gameWin': 'Victoria',
  'home.tutorial.points.roundWin': 'Gana ronda',
  'home.tutorial.points.maxPts': 'Pts máx.',
  'home.tutorial.championLabel': 'El campeón siempre liquida a',
  'home.tutorial.chart.points': 'PTS',
  'home.tutorial.chart.playoffRound': 'RONDA DE PLAYOFF',
  'home.tutorial.chart.gamesPlayed': 'PARTIDOS JUGADOS',
  'home.tutorial.chart.boughtAt': 'COMPRADO A {value}',
  'home.tutorial.chart.soldAt': 'VENDIDO A {value}',
  'home.tutorial.chart.settlesAt': 'LIQUIDA A {value}',
  'home.tutorial.chart.gain': 'GANANCIA',
  'home.tutorial.chart.loss': 'PÉRDIDA',
  'home.tutorial.dismissAccessibility': 'Cerrar tutorial',
  'home.tutorial.gotIt': 'Entendido',
  'market.header.title': 'Mercado',
  'market.pending.cta': 'Pronósticos →',
  'market.invalidPool.title': 'Configuración de pool no válida',
  'market.invalidPool.message':
    'Al pool seleccionado le faltan datos de temporada. Vuelve a Inicio y selecciona el pool de nuevo.',
  'market.quote.bidSize': 'Tam. compra',
  'market.quote.askSize': 'Tam. venta',
  'market.action.buy': 'Comprar',
  'market.action.sell': 'Vender',
  'market.orderBook.loading': 'Cargando libro de órdenes…',
  'market.orderBook.empty': 'No hay profundidad disponible',
  'market.orderBook.unavailable': 'Libro de órdenes no disponible',
  'market.orderBook.notAuthenticated': 'No autenticado',
  'market.bidAskTip.message':
    'Toca la compra o venta de un equipo para enviar una orden.',
  'market.bidAskTip.action': 'Entendido',
  'market.error.retry': 'Intentar de nuevo',
  'market.worldCup.status.all': 'Todos',
  'market.worldCup.status.live': 'En juego',
  'market.worldCup.status.out': 'Fuera',
  'market.worldCup.status.allAccessibility':
    'Mostrar todos los equipos de fútbol',
  'market.worldCup.status.liveAccessibility':
    'Mostrar equipos de fútbol en juego',
  'market.worldCup.status.outAccessibility':
    'Mostrar equipos de fútbol eliminados',
  'market.guest.context': 'Cada partido mueve el mercado.',
  'market.guest.emphasis': 'Compra bajo. Grita fuerte.',
  'market.guest.body':
    'Mira cómo el piso se ilumina con compras y ventas, y actúa antes de que tus amigos muevan el precio.',
  'market.guest.invite': 'Ingresa código de invitación',
  'guest.landing.nav.create': 'Crear',
  'guest.landing.nav.market': 'Mercado',
  'guest.landing.nav.picks': 'Pronósticos',
  'guest.landing.nav.social': 'Social',
  'guest.landing.social.context': 'EL CHAT ES EL PISO.',
  'guest.landing.social.emphasis': 'Chat en vivo de SportEx100.',
  'guest.landing.social.body':
    'Cada pool tiene chat en vivo: lanza tu predicción, publica tu precio y reta a alguien a tomar el otro lado. Cada opinión fuerte puede convertirse en una operación.',
  'guest.landing.social.imageAlt':
    'Pantalla de chat del pool en la app móvil de SportEx100',
  'guest.landing.nav.standings': 'Clasificación',
  'guest.landing.standings.context': 'Cada trade sacude la carrera.',
  'guest.landing.standings.emphasis': 'Toma la corona.',
  'guest.landing.standings.body':
    'Tu ranking se mueve con cada partido y cada operación. Sin regalos ni piedad: supera a quien tengas delante y avisa a toda la tabla.',
  'guest.landing.standings.imageAlt':
    'Pantalla de clasificación de SportEx100 en la app móvil',
  'guest.landing.nav.track': 'Seguir',
  'guest.landing.contract.imageAlt':
    'Pantalla de contrato de equipo en la app móvil de SportEx100',
  'guest.landing.market.imageAlt':
    'Pantalla del mercado de fútbol en la app móvil de SportEx100',
  'guest.landing.picks.imageAlt':
    'Pantalla de pronósticos en la app móvil de SportEx100',
  'guest.landing.track.context': 'Cada movimiento. Una pantalla.',
  'guest.landing.track.emphasis': 'Conoce tu valor.',
  'guest.landing.track.body':
    'Órdenes, trades, puntos y valores de equipo, todo en un lugar. Mira qué tienes, qué se calienta y dónde se esconde tu próxima gran jugada.',
  'guest.landing.track.imageAlt':
    'Pantalla de portfolio en la app móvil de SportEx100',
  'order.title.place': 'Enviar orden',
  'order.title.update': 'Actualizar orden',
  'order.form.side': 'Lado',
  'order.form.qty': 'Cant.',
  'order.form.price': 'Precio',
  'order.action.buy': 'Comprar',
  'order.action.sell': 'Vender',
  'order.preview.market': 'MKT',
  'order.preview.position': 'Posición',
  'order.preview.myPickValue': 'Valor de mi pronóstico: {value}',
  'order.preview.myPickValueLoading': 'Valor de mi pronóstico: ...',
  'order.preview.setupSimulation':
    'Configura el valor teórico del equipo en Pronósticos.',
  'order.validation.pricePositive': 'El precio debe ser positivo',
  'order.validation.minPrice': 'Precio mín.: {price}',
  'order.validation.maxPrice': 'Precio máx.: {price}',
  'order.success.filled':
    '¡Orden ejecutada! Se ejecutaron {fills} {tradeLabel}.',
  'order.success.tradeOne': 'trade',
  'order.success.tradeMany': 'trades',
  'order.success.updated': '¡Orden actualizada correctamente!',
  'order.success.placed': '¡Orden enviada correctamente!',
  'order.error.title': 'Orden fallida',
  'order.error.updateFailed': 'No se pudo actualizar la orden',
  'order.error.submitFailed': 'No se pudo enviar la orden',
  'order.action.cancel': 'Cancelar',
  'order.action.submit': 'Enviar orden',
  'order.action.submitting': 'Enviando',
  'order.action.update': 'Actualizar orden',
  'order.action.updating': 'Actualizando',
  'order.alert.ok': 'OK',
  'picks.header.title': 'Coach del pool',
  'picks.noPool.message': 'Selecciona un pool para abrir Coach.',
  'picks.hint.seriesMessage':
    'Toca una fila de equipo para elegir el ganador y el marcador de la serie. W-L es tu récord previsto de la serie. PTS es el valor proyectado del equipo.',
  'picks.hint.singleGameMessage':
    'Toca una fila de equipo para elegir el ganador. PTS es el valor proyectado del equipo.',
  'picks.hint.action': 'Entendido',
  'picks.scenario.subtitle': 'Toca equipos para predecir',
  'picks.empty.bracketPending': 'Bracket pendiente',
  'picks.empty.noBracket': 'No hay datos de bracket',
  'picks.empty.worldCupPending':
    'Los pronósticos de fútbol se desbloquearán cuando el script de clasificación de grupos publique los cruces de dieciseisavos.',
  'picks.empty.noBracketBody':
    'El bracket aparecerá cuando comiencen los playoffs.',
  'picks.status.groupStage': 'FASE DE GRUPOS',
  'picks.status.pickNow': 'ELIGE AHORA',
  'picks.status.active': 'ACTIVO',
  'picks.status.complete': 'COMPLETO',
  'picks.status.pending': 'PENDIENTE',
  'picks.column.series': 'W-L',
  'picks.column.points': 'PTS',
  'picks.pending.opponent':
    'Elige el ganador de la ronda anterior para definir rival.',
  'picks.pending.matchup': 'Elige ganadores de la ronda anterior.',
  'picks.pending.scoreHint':
    'Toca el ganador otra vez para ajustar el marcador de la serie',
  'picks.trade.title': 'Operar {teamCode}',
  'picks.trade.value': 'Valor del pronóstico {value}',
  'picks.trade.cancel': 'Cancelar',
  'picks.trade.buy': 'Comprar al valor del pronóstico',
  'picks.trade.sell': 'Vender al valor del pronóstico',
  'picks.alert.pendingTitle': 'Solo lectura para miembros pendientes',
  'picks.alert.pendingMessage':
    'El trading se desbloquea tras la aprobación del administrador.',
  'picks.alert.resetTitle': 'Restablecer pronósticos',
  'picks.alert.resetMessage':
    '¿Seguro que quieres restablecer todos tus pronósticos para este bracket?',
  'picks.alert.resetConfirm': 'Restablecer',
  'picks.alert.ok': 'OK',
  'picks.alert.groupLimitTitle': 'Solo tres pueden avanzar',
  'picks.alert.groupLimitMessage':
    'Elige los dos primeros y, como máximo, un equipo en tercer lugar de este grupo.',
  'picks.alert.qualifierLimitTitle': 'Límite de clasificados',
  'picks.alert.qualifierLimitMessage':
    'Selecciona {count} clasificados de fútbol en total.',
  'picks.alert.bestThirdLimitTitle': 'Límite de mejores terceros',
  'picks.alert.bestThirdLimitMessage':
    'Selecciona {count} mejores terceros clasificados en total.',
  'picks.worldCup.seedGroupStage': 'Semilla {seed} · Fase de grupos',
  'picks.worldCup.groupRankFirst': 'Top 1',
  'picks.worldCup.groupRankSecond': 'Top 2',
  'picks.worldCup.groupRankThird': 'Mejor 3.º',
  'picks.worldCup.topTwoProgress': 'Top 2 {count}/{total}',
  'picks.worldCup.bestThirdProgress': 'Mejores 3.º {count}/{total}',
  'picks.round.first': 'Primera ronda',
  'picks.round.second': 'Segunda ronda',
  'picks.round.roundOf16': 'Octavos de final',
  'picks.round.quarterFinals': 'Cuartos de final',
  'picks.round.semiFinals': 'Semifinales',
  'picks.round.final': 'Final',
  'picks.round.finals': 'Finales',
  'picks.round.round': 'Ronda {sequence}',
  'picks.round.lockedAccessibility': '{round} bloqueada',
  'picks.competition.generic': 'Competencia',
  'picks.competition.hockey': 'Hockey pro',
  'picks.competition.football': 'Fútbol americano SportEx100',
  'picks.competition.soccer': 'Fútbol mundial',
  'picks.pointSystem.close': 'Cerrar sistema de puntos',
  'picks.pointSystem.champion': 'El campeón siempre liquida a',
  'picks.pointSystem.settlement': 'Liquidación',
  'picks.pointSystem.settlementValue': 'Valor de liquidación',
  'picks.pointSystem.winPoints': 'Puntos por victoria',
  'picks.pointSystem.perGameWin': 'Por victoria de partido',
  'picks.pointSystem.teamPoints': 'Puntos de equipo',
  'picks.pointSystem.stage': 'Ronda',
  'picks.pointSystem.result': 'Resultado',
  'picks.pointSystem.earnAdds': 'Gana +',
  'picks.pointSystem.settlesAt': 'Liquida a',
  'picks.pointSystem.noValue': '—',
  'picks.pointSystem.value': 'Valor',
  'picks.pointSystem.points': 'Puntos',
  'picks.pointSystem.qualification': 'Clasificación a playoffs',
  'picks.pointSystem.soccer.winner': 'Campeón',
  'picks.pointSystem.soccer.runnerUp': 'Subcampeón',
  'picks.pointSystem.soccer.thirdPlace': 'Tercer lugar',
  'picks.pointSystem.soccer.worldCupChampion': 'Campeón',
  'picks.pointSystem.soccer.perfectPath': 'Ruta perfecta al título',
  'picks.pointSystem.soccer.runningTotal': 'TOTAL {points}',
  'picks.pointSystem.winRound': 'GANA {round}',
  'picks.pointSystem.soccer.winRound': 'GANA {round}',
  'picks.pointSystem.soccer.exitValue': 'Valor al salir',
  'picks.pointSystem.soccer.exitValueBody':
    'El equipo liquida los puntos que ya obtuvo.',
  'picks.pointSystem.soccer.eliminatedGroupStage':
    'Eliminado en fase de grupos',
  'picks.pointSystem.soccer.advanceKnockout': 'Avanza a eliminación directa',
  'picks.pointSystem.soccer.winRound32': 'Gana en ronda de 32',
  'picks.pointSystem.soccer.winRound16': 'Gana en octavos de final',
  'picks.pointSystem.soccer.winQuarterFinal': 'Gana en cuartos de final',
  'picks.pointSystem.soccer.winSemiFinal': 'Gana en semifinales',
  'picks.pointSystem.soccer.winThirdPlace': 'Gana partido por 3er lugar',
  'picks.pointSystem.soccer.eliminatedSemiFinal': 'Eliminado en semifinales',
  'picks.pointSystem.soccer.eliminatedQuarterFinal':
    'Eliminado en cuartos de final',
  'picks.pointSystem.soccer.eliminatedRound16': 'Eliminado en octavos de final',
  'picks.pointSystem.soccer.eliminatedRound32': 'Eliminado en ronda de 32',
  'picks.pointSystem.soccer.missedKnockout': 'No clasificó',
  'picks.guest.context': 'Construye tu proyección.',
  'picks.guest.emphasis': 'Confía en tu intuición.',
  'picks.guest.body':
    'Predice el bracket, respalda tu corazonada y ataca cuando el precio esté mal. Los audaces cobran; los tímidos se quedan atrás.',
  'picks.guest.invite': 'Ingresa código de invitación',
  'standings.header.title': 'Clasificación',
  'standings.freshness.updated': 'Actualizado {time}',
  'standings.empty.body':
    'La clasificación aparecerá cuando empiece el trading.',
  'standings.recentActivity.title': 'Actividad reciente',
  'standings.recentActivity.loading': 'Cargando actividad...',
  'standings.recentActivity.error': 'No se pudo cargar la actividad.',
  'standings.guest.context': 'Cada trade cambia la persecución.',
  'standings.guest.emphasis': 'Caza el primer lugar.',
  'standings.guest.body':
    'Tu puntaje y ranking se actualizan tras cada partido y cada movimiento. Mira quién va delante, cierra la brecha y haz que el próximo trade presione la tabla.',
  'standings.guest.invite': 'Ingresa código de invitación',
  'portfolio.guest.context': 'Mira cómo vas.',
  'portfolio.guest.emphasis': 'Sigue tu puntaje.',
  'portfolio.guest.body':
    'Tus pronósticos, movimientos, puntos y progreso aparecen aquí.',
  'portfolio.guest.invite': 'Ingresa código de invitación',
  'portfolio.header.title': 'Sportfolio',
  'portfolio.filters.all': 'Todos',
  'portfolio.filters.positions': 'Posiciones',
  'portfolio.filters.orders': 'Órdenes',
  'portfolio.filters.history': 'Historial',
  'portfolio.filters.buy': 'Compra',
  'portfolio.filters.sell': 'Venta',
  'portfolio.filters.settle': 'Liquidación',
  'portfolio.filters.long': 'Largo',
  'portfolio.filters.short': 'Corto',
  'portfolio.sections.positions': 'Posiciones',
  'portfolio.sections.openOrders': 'Órdenes abiertas',
  'portfolio.sections.history': 'Historial',
  'portfolio.empty.positions': 'Sin posiciones',
  'portfolio.empty.openOrders': 'Sin órdenes abiertas',
  'portfolio.noPool': 'Selecciona un pool para ver tu sportfolio.',
  'portfolio.summary.totalPnl': 'P&L total',
  'portfolio.summary.rank': 'Rango',
  'portfolio.summary.settled': 'Liquidado',
  'portfolio.trade.deletedTrader': 'ELIMINADO',
  'portfolio.order.actionsAccessibility': 'Acciones de orden de {team}',
  'portfolio.order.market': 'MKT',
  'portfolio.order.hint': 'Toca una orden para compartir, editar o cancelar.',
  'portfolio.order.hintAction': 'Entendido',
  'portfolio.order.cancelTitle': 'Cancelar orden',
  'portfolio.order.cancelPrompt': '¿Seguro que quieres cancelar esta orden?',
  'portfolio.order.cancelNo': 'No',
  'portfolio.order.cancelYes': 'Sí',
  'portfolio.order.cancelErrorTitle': 'Error',
  'portfolio.order.cancelErrorMessage': 'No se pudo cancelar la orden',
  'portfolio.order.poolNotReadyTitle': 'Pool no listo',
  'portfolio.order.poolNotReadyMessage':
    'Aún no se pudieron cargar los datos de mercado para esta orden.',
  'portfolio.order.teamNotFoundTitle': 'Equipo no encontrado',
  'portfolio.order.teamNotFoundMessage':
    'No se pudieron cargar los datos del equipo para esta orden.',
  'pulse.time.justNow': 'AHORA',
  'pulse.time.minutesAgo': 'HACE {count}M',
  'pulse.time.hoursAgo': 'HACE {count}H',
  'pulse.time.daysAgo': 'HACE {count}D',
  'pulse.time.lastWindow': 'ÚLTIMOS {duration}',
  'pulse.action.viewAll': 'Ver todo',
  'pulse.action.showLess': 'Ver menos',
  'pulse.story.wall': 'Muro',
  'pulse.story.airball': 'Airball',
  'pulse.tradeBehavior.pill.stacking': 'ACUMULA',
  'pulse.tradeBehavior.pill.dumping': 'SUELTA',
  'pulse.tradeBehavior.pill.heatingUp': 'CALIENTE',
  'pulse.tradeBehavior.pill.whale': 'BALLENA',
  'pulse.tradeBehavior.side.buy': 'COMPRA',
  'pulse.tradeBehavior.side.sell': 'VENTA',
  'pulse.tradeBehavior.context.pattern':
    '{side} {quantity} · {fills} EJEC. · PROM {avg}',
  'pulse.tradeBehavior.headline.stacking': '{trader} acumula {team}',
  'pulse.tradeBehavior.headline.dumping': '{trader} suelta {team}',
  'pulse.tradeBehavior.headline.whale': '{buyer} compró {team} a {seller}',
  'pulse.orderPressure.pill.wall': 'MURO',
  'pulse.orderPressure.pill.airball': 'AIRBALL',
  'pulse.orderPressure.side.buyHeadline': 'compra',
  'pulse.orderPressure.side.sellHeadline': 'venta',
  'pulse.orderPressure.headline.wall': 'Muro de {side} en {team}',
  'pulse.orderPressure.headline.wallSell': 'Muro de {side} en {team}',
  'pulse.orderPressure.headline.airball': 'Airball de {side} en {team}',
  'pulse.orderPressure.context.full':
    '{side} {price} × {quantity} · VALOR {value} · MARK {mark}',
  'pulse.teamHeat.headline': '{team} está encendido.',
  'pulse.teamHeat.subline': '{traders} traders · {qty} contratos · prom {avg}',
  'pulse.teamHeat.metric.traders': '{count} traders',
  'pulse.teamHeat.metric.qtyMoved': '{qty} contratos',
  'pulse.teamHeat.metric.avg': 'prom {avg}',
  'pulse.position.asOf': 'AL {time}',
  'pulse.position.pill.crowded': 'CARGADO',
  'pulse.position.pill.contrarian': 'CONTRARIO',
  'pulse.position.side.long': 'LARGO',
  'pulse.position.side.short': 'CORTO',
  'pulse.position.intensity.allIn': 'TODO',
  'pulse.position.intensity.lopsided': 'DESBALANCEADO',
  'pulse.position.intensity.heavily': 'FUERTE',
  'pulse.position.intensity.leaning': 'INCLINADO',
  'pulse.position.context.value': 'VALOR {value}{qty}',
  'pulse.position.context.qtySuffix': ' · CANT {qty}',
  'pulse.position.context.liveQty': 'ACTIVO · CANT {qty}',
  'pulse.position.context.live': 'ACTIVO',
  'pulse.settlement.pill': 'LIQUIDA',
  'pulse.settlement.best': 'MEJOR',
  'pulse.settlement.worst': 'PEOR',
  'pulse.settlement.vs': 'VS',
  'pulse.settlement.traders': '{count} TRADERS',
  'pulse.settlement.points': '{points} PTS',
  'portfolio.history.loadMore': 'Cargar más ({remaining} restantes)',
};

export const translations: Record<
  SupportedLocale,
  Partial<Record<TranslationKey, string>>
> = {
  en,
  fr,
  ja,
  es,
};
